import panzoom from 'panzoom'
import { getSDK } from '../../shared/functions/sdk.js'
import {
  ownerSlug,
  ownerMatches,
  projectKey,
  projectScreenshotUrl,
  SCREENSHOT_CDN_READY
} from '../../shared/functions/projects.js'
import { buildProjectUrls } from '../functions/buildProjectUrls.js'

const NODE_W = 320
const NODE_H = 256
const THUMBNAIL_H = 180

// Per-projectType tile dimensions. App/desktop/tv/deck render landscape;
// mobile renders portrait; library uses a slimmer landscape distinct shape.
// COL_STRIDE/ROW_STRIDE use the LARGEST footprint so the auto-grid never
// overlaps regardless of which type lives in a slot.
const TYPE_DIMS = {
  app:     { w: 320, h: 224 },
  website: { w: 320, h: 224 },
  desktop: { w: 320, h: 224 },
  tv:      { w: 360, h: 220 },
  deck:    { w: 320, h: 200 },
  mobile:  { w: 200, h: 320 },
  library: { w: 280, h: 168 },
  platform:{ w: 320, h: 224 }
}
const dimsFor = (projectType) => TYPE_DIMS[projectType] || { w: NODE_W, h: NODE_H }
const COL_GAP = 64
const PER_ROW = 4
const BOARD_W = 2400
const BOARD_H = 2000
// matches the Dots background size so snap = dot pitch
const GRID_STEP = 24
const DRAG_THRESHOLD = 4

const snapUp = (n) => Math.ceil(n / GRID_STEP) * GRID_STEP
const COL_STRIDE = snapUp(NODE_W + COL_GAP)
const ROW_STRIDE = snapUp(NODE_H + COL_GAP)

// Extract the workspace slug ONLY when the current URL is actually on
// /workspaces/{slug}. Otherwise returns ''. Callers combine with
// `root.activeWorkspace` — on /workspaces/{slug} the URL wins so a
// direct navigation doesn't silently render a stale activeWorkspace.
const ownerSlugFromUrl = () => {
  const segs = window.location.pathname.split('/').filter(Boolean)
  return (segs[0] === 'workspaces' || segs[0] === 'studio') ? (segs[1] || '') : ''
}

// Simpleicons slugs that 404 on cdn.simpleicons.org (trademark removals or
// slugs that never existed). Any DB `icon` value in this set is treated as
// absent so the letter-initials glyph renders instead of a broken <img>.
// Kept in sync with workspace/packages/marketplace/functions/index.js#SIMPLEICONS_404.
const SIMPLEICONS_404 = new Set([
  'amazonwebservices', // aws — removed from simpleicons
  'amazons3',          // aws_s3 — removed
  'openai',            // removed
  'slack',             // removed
  'twilio',            // removed
  'sendgrid',          // removed
  'segment',           // removed
  'neon',              // never in simpleicons
  'workos',            // never in simpleicons
  'pinecone',          // never in simpleicons
  'logrocket',         // never in simpleicons
  'uploadthing',       // never in simpleicons
  'server',            // generic, not in simpleicons (custom_http placeholder)
  'mic'                // generic, not in simpleicons (livekit placeholder)
])

// Sanitise an icon slug from the DB before using as a simpleicons src.
// Returns null when the slug is blank, the string "undefined", or known-404.
function sanitiseIcon(raw) {
  if (!raw || raw === 'undefined' || raw === 'null') return null
  return SIMPLEICONS_404.has(raw) ? null : raw
}

// Category → accent color map. Canvas derives arrow + chip color from the
// kind's `category` (from `integration_kinds`) instead of hardcoding per
// kind. New kinds inherit their category's look automatically.
const INTEGRATION_CATEGORY_COLOR = {
  data: '#3ecf8e',
  dev: '#d0d7de',
  workspace: '#f0b85a',
  ai: '#b88cff',
  messaging: '#54aeff',
  analytics: '#ff9f5a',
  automation: '#ff7a9c',
  custom: '#8ab4ff'
}

// Glyph fallbacks by kind prefix — used when `icon` is absent or when the
// icon name isn't a known token. One character, drawn inside a rounded
// color tile. Later we can swap these for real icon refs.
const INTEGRATION_KIND_GLYPH = {
  supabase_project: 'S',
  github: 'G',
  google_workspace: 'W',
  google_analytics: 'A',
  npm: 'N',
  n8n: '⚙',
  custom_http: '◆'
}

const INTEGRATION_NODE_W = 350
const INTEGRATION_NODE_H = 72

// Installable canvas tiles — dimensions for non-integration domains.
// Libraries get a slightly taller card to hint at their "stack" visual.
// MCP connectors and plugins reuse the same compact height as integrations.
const INSTALLABLE_W = 200
const INSTALLABLE_H = 80

function integrationColor(category) {
  return (
    INTEGRATION_CATEGORY_COLOR[category] || INTEGRATION_CATEGORY_COLOR.custom
  )
}

function integrationGlyph(kind) {
  if (INTEGRATION_KIND_GLYPH[kind]) return INTEGRATION_KIND_GLYPH[kind]
  return (kind || '?').charAt(0).toUpperCase()
}

// Map `kind` → slug used in the governance admin URL. Mirrors the helper
// in governance/.../admin-integrations.js so a canvas integration chip
// always lands on the correct per-kind configuration page.
function integrationKindSlug(kind) {
  if (kind === 'google_workspace') return 'google'
  if (kind === 'supabase_project') return 'supabase'
  return kind
}

function integrationAdminUrl(kind) {
  const host = window.location.hostname.includes('localhost')
    ? 'http://workspace.localhost:1355'
    : 'https://us-at.symbols.app'
  const slug = integrationKindSlug(kind)
  return `${host}/admin/integrations${slug ? '/' + slug : ''}`
}

function marketplaceUrl() {
  return window.location.hostname.includes('localhost')
    ? 'http://marketplace.localhost:1355'
    : 'https://marketplace.symbols.app'
}

// Factory for SettingsModal tabs — left-rail button that picks a section.
function settingsTab(tabKey, label, hint) {
  return {
    tag: 'button',
    type: 'button',
    flow: 'y',
    gap: '2px',
    textAlign: 'left',
    padding: 'V A',
    round: 'Z',
    border: '0',
    cursor: 'pointer',
    background: (el, s) =>
      (s.root?.settingsTab || 'workspace') === tabKey
        ? 'rgba(255,255,255,0.1)'
        : 'transparent',
    color: 'white',
    ':hover': { background: 'rgba(255,255,255,0.06)' },
    Name: { tag: 'span', text: label, fontSize: 'Z2', fontWeight: '600' },
    Hint: {
      tag: 'span',
      text: hint,
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.5)'
    },
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.root?.update({ settingsTab: tabKey })
    }
  }
}

// Factory for canvas-scoped settings rows — only renders when a canvas
// context exists. Wraps `settingsBlockRow` with a checkbox that reads
// the canvas v2 state and dispatches `patchCanvas` / `patchSnap` so
// consumers don't need to duplicate the lifecycle plumbing.
function settingsCanvasRow(label, hint, opts = {}) {
  const readChecked = (v2) => {
    if (typeof opts.checked === 'function') return opts.checked(v2)
    if (typeof opts.checked === 'string') return !!v2?.[opts.checked]
    return false
  }
  return {
    if: (el) => !!el.call?.('getCanvasV2'),
    flow: 'x',
    align: 'center space-between',
    padding: 'V 0',
    Label: {
      flow: 'y',
      gap: 'V',
      flexGrow: '1',
      Name: { tag: 'span', text: label, fontWeight: '600' },
      Hint: {
        tag: 'span',
        text: hint,
        fontSize: 'Z',
        color: 'rgba(255,255,255,0.55)'
      }
    },
    Control: {
      tag: 'input',
      type: 'checkbox',
      checked: (el) => readChecked(el.call?.('getCanvasV2')),
      onChange: (ev, el) => {
        const v = !!ev.target.checked
        if (opts.patchSnap) el.call?.('patchSnap', opts.patchSnap(v))
        else if (opts.patch) el.call?.('patchCanvas', opts.patch(v))
      }
    }
  }
}

// Factory for SettingsModal content rows — label + hint on the left, a
// control (toggle, button, input) on the right. Takes the control def
// directly so callers own its state/handlers.
function settingsBlockRow(label, hint, control) {
  return {
    flow: 'x',
    align: 'center space-between',
    padding: 'V 0',
    Label: {
      flow: 'y',
      gap: 'V',
      flexGrow: '1',
      Name: { tag: 'span', text: label, fontWeight: '600' },
      Hint: {
        tag: 'span',
        text: hint,
        fontSize: 'Z',
        color: 'rgba(255,255,255,0.55)'
      }
    },
    Control: control
  }
}

// Cross-origin governance session is picked up via crossAppAuth.js —
// `hydrateAuthFromCookie()` mirrors every `sb-<ref>-auth-token` from the
// parent-domain cookie into this tab's localStorage, so the governance
// Supabase client reads the same session the user has on the intranet.
// Works in prod (shared `.symbols.app` cookie). In localhost dev, Chrome's
// public-suffix rule blocks `.localhost` cookies and iframe storage is
// partitioned — see workspace README for the dev-side workaround.
// Canvas renders integrations at org, workspace, AND project scope in the
// same board, so we query across every scope in one shot. `loadOrgIntegrations`
// pins `p_scope_type='org'` and hides project-scoped rows (e.g. Supabase
// projects with `default_scope='project'`) — not what we want here.
async function loadIntegrationUnitsSafe(orgId) {
  if (!orgId) return []
  try {
    const mod = await import('../../shared/governanceBridge.js')
    try {
      mod.hydrateSupabaseSessionsFromCookie?.()
    } catch {}
    if (typeof mod.loadIntegrationUnits !== 'function') return []
    const units = await mod.loadIntegrationUnits({ orgId, type: null })
    return Array.isArray(units) ? units : []
  } catch (err) {
    console.warn(
      '[workspace] loadIntegrationUnits failed:',
      err?.message || err
    )
    return []
  }
}

// Resolve an org's UUID from its slug via the SDK. Cached at module level
// AND in sessionStorage to survive reloads — `listOrganizations` is
// heavily rate-limited (HTTP 429) if hammered.
const ORG_ID_BY_SLUG = new Map()
const ORG_CACHE_KEY = '__canvas_orgs_v1'
const ORG_CACHE_TTL_MS = 5 * 60_000
let ORG_LIST_PROMISE = null

function readOrgCache() {
  try {
    const raw = sessionStorage.getItem(ORG_CACHE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry?.at || !Array.isArray(entry.data)) return null
    return entry
  } catch {
    return null
  }
}

function writeOrgCache(list) {
  try {
    sessionStorage.setItem(
      ORG_CACHE_KEY,
      JSON.stringify({ at: Date.now(), data: list })
    )
  } catch {}
}

async function orgIdForSlug(slug) {
  if (!slug) return null
  if (ORG_ID_BY_SLUG.has(slug)) return ORG_ID_BY_SLUG.get(slug)
  const cached = readOrgCache()
  if (cached && Date.now() - cached.at < ORG_CACHE_TTL_MS) {
    for (const o of cached.data) if (o?.slug) ORG_ID_BY_SLUG.set(o.slug, o.id)
    if (ORG_ID_BY_SLUG.has(slug)) return ORG_ID_BY_SLUG.get(slug)
  }
  // Fallback: derive org id from cached project list's owner refs. Each
  // project carries `{ owner: { slug, id, type } }` — enough to recover
  // org UUIDs when `listOrganizations` is 429-throttled.
  try {
    const rawProjects = sessionStorage.getItem('__canvas_projects_v1')
    if (rawProjects) {
      const entry = JSON.parse(rawProjects)
      for (const p of entry?.data || []) {
        const o = p?.owner
        if (o?.slug && o?.id && o?.type === 'organization')
          ORG_ID_BY_SLUG.set(o.slug, o.id)
      }
      if (ORG_ID_BY_SLUG.has(slug)) return ORG_ID_BY_SLUG.get(slug)
    }
  } catch {}
  if (!ORG_LIST_PROMISE) {
    ORG_LIST_PROMISE = (async () => {
      try {
        const sdk = getSDK()
        if (!sdk.isReady?.()) await sdk.initialize({})
        const orgs = await sdk.listOrganizations()
        if (Array.isArray(orgs)) writeOrgCache(orgs)
        return orgs || []
      } catch (err) {
        console.warn('[workspace] orgIdForSlug failed:', err?.message || err)
        return cached?.data || []
      }
    })()
  }
  const orgs = await ORG_LIST_PROMISE
  for (const o of orgs || []) if (o?.slug) ORG_ID_BY_SLUG.set(o.slug, o.id)
  return ORG_ID_BY_SLUG.get(slug) || null
}

// Fold the unit-shaped API response into flat per-instance records the
// canvas renderer consumes. Each record = one integration chip/card.
// scope_type='project' resolves scope_id against project UUID first, then
// falls back to project key — the admin picker stored `p.id || p.key`, so
// legacy rows carry the key rather than the UUID.
function flattenIntegrationUnits(units, nodes, orgId) {
  const nodeByUuid = {}
  const nodeByKey = {}
  for (const n of nodes) {
    if (n._id) nodeByUuid[n._id] = n
    if (n.key) nodeByKey[n.key] = n
  }
  const out = []
  for (const unit of units || []) {
    for (const inst of unit.instances || []) {
      if (inst.enabled === false) continue
      const scope = inst.scope_type || unit.default_scope || 'org'
      const config =
        inst.config && typeof inst.config === 'object' ? inst.config : {}
      const record = {
        id: `int:${scope}:${unit.kind}:${inst.slug}`,
        kind: unit.kind,
        category: unit.category,
        icon: sanitiseIcon(unit.icon),
        label: inst.display_name || unit.label || unit.kind,
        kindLabel: unit.label || unit.kind,
        scope,
        scopeId: inst.scope_id || null,
        slug: inst.slug,
        orgId,
        config,
        capabilities: unit.capabilities || [],
        authModel: unit.auth_model,
        hasSecret: inst.has_secret,
        enabled: inst.enabled !== false
      }
      if (scope === 'project') {
        const sid = inst.scope_id
        const node = sid ? nodeByUuid[sid] || nodeByKey[sid] : null
        if (!node) continue
        record.projectId = node.id
      }
      out.push(record)
    }
  }
  return out
}

// Cascades dev → prod screenshot URLs. Both null (CDN offline) → no-op,
// letter placeholder shows through. Ordered fallback with per-URL
// timeout because screenshots.symbo.ls sometimes hangs instead of 404.
function mountThumbnailCascade (el, s) {
  const urls = [s.screenshotDev, s.screenshot].filter(Boolean)
  if (!urls.length) return
  const node = el.node
  if (!node || node.__stackMounted) return
  node.__stackMounted = true
  const doc = el.context.document

  const img = doc.createElement('img')
  img.alt = ''
  img.loading = 'lazy'
  img.decoding = 'async'
  img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block'

  let idx = 0
  let timeoutId = null
  const IMG_TIMEOUT_MS = 3500
  const advance = () => {
    clearTimeout(timeoutId)
    idx += 1
    if (idx < urls.length) {
      img.src = urls[idx]
      timeoutId = setTimeout(advance, IMG_TIMEOUT_MS)
      return
    }
    try { img.remove() } catch {}
  }
  img.addEventListener('error', advance)
  img.addEventListener('load', () => { clearTimeout(timeoutId) })
  img.src = urls[0]
  timeoutId = setTimeout(advance, IMG_TIMEOUT_MS)
  node.appendChild(img)
}

function normalizeProject(p, localPositions, { page = 'root' } = {}) {
  const owner = ownerSlug(p)
  const key = projectKey(p)
  const explicitImage =
    (p?.icon && typeof p.icon === 'object' && p.icon.src) ||
    p?.thumbnail ||
    null
  const screenshotDev = SCREENSHOT_CDN_READY ? projectScreenshotUrl(p, { page, env: 'development' }) : null
  const screenshot = SCREENSHOT_CDN_READY ? projectScreenshotUrl(p, { page }) : null
  // Real dependency graph lives on `useLibraries` (array of project _id refs)
  // returned by the list endpoint. Fall back to `sharedLibraries` for older
  // project shapes so seeded arrows still appear.
  const useLibraries = Array.isArray(p?.useLibraries) ? p.useLibraries : []
  const legacySharedLibs =
    p?.sharedLibraries && typeof p.sharedLibraries === 'object'
      ? Object.keys(p.sharedLibraries)
      : Array.isArray(p?.sharedLibraries)
        ? p.sharedLibraries
        : []
  // Position priority:
  //   1. canvasPosition from the list response (won't appear today because
  //      the server's getProjects projection drops it — but if the server
  //      ever starts including it, we pick it up transparently).
  //   2. locally-cached canvasPosition keyed by project _id (instant
  //      rehydrate on reload within the same origin).
  // The background refetch (`hydrateCanvasPositionsFromServer`) then fans
  // out `getProjectData` per owned project to sync from the source of
  // truth for multi-user correctness.
  const dbId = p?._id || p?.id || null
  const fromPayload =
    p?.canvasPosition && typeof p.canvasPosition === 'object'
      ? { x: +p.canvasPosition.x, y: +p.canvasPosition.y }
      : null
  const fromLocal =
    dbId && localPositions && localPositions[dbId]
      ? { x: +localPositions[dbId].x, y: +localPositions[dbId].y }
      : null
  const canvasPosition =
    fromPayload &&
    Number.isFinite(fromPayload.x) &&
    Number.isFinite(fromPayload.y)
      ? fromPayload
      : fromLocal &&
          Number.isFinite(fromLocal.x) &&
          Number.isFinite(fromLocal.y)
        ? fromLocal
        : null
  return {
    id: `${owner}/${key}`,
    _id: dbId,
    owner,
    key,
    name: p?.name || key || 'Untitled',
    icon: explicitImage,
    screenshot,
    screenshotDev,
    useLibraries,
    legacySharedLibs,
    canvasPosition,
    type: p?.type || null,
    projectType: p?.projectType || null
  }
}

// Seed arrows from each project's declared library deps so reloading a
// workspace renders the same dependency graph stored in Mongo. Match by
// project _id (primary path via `useLibraries`), then fall back to bare
// project keys (legacy `sharedLibraries`). Also seeds integration→project
// arrows for project-scope integrations so e.g. a Supabase tied to a
// project renders as a link on load (no manual drag required).
function initialConnections(nodes, integrations) {
  const byId = {}
  const byKey = {}
  nodes.forEach((n) => {
    if (n._id) byId[n._id] = n.id
    byKey[n.key] = byKey[n.key] || n.id
  })
  const conns = []
  nodes.forEach((n) => {
    const seen = new Set()
    for (const libId of n.useLibraries || []) {
      const from = byId[libId]
      if (!from || from === n.id || seen.has(from)) continue
      seen.add(from)
      conns.push({ id: from + '→' + n.id, from, to: n.id })
    }
    for (const libKey of n.legacySharedLibs || []) {
      const from = byKey[libKey]
      if (!from || from === n.id || seen.has(from)) continue
      seen.add(from)
      conns.push({ id: from + '→' + n.id, from, to: n.id })
    }
  })
  // Project-scope integrations → their owning project.
  for (const it of integrations || []) {
    if (it.scope !== 'project' || !it.projectId) continue
    const to = it.projectId
    if (!byId[it.scopeId] && !nodes.some((n) => n.id === to)) continue
    conns.push({ id: it.id + '→' + to, from: it.id, to, kind: 'integration' })
  }
  return conns
}

// Compute the `useLibraries` array for one project node from the current
// connection graph — libraries are the `from` side (library → project edge).
function computeSharedLibs(projectNodeId, connections, nodes) {
  const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]))
  const libs = []
  for (const c of connections) {
    if (c.to !== projectNodeId) continue
    const libNode = nodeById[c.from]
    if (libNode?._id) libs.push(libNode._id)
  }
  return libs
}

const POSITION_DEBOUNCE_MS = 250
const POSITION_TIMERS = new Map()
const POSITION_LAST = new Map()
// Pending writes indexed by node id so beforeunload / navigation can
// flush them synchronously. Each entry holds the exact payload the
// debounced timer would otherwise send.
const POSITION_PENDING = new Map()
const INT_POSITION_TIMERS = new Map()
const INT_POSITION_LAST = new Map()
const INT_SCOPE_PENDING = new Map()

async function sdkBridge() {
  try {
    return await import('../../shared/governanceBridge.js')
  } catch {
    return null
  }
}

function persistIntegrationPositions(root, ids) {
  const integrations = root?.integrations || []
  const positions = root?.positions || {}
  for (const id of ids) {
    const it = integrations.find((i) => i.id === id)
    const pos = positions[id]
    if (!it?.orgId || !it?.kind || !it?.slug || !pos) continue
    const sig = `${Math.round(pos.x)},${Math.round(pos.y)}`
    if (INT_POSITION_LAST.get(id) === sig) continue
    clearTimeout(INT_POSITION_TIMERS.get(id))
    INT_POSITION_TIMERS.set(
      id,
      setTimeout(async () => {
        INT_POSITION_TIMERS.delete(id)
        INT_POSITION_LAST.set(id, sig)
        const bridge = await sdkBridge()
        if (!bridge?.saveIntegration) return
        // Bug D fix: hydrate the governance Supabase session from cross-origin
        // cookie before the saveIntegration call. Without this, the governance
        // client has no active session when the canvas tab hasn't done its own
        // auth flow — `_adminCall` then returns 'not authenticated'.
        // Same pattern as loadIntegrationUnitsSafe (line ~246 above).
        try { bridge.hydrateSupabaseSessionsFromCookie?.() } catch {}
        const nextConfig = {
          ...(it.config || {}),
          canvasPosition: { x: Math.round(pos.x), y: Math.round(pos.y) }
        }
        try {
          const res = await bridge.saveIntegration({
            orgId: it.orgId,
            kind: it.kind,
            slug: it.slug,
            scopeType: it.scope,
            scopeId: it.scopeId,
            config: nextConfig
          })
          if (!res?.ok) {
            INT_POSITION_LAST.delete(id)
            console.warn(
              '[workspace] persist int position failed:',
              id,
              res?.body?.error
            )
            return
          }
          // Mirror the saved config back into state so future saves merge
          // against fresh config instead of a stale snapshot.
          const updated = (root.integrations || []).map((r) =>
            r.id === id ? { ...r, config: nextConfig } : r
          )
          root.update({ integrations: updated })
        } catch (err) {
          INT_POSITION_LAST.delete(id)
          console.warn(
            '[workspace] persist int position threw:',
            id,
            err?.message || err
          )
        }
      }, POSITION_DEBOUNCE_MS)
    )
  }
}

// Aggregate ALL workspace-scoped installable instances across every domain
// (integration, mcp, library, plugin). Returns a flat array in the common
// canvas installable shape:
//   { id, domain, name, kind, category, icon, scopeType, scopeId, status, _raw }
// The MCP path calls listConnectors from sdk-bridge. Library/plugin stubs
// return empty arrays until those SDK helpers are wired in Phase 6.
async function loadWorkspaceInstallablesSafe(orgId) {
  const results = []

  // 1. Integrations (already covered by flattenIntegrationUnits, but we need
  //    them again here as a flat list for the canvas nodes layer).
  try {
    const units = await loadIntegrationUnitsSafe(orgId)
    for (const unit of units || []) {
      for (const inst of unit.instances || []) {
        if (inst.enabled === false) continue
        const scopeType = inst.scope_type || unit.default_scope || 'org'
        const config = inst.config && typeof inst.config === 'object' ? inst.config : {}
        results.push({
          id: `int:${scopeType}:${unit.kind}:${inst.slug}`,
          domain: 'integration',
          name: inst.display_name || unit.label || unit.kind,
          kind: unit.kind,
          category: unit.category,
          icon: sanitiseIcon(unit.icon),
          scopeType,
          scopeId: inst.scope_id || null,
          slug: inst.slug,
          orgId,
          config,
          status: inst.enabled !== false ? 'enabled' : 'disabled',
          _raw: inst
        })
      }
    }
  } catch (err) {
    console.warn('[workspace] loadWorkspaceInstallablesSafe integrations:', err?.message || err)
  }

  // 2. MCP connectors — loaded from sdk-bridge.listConnectors (RLS filters
  //    by jwt_active_workspace so no org scope needed).
  try {
    const mod = await import('../../shared/governanceBridge.js')
    mod.hydrateSupabaseSessionsFromCookie?.()
    if (typeof mod.listConnectors === 'function') {
      const { data, error } = await mod.listConnectors()
      if (!error && Array.isArray(data)) {
        for (const c of data) {
          results.push({
            id: `mcp:${c.scope_type || 'workspace'}:${c.id}`,
            domain: 'mcp',
            name: c.name || c.url || 'MCP',
            kind: 'mcp_connector',
            category: 'automation',
            icon: null,
            scopeType: c.scope_type || 'workspace',
            scopeId: c.scope_id || null,
            config: c.metadata || {},
            status: c.status || 'enabled',
            _raw: c
          })
        }
      }
    }
  } catch (err) {
    console.warn('[workspace] loadWorkspaceInstallablesSafe mcp:', err?.message || err)
  }

  // 3. Libraries stub — will be wired in Phase 6 when sdk-bridge exposes
  //    listWorkspaceLibraries. Returns empty for now so the canvas is ready.

  // 4. Plugins stub — same as libraries; deferred to Phase 6.

  return results
}

// Persist canvas position for a non-project installable record. For
// MCP connectors this writes to `mcp_connectors.metadata.canvasPosition`
// via sdk-bridge.saveConnectorMetadata (fallback: direct Supabase PATCH).
// For integrations the existing `persistIntegrationPositions` covers it.
const MCP_POSITION_TIMERS = new Map()
const MCP_POSITION_LAST = new Map()

function persistInstallableMCPPosition(root, ids) {
  const installables = root?._installables || []
  const positions = root?.positions || {}
  for (const id of ids) {
    const it = installables.find((i) => i.id === id)
    if (!it || it.domain !== 'mcp') continue
    const pos = positions[id]
    if (!pos) continue
    const sig = `${Math.round(pos.x)},${Math.round(pos.y)}`
    if (MCP_POSITION_LAST.get(id) === sig) continue
    clearTimeout(MCP_POSITION_TIMERS.get(id))
    MCP_POSITION_TIMERS.set(
      id,
      setTimeout(async () => {
        MCP_POSITION_TIMERS.delete(id)
        MCP_POSITION_LAST.set(id, sig)
        const bridge = await sdkBridge()
        if (!bridge) return
        const canvasPosition = { x: Math.round(pos.x), y: Math.round(pos.y) }
        const connectorId = it._raw?.id
        if (!connectorId) return
        try {
          // Primary: use sdk-bridge.saveConnectorMetadata when available.
          if (typeof bridge.saveConnectorMetadata === 'function') {
            await bridge.saveConnectorMetadata(connectorId, { canvasPosition })
            return
          }
          // Fallback: direct Supabase PATCH on metadata JSONB column.
          const client = bridge.getClient?.()
          if (!client) return
          const prev = it.config || {}
          await client.from('mcp_connectors')
            .update({ metadata: { ...prev, canvasPosition } })
            .eq('id', connectorId)
        } catch (err) {
          MCP_POSITION_LAST.delete(id)
          console.warn('[workspace] persist mcp position failed:', id, err?.message || err)
        }
      }, POSITION_DEBOUNCE_MS)
    )
  }
}

// Refresh all workspace-scoped installables (integrations + MCP + future
// domains) and update root._installables + positions. Called after install,
// delete, or scope change. Replaces the integrations-only refreshIntegrations()
// in the post-install path (refreshIntegrations is preserved for backward compat).
async function refreshWorkspaceInstallables(root) {
  const slug = root?.slug
  if (!slug) return
  const orgId = await orgIdForSlug(slug)
  if (!orgId) return

  const all = await loadWorkspaceInstallablesSafe(orgId)
  const positions = { ...(root.positions || {}) }

  let wsRowX = 24
  const wsRowY = -INSTALLABLE_H - 48
  let mcpRowX = 24
  const mcpRowY = wsRowY - INSTALLABLE_H - 24

  all.forEach((it) => {
    if (positions[it.id]) return // already positioned from previous load
    const saved = it.config?.canvasPosition || it._raw?.metadata?.canvasPosition
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
      positions[it.id] = { x: +saved.x, y: +saved.y, w: INSTALLABLE_W, h: INSTALLABLE_H }
      return
    }
    if (it.domain === 'mcp') {
      positions[it.id] = { x: mcpRowX, y: mcpRowY, w: INSTALLABLE_W, h: INSTALLABLE_H }
      mcpRowX += INSTALLABLE_W + 24
    } else {
      positions[it.id] = { x: wsRowX, y: wsRowY, w: INSTALLABLE_W, h: INSTALLABLE_H }
      wsRowX += INSTALLABLE_W + 24
    }
  })

  root.update({ _installables: all, positions })
  redrawConnections()
}

// Arrow create/delete between an integration chip and a project reassigns
// the integration's scope via `integration-admin.assign_scope`. Creating
// an edge moves scope → project. Deleting it moves scope → org.
async function saveIntegrationFromPicker(root) {
  const p = root?.integrationPicker
  if (!p || p.saving) return
  const kind = p.selectedKind
  if (!kind) return
  const orgId = await orgIdForSlug(root.slug)
  if (!orgId) {
    root.update({
      integrationPicker: { ...p, error: 'No org id for ' + root.slug }
    })
    return
  }
  const form = p.form || {}
  const slug = (form.__slug || '').trim() || 'default'
  const scope = form.__scope || kind.default_scope || 'org'
  const scopeId =
    scope === 'project'
      ? form.__scopeId
      : scope === 'workspace'
        ? form.__scopeId
        : null
  if (scope === 'project' && !scopeId) {
    root.update({
      integrationPicker: { ...p, error: 'Pick a project for project scope' }
    })
    return
  }
  const config = {}
  for (const [k, v] of Object.entries(form)) {
    if (k.startsWith('__')) continue
    config[k] = v
  }
  root.update({ integrationPicker: { ...p, saving: true, error: null } })
  try {
    const bridge = await sdkBridge()
    if (!bridge?.saveIntegration) throw new Error('sdk-bridge not available')
    const res = await bridge.saveIntegration({
      orgId,
      kind: kind.kind,
      slug,
      scopeType: scope,
      scopeId,
      displayName: form.__displayName || config.display_name || null,
      config,
      secret: form.__secret || undefined
    })
    if (!res?.ok) {
      root.update({
        integrationPicker: {
          ...p,
          saving: false,
          error: res?.body?.error || 'save failed'
        }
      })
      return
    }
    root.update({ integrationPicker: { open: false } })
    // Re-pull integrations so the new row is reflected on shelf / canvas.
    await refreshIntegrations(root)
  } catch (err) {
    root.update({
      integrationPicker: {
        ...p,
        saving: false,
        error: err?.message || 'save failed'
      }
    })
  }
}

async function refreshIntegrations(root) {
  const slug = root?.slug
  if (!slug) return
  const orgId = await orgIdForSlug(slug)
  if (!orgId) return
  const units = await loadIntegrationUnitsSafe(orgId)
  const nodes = root.nodes || []
  const integrations = flattenIntegrationUnits(units, nodes, orgId)
  const positions = { ...(root.positions || {}) }
  integrations.forEach((it) => {
    if (it.scope !== 'project' || !it.projectId) return
    if (positions[it.id]) return
    const saved = it.config?.canvasPosition
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
      positions[it.id] = {
        x: +saved.x,
        y: +saved.y,
        w: INTEGRATION_NODE_W,
        h: INTEGRATION_NODE_H
      }
      return
    }
    const anchor = positions[it.projectId]
    if (!anchor) return
    positions[it.id] = {
      x: anchor.x,
      y: anchor.y + NODE_H + 24,
      w: INTEGRATION_NODE_W,
      h: INTEGRATION_NODE_H
    }
  })
  root.update({ integrations, positions })
  redrawConnections()
  // Phase 5: also refresh the full installables layer so new tiles appear.
  refreshWorkspaceInstallables(root).catch(() => {})
}

async function persistIntegrationScope(
  root,
  integrationId,
  toScope,
  toProjectId
) {
  const it = (root?.integrations || []).find((i) => i.id === integrationId)
  if (!it?.orgId || !it?.kind || !it?.slug) return
  if (INT_SCOPE_PENDING.get(integrationId)) return
  INT_SCOPE_PENDING.set(integrationId, true)
  const bridge = await sdkBridge()
  if (!bridge?.assignIntegrationScope) {
    INT_SCOPE_PENDING.delete(integrationId)
    return
  }
  const toProjectNode = toProjectId
    ? (root.nodes || []).find((n) => n.id === toProjectId)
    : null
  const toScopeId = toScope === 'project' ? toProjectNode?._id || null : null
  try {
    const res = await bridge.assignIntegrationScope({
      orgId: it.orgId,
      kind: it.kind,
      slug: it.slug,
      fromScopeType: it.scope,
      fromScopeId: it.scopeId,
      toScopeType: toScope,
      toScopeId
    })
    if (!res?.ok) {
      console.warn(
        '[workspace] assign_scope failed:',
        integrationId,
        res?.body?.error
      )
      return
    }
    // Update the record in place so subsequent saves reference the new scope.
    const updated = (root.integrations || []).map((r) =>
      r.id === integrationId
        ? {
            ...r,
            scope: toScope,
            scopeId: toScopeId,
            projectId: toScope === 'project' ? toProjectId : undefined,
            id: `int:${toScope}:${r.kind}:${r.slug}`
          }
        : r
    )
    root.update({ integrations: updated })
  } catch (err) {
    console.warn(
      '[workspace] assign_scope threw:',
      integrationId,
      err?.message || err
    )
  } finally {
    INT_SCOPE_PENDING.delete(integrationId)
  }
}

function persistPositions(root, nodeIds) {
  const nodes = root?.nodes || []
  const positions = root?.positions || {}
  const seen = new Set()
  for (const nodeId of nodeIds) {
    if (seen.has(nodeId)) continue
    seen.add(nodeId)
    const node = nodes.find((n) => n.id === nodeId)
    const pos = positions[nodeId]
    if (!node?._id || !pos) continue
    const canvasPosition = { x: Math.round(pos.x), y: Math.round(pos.y) }
    const sig = `${canvasPosition.x},${canvasPosition.y}`
    if (POSITION_LAST.get(nodeId) === sig) continue
    clearTimeout(POSITION_TIMERS.get(nodeId))
    // Record pending write so beforeunload can flush it even if the
    // debounce hasn't fired yet.
    POSITION_PENDING.set(nodeId, {
      nodeDbId: node._id,
      nodeLabel: node.id,
      canvasPosition,
      sig
    })
    POSITION_TIMERS.set(
      nodeId,
      setTimeout(() => firePositionWrite(nodeId), POSITION_DEBOUNCE_MS)
    )
  }
}

// Write a canvasPosition to the server. `updateProject` silently strips
// unlisted fields (canvasPosition isn't in its allowed set), so we go
// through `setProjectValue` which accepts arbitrary top-level paths and
// stores the value verbatim — readable back via `getProjectData`.
async function writeCanvasPositionToServer(projectDbId, canvasPosition) {
  const sdk = getSDK()
  if (typeof sdk.setProjectValue !== 'function') {
    // Fallback for older SDKs: still call updateProject (no-op on modern
    // server, but harmless).
    return sdk.updateProject(projectDbId, { canvasPosition })
  }
  return sdk.setProjectValue(projectDbId, ['canvasPosition'], canvasPosition)
}

async function firePositionWrite(nodeId) {
  const pending = POSITION_PENDING.get(nodeId)
  if (!pending) return
  POSITION_PENDING.delete(nodeId)
  POSITION_TIMERS.delete(nodeId)
  POSITION_LAST.set(nodeId, pending.sig)
  // Always mirror to localStorage so reload is instant even before the
  // background refetch runs — and works across tabs within the origin.
  writePositionToLocal(pending.nodeDbId, pending.canvasPosition)
  try {
    await writeCanvasPositionToServer(pending.nodeDbId, pending.canvasPosition)
    patchCachedProject(pending.nodeDbId, {
      canvasPosition: pending.canvasPosition
    })
  } catch (err) {
    POSITION_LAST.delete(nodeId)
    console.error(
      '[workspace] persist position failed:',
      pending.nodeLabel,
      err?.message || err
    )
    // One quick retry — covers transient 429s from bursty drags.
    setTimeout(async () => {
      try {
        await writeCanvasPositionToServer(
          pending.nodeDbId,
          pending.canvasPosition
        )
        patchCachedProject(pending.nodeDbId, {
          canvasPosition: pending.canvasPosition
        })
        POSITION_LAST.set(nodeId, pending.sig)
      } catch (err2) {
        console.error(
          '[workspace] persist position retry failed:',
          pending.nodeLabel,
          err2?.message || err2
        )
      }
    }, 1500)
  }
}

// --- localStorage position fallback -----------------------------------------
// The server list endpoint (`getProjects`) returns a slim projection that
// doesn't include `canvasPosition`. `getProjectData` does, but it also
// ships components/pages and is heavy. To keep reloads feeling instant
// we snapshot positions in localStorage keyed by project id, and hydrate
// from there while the background refetch confirms with fresh server data.
const LOCAL_POS_KEY = '__canvas_positions_v1'

function readAllLocalPositions() {
  try {
    const raw = localStorage.getItem(LOCAL_POS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writePositionToLocal(projectDbId, canvasPosition) {
  try {
    const all = readAllLocalPositions()
    all[projectDbId] = canvasPosition
    localStorage.setItem(LOCAL_POS_KEY, JSON.stringify(all))
  } catch {}
}

// Fetch authoritative positions from the server for the given project
// DB ids. Uses `getProjectData` because the list endpoint's projection
// doesn't include custom fields like `canvasPosition`. Runs concurrently
// with a small cap so large workspaces don't hammer the API. Emits an
// updated positions map for only the nodes that actually had a stored
// position — callers merge into `root.positions`.
const POSITION_SYNC_CONCURRENCY = 3
async function fetchServerPositions(projectDbIds, onPositionResolved) {
  const sdk = getSDK()
  if (typeof sdk.getProjectData !== 'function') return
  const queue = [...projectDbIds]
  const runOne = async () => {
    while (queue.length) {
      const id = queue.shift()
      try {
        const data = await sdk.getProjectData(id)
        const pos = data?.canvasPosition
        if (pos && Number.isFinite(+pos.x) && Number.isFinite(+pos.y)) {
          const clean = { x: +pos.x, y: +pos.y }
          writePositionToLocal(id, clean)
          onPositionResolved?.(id, clean)
        }
      } catch (err) {
        // Silent: transient failures aren't worth UI noise — next
        // drag/reload will retry. 429 in particular self-heals once
        // the server cools down.
      }
    }
  }
  await Promise.all(
    Array.from(
      { length: Math.min(POSITION_SYNC_CONCURRENCY, queue.length) },
      runOne
    )
  )
}

// `fetch` may be cancelled by the browser on unload; `sendBeacon` is
// queued by the platform and survives the navigation. Falls back to
// the regular debounced write path when not on an unload boundary.
function beaconCanvasPosition(projectDbId, canvasPosition) {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false
  const sdk = getSDK()
  const apiUrl = sdk?.apiUrl
  const token = sdk?.getAuthToken?.()
  if (!apiUrl || !token) return false
  try {
    const blob = new Blob(
      [JSON.stringify({ path: ['canvasPosition'], value: canvasPosition, token })],
      { type: 'application/json' }
    )
    return navigator.sendBeacon(`${apiUrl}/projects/${projectDbId}/value`, blob)
  } catch { return false }
}

function flushPendingPositions() {
  for (const nodeId of Array.from(POSITION_PENDING.keys())) {
    const pending = POSITION_PENDING.get(nodeId)
    clearTimeout(POSITION_TIMERS.get(nodeId))
    if (!pending) continue
    POSITION_PENDING.delete(nodeId)
    POSITION_TIMERS.delete(nodeId)
    POSITION_LAST.set(nodeId, pending.sig)
    writePositionToLocal(pending.nodeDbId, pending.canvasPosition)
    if (!beaconCanvasPosition(pending.nodeDbId, pending.canvasPosition)) {
      writeCanvasPositionToServer(pending.nodeDbId, pending.canvasPosition).catch(() => {})
    }
  }
}

// `storage` event fires only on OTHER tabs of the same origin (per spec).
function onCrossTabPositionUpdate(ev) {
  if (ev.key !== LOCAL_POS_KEY || !ev.newValue) return
  const root = WORKSPACE_ROOT
  if (!root) return
  let next, prev
  try {
    next = JSON.parse(ev.newValue)
    prev = ev.oldValue ? JSON.parse(ev.oldValue) : {}
  } catch { return }
  if (!next || typeof next !== 'object') return
  const nodes = root.nodes || []
  const byDbId = new Map(nodes.filter((n) => n._id).map((n) => [n._id, n]))
  const nextPositions = { ...(root.positions || {}) }
  let changed = false
  for (const [dbId, pos] of Object.entries(next)) {
    const old = prev?.[dbId]
    if (old && +old.x === +pos.x && +old.y === +pos.y) continue
    if (!pos || !Number.isFinite(+pos.x) || !Number.isFinite(+pos.y)) continue
    const node = byDbId.get(dbId)
    if (!node) continue
    const nd = dimsFor(node.projectType)
    nextPositions[node.id] = { x: +pos.x, y: +pos.y, w: nd.w, h: nd.h }
    changed = true
  }
  if (!changed) return
  root.update({ positions: nextPositions })
  redrawConnections()
}

if (typeof window !== 'undefined' && !window.__wsFlushHooked) {
  window.__wsFlushHooked = true
  // `pagehide` fires on real unload and on bfcache stashing; `beforeunload`
  // doesn't fire on iOS. Covering both is safest.
  window.addEventListener('pagehide', flushPendingPositions)
  window.addEventListener('beforeunload', flushPendingPositions)
  window.addEventListener('storage', onCrossTabPositionUpdate)
}

// Post-install refresh: governance fires `{ type: 'symbols:integration-installed' }`
// via postMessage after a successful marketplace install. The canvas receives it
// here and calls refreshIntegrations so the new row appears within ~2s without
// requiring a manual reload. Hoisted once; guard prevents duplicate listeners
// across hot-module-replacement reloads.
function onInstallMessage(ev) {
  if (ev?.data?.type !== 'symbols:integration-installed') return
  const root = WORKSPACE_ROOT
  if (!root) return
  // Small delay so the server-side DB write completes before we re-fetch.
  // refreshIntegrations also triggers refreshWorkspaceInstallables (Phase 5).
  setTimeout(() => refreshIntegrations(root), 600)
}
if (typeof window !== 'undefined' && !window.__wsInstallMsgHooked) {
  window.__wsInstallMsgHooked = true
  window.addEventListener('message', onInstallMessage)
}

// Re-assert the live pz scale on ZoomInNavbar's Box, which DOMQL's
// reactive re-render keeps reverting to the static `text: '100%'`
// default. Node is resolved lazily and cached so the 250ms backstop
// doesn't re-scan the DOM every tick.
let ZOOM_LABEL_NODE = null
function resolveZoomLabelNode () {
  if (ZOOM_LABEL_NODE && document.contains(ZOOM_LABEL_NODE)) return ZOOM_LABEL_NODE
  const viewH = window.innerHeight
  const viewW = window.innerWidth
  for (const n of document.querySelectorAll('span, div')) {
    const t = (n.innerText || '').trim()
    if (!/^\d+%$/.test(t)) continue
    const r = n.getBoundingClientRect()
    if (r.bottom < viewH - 160 || r.right < viewW - 360) continue
    ZOOM_LABEL_NODE = n
    return n
  }
  return null
}
function writeZoomLabel (pz) {
  if (!pz || typeof pz.getTransform !== 'function') return
  const pct = Math.round(pz.getTransform().scale * 100) + '%'
  const node = resolveZoomLabelNode()
  if (!node) return
  // DOMQL re-renders clobber this back to '100%' so we can't skip the
  // write just because pct is unchanged — always compare against the
  // actual DOM value.
  if (node.innerText === pct) return
  node.innerText = pct
}

const PERSIST_DEBOUNCE_MS = 500
const PERSIST_TIMERS = new Map()
// `/projects/:id/libraries` validates that targets are active *shared*
// libraries and 404s otherwise — too strict for arbitrary project→project
// arrows on the workspace. `PATCH /projects/:id` with `useLibraries` just
// persists the ref list, which is what we want.
const LAST_SIGNATURE = new Map()

function persistSharedLibraries(root, projectNodeIds) {
  const nodes = root?.nodes || []
  const connections = root?.connections || []
  const ids = Array.from(new Set(projectNodeIds)).filter(Boolean)
  for (const nodeId of ids) {
    const node = nodes.find((n) => n.id === nodeId)
    if (!node?._id) continue
    const libs = computeSharedLibs(nodeId, connections, nodes)
    const signature = libs.slice().sort().join('|')
    if (!LAST_SIGNATURE.has(nodeId)) {
      LAST_SIGNATURE.set(
        nodeId,
        (node.useLibraries || []).slice().sort().join('|')
      )
    }
    if (LAST_SIGNATURE.get(nodeId) === signature) continue
    const prev = LAST_SIGNATURE.get(nodeId)
    clearTimeout(PERSIST_TIMERS.get(nodeId))
    PERSIST_TIMERS.set(
      nodeId,
      setTimeout(async () => {
        PERSIST_TIMERS.delete(nodeId)
        LAST_SIGNATURE.set(nodeId, signature)
        try {
          await getSDK().updateProject(node._id, { useLibraries: libs })
          patchCachedProject(node._id, { useLibraries: libs })
        } catch (err) {
          LAST_SIGNATURE.set(nodeId, prev)
          console.warn(
            '[workspace] persist sharedLibraries failed:',
            node.id,
            err?.message || err
          )
        }
      }, PERSIST_DEBOUNCE_MS)
    )
  }
}

function anchorPoint(pos, side) {
  if (side === 'top') return { x: pos.x + pos.w / 2, y: pos.y }
  if (side === 'bottom') return { x: pos.x + pos.w / 2, y: pos.y + pos.h }
  if (side === 'left') return { x: pos.x, y: pos.y + pos.h / 2 }
  return { x: pos.x + pos.w, y: pos.y + pos.h / 2 }
}

function pickSides(fromPos, toPos) {
  const dx = toPos.x + toPos.w / 2 - (fromPos.x + fromPos.w / 2)
  const dy = toPos.y + toPos.h / 2 - (fromPos.y + fromPos.h / 2)
  if (Math.abs(dx) >= Math.abs(dy))
    return dx >= 0 ? ['right', 'left'] : ['left', 'right']
  return dy >= 0 ? ['bottom', 'top'] : ['top', 'bottom']
}

function curvePath(fromPos, toPos) {
  const [fSide, tSide] = pickSides(fromPos, toPos)
  const a = anchorPoint(fromPos, fSide)
  const b = anchorPoint(toPos, tSide)
  const vertical = fSide === 'top' || fSide === 'bottom'
  if (vertical) {
    const off = Math.max(40, Math.abs(b.y - a.y) * 0.5)
    const c1y = a.y + (fSide === 'bottom' ? off : -off)
    const c2y = b.y + (tSide === 'bottom' ? off : -off)
    return `M${a.x},${a.y} C${a.x},${c1y} ${b.x},${c2y} ${b.x},${b.y}`
  }
  const off = Math.max(40, Math.abs(b.x - a.x) * 0.5)
  const c1x = a.x + (fSide === 'right' ? off : -off)
  const c2x = b.x + (tSide === 'right' ? off : -off)
  return `M${a.x},${a.y} C${c1x},${a.y} ${c2x},${b.y} ${b.x},${b.y}`
}

// Approximate midpoint of the Bezier curve built by `curvePath`: use the
// mean of the two endpoints — close enough for placing a delete button.
function curveMidpoint(fromPos, toPos) {
  const [fSide, tSide] = pickSides(fromPos, toPos)
  const a = anchorPoint(fromPos, fSide)
  const b = anchorPoint(toPos, tSide)
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

function buildConnectionsSVG(root) {
  const positions = root?.positions || {}
  const connections = root?.connections || []
  const integrations = root?.integrations || []
  const intById = Object.fromEntries(integrations.map((i) => [i.id, i]))
  const stroke = 'rgba(255,255,255,0.6)'

  const groups = connections
    .map((c) => {
      const from = positions[c.from]
      const to = positions[c.to]
      if (!from || !to) return ''
      const d = curvePath(from, to)
      const mid = curveMidpoint(from, to)
      const isIntegration =
        c.kind === 'integration' || intById[c.from] || intById[c.to]
      const intCategory = intById[c.from]?.category || intById[c.to]?.category
      const intColor = integrationColor(intCategory)
      const pathStroke = isIntegration ? intColor : stroke
      const dashAttr = isIntegration ? ' stroke-dasharray="6 4"' : ''
      const markerId = isIntegration ? 'ws-arrow-int' : 'ws-arrow'
      return (
        `<g class="conn" data-connection-id="${c.id}">` +
        `<path d="${d}" stroke="${pathStroke}" stroke-width="1.5" fill="none" opacity="0.85"${dashAttr} marker-end="url(#${markerId})"/>` +
        // Wide invisible hit zone → easy click target along the whole path.
        `<path d="${d}" stroke="transparent" stroke-width="14" fill="none" style="pointer-events:stroke;cursor:pointer"><title>click to remove</title></path>` +
        // × button at the midpoint, revealed on hover of the whole <g>.
        `<g class="conn-x" transform="translate(${mid.x},${mid.y})" style="pointer-events:all;cursor:pointer" data-connection-id="${c.id}">` +
        `<circle r="8" fill="rgba(20,22,27,0.9)" stroke="rgba(255,255,255,0.25)" stroke-width="1"/>` +
        `<path d="M-3,-3 L3,3 M-3,3 L3,-3" stroke="rgba(255,255,255,0.85)" stroke-width="1.2" stroke-linecap="round"/>` +
        `</g>` +
        `</g>`
      )
    })
    .join('')

  const linking = root?.linking
  let ghost = ''
  if (linking?.fromId) {
    const from = positions[linking.fromId]
    if (from) {
      const target = linking.toId ? positions[linking.toId] : null
      let d
      if (target) d = curvePath(from, target)
      else {
        const cursorRect = { x: linking.toX, y: linking.toY, w: 0, h: 0 }
        const [fSide] = pickSides(from, cursorRect)
        const start = anchorPoint(from, fSide)
        d = `M${start.x},${start.y} L${linking.toX},${linking.toY}`
      }
      ghost = `<path d="${d}" stroke="rgba(255,255,255,0.95)" stroke-width="2.5" stroke-dasharray="8 5" fill="none" style="filter:drop-shadow(0 0 6px rgba(0,0,0,0.6))"/>`
    }
  }

  const style = `<style>.conn-x{opacity:0;transition:opacity .12s ease}.conn:hover .conn-x{opacity:1}</style>`

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${BOARD_W}" height="${BOARD_H}" style="position:absolute;top:0;left:0;pointer-events:none;overflow:visible">${style}<defs><marker id="ws-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="${stroke}"/></marker><marker id="ws-arrow-int" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,2 L10,5 L0,8 z" fill="#7aa2ff" opacity="0.9"/></marker></defs>${groups}${ghost}</svg>`
}

function layoutPositions(nodes) {
  const positions = {}
  let autoIndex = 0
  nodes.forEach((n) => {
    if (
      n.canvasPosition &&
      Number.isFinite(n.canvasPosition.x) &&
      Number.isFinite(n.canvasPosition.y)
    ) {
      positions[n.id] = {
        x: n.canvasPosition.x,
        y: n.canvasPosition.y,
        w: dimsFor(n.projectType).w,
        h: dimsFor(n.projectType).h
      }
      return
    }
    const col = autoIndex % PER_ROW
    const row = Math.floor(autoIndex / PER_ROW)
    autoIndex++
    positions[n.id] = {
      x: col * COL_STRIDE,
      y: row * ROW_STRIDE,
      w: dimsFor(n.projectType).w,
      h: dimsFor(n.projectType).h
    }
  })
  return positions
}

let PZ = null
let BOARD_EL = null
let CONNECTIONS_NODE = null
let WORKSPACE_ROOT = null
const ACTIVE_DRAG_IDS = new Set()

// Compute the live screen-space position of a tile's bottom-right anchor
// (where the project context-menu attaches). Reads board-coords from
// root.positions[id] and applies the current panzoom transform.
//
// Returns { tileTop, tileBottom, tileLeft, tileRight } in viewport CSS
// pixels, or null when position/transform aren't ready yet (initial mount,
// or stale openMenuId after a workspace switch).
function computeMenuScreenPos(root, nodeId) {
  if (!root || !nodeId) return null
  const pos = root.positions?.[nodeId]
  if (!pos) return null
  // panzoom transform — fall back to identity when PZ hasn't initialized
  // yet (menu opens during the first paint before pz.zoomAbs runs).
  const t = PZ?.getTransform?.() || { x: 0, y: 0, scale: 1 }
  const w = pos.w || NODE_W
  const h = pos.h || NODE_H
  const tileLeft = pos.x * t.scale + t.x
  const tileTop = pos.y * t.scale + t.y
  const tileRight = tileLeft + w * t.scale
  const tileBottom = tileTop + h * t.scale
  return { tileTop, tileBottom, tileLeft, tileRight }
}

// During drag we move cards via direct DOM style writes (bypassing
// root.update) to avoid triggering the full reactive subtree on every
// mousemove. DRAG_DOM_NODES maps nodeId → DOM element for fast lookup.
// DRAG_RAF holds the pending rAF handle so we don't schedule more than
// one SVG redraw per frame.
const DRAG_DOM_NODES = new Map()
let DRAG_RAF = 0

// Dropping a card onto another card / an existing workspace frame assigns
// it to a `workspaceN` slot — the visual "group" that wraps both.
const GROUP_SLOTS = Array.from({ length: 50 }, (_, i) => 'group' + (i + 1))
const GROUP_PAD = 20

function currentGroup(node, state) {
  return state?.groupMap?.[node.id] || null
}

function nodesOfGroup(wsId, state) {
  return (state?.nodes || []).filter((n) => currentGroup(n, state) === wsId)
}

function groupBbox(state, wsId) {
  const items = nodesOfGroup(wsId, state)
    .filter((n) => !ACTIVE_DRAG_IDS.has(n.id))
    .map((n) => state.positions?.[n.id])
    .filter(Boolean)
  if (!items.length) return null
  const minX = Math.min(...items.map((p) => p.x))
  const minY = Math.min(...items.map((p) => p.y))
  const maxX = Math.max(...items.map((p) => p.x + p.w))
  const maxY = Math.max(...items.map((p) => p.y + p.h))
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

function nextGroupName(state) {
  const used = new Set(Object.values(state.groupMap || {}))
  for (const slot of GROUP_SLOTS) if (!used.has(slot)) return slot
  return 'group' + Date.now()
}

function detectDropTarget(selfId, box, state) {
  const cx = box.x + box.w / 2
  const cy = box.y + box.h / 2
  for (const n of state.nodes || []) {
    if (n.id === selfId) continue
    const p = state.positions?.[n.id]
    if (!p) continue
    if (cx >= p.x && cx <= p.x + p.w && cy >= p.y && cy <= p.y + p.h) {
      const ws = currentGroup(n, state)
      if (ws) return { nodeId: null, nodeGroupId: ws, groupId: ws }
      return { nodeId: n.id, nodeGroupId: null, groupId: null }
    }
  }
  for (const ws of GROUP_SLOTS) {
    const bb = groupBbox(state, ws)
    if (
      bb &&
      cx >= bb.x &&
      cx <= bb.x + bb.w &&
      cy >= bb.y &&
      cy <= bb.y + bb.h
    ) {
      return { nodeId: null, nodeGroupId: null, groupId: ws }
    }
  }
  return { nodeId: null, nodeGroupId: null, groupId: null }
}

function restackGroup(wsId, state) {
  const positions = { ...state.positions }
  const members = nodesOfGroup(wsId, state)
    .map((n) => n.id)
    .filter((id) => positions[id])
  if (!members.length) return state
  const minX = Math.min(...members.map((id) => positions[id].x))
  const minY = Math.min(...members.map((id) => positions[id].y))
  members.forEach((id, i) => {
    const col = i % PER_ROW
    const row = Math.floor(i / PER_ROW)
    positions[id] = {
      ...positions[id],
      x: minX + col * COL_STRIDE,
      y: minY + row * ROW_STRIDE
    }
  })
  return { ...state, positions }
}

function currentScale() {
  return PZ?.getTransform?.().scale || 1
}

function redrawConnections(override) {
  if (!CONNECTIONS_NODE) return
  const root = override || WORKSPACE_ROOT
  if (!root) return
  CONNECTIONS_NODE.innerHTML = buildConnectionsSVG(root)
}

function nodeIdAtPoint(doc, clientX, clientY, selfId) {
  const list = doc.elementsFromPoint(clientX, clientY)
  for (const el of list) {
    const id = el.dataset?.nodeId
    if (id && id !== selfId) return id
  }
  return null
}

function snapToGrid(n) {
  return Math.round(n / GRID_STEP) * GRID_STEP
}

function getDragMode(root) {
  return root?.dragMode || 'grid'
}

function startDrag(e, el, root, id, opts = {}) {
  if (e.button !== 0) return
  e.stopPropagation()
  const doc = el.context.document
  const pos = root.positions?.[id]
  if (!pos) return

  const isGroupDrag = !!opts.group
  const groupWs = opts.groupWs || null
  const groupIds =
    isGroupDrag && groupWs ? nodesOfGroup(groupWs, root).map((n) => n.id) : [id]

  const starts = {}
  for (const gid of groupIds) {
    const p = root.positions?.[gid]
    if (p) starts[gid] = { x: p.x, y: p.y, w: p.w, h: p.h }
  }

  const start = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y }
  let moved = false
  const selfNode = (root.nodes || []).find((n) => n.id === id)
  const originalWs = selfNode ? currentGroup(selfNode, root) : null

  if (!isGroupDrag) {
    for (const gid of groupIds) ACTIVE_DRAG_IDS.add(gid)
  }
  // Only update draggingId in root state — NOT positions. Position updates
  // during drag bypass root.update() to prevent the reactive Nodes children
  // from re-evaluating (and state.replace()-ing) every ProjectNode on every
  // mousemove event. Direct DOM style writes are used instead; the final
  // position is committed to root.positions once on mouseup.
  root.update({ draggingId: id })

  // Track live positions in a local map so drop-target detection reads the
  // current drag position without touching root state.
  const livePositions = { ...root.positions }

  const onMove = (ev) => {
    const scale = currentScale()
    const dx = (ev.clientX - start.mx) / scale
    const dy = (ev.clientY - start.my) / scale
    if (!moved && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) moved = true
    if (!moved) return
    const applyMode = (x, y) =>
      getDragMode(root) === 'grid'
        ? { x: snapToGrid(x), y: snapToGrid(y) }
        : { x, y }
    const snapped = applyMode(start.px + dx, start.py + dy)
    const snappedDx = snapped.x - start.px
    const snappedDy = snapped.y - start.py

    // Move dragged DOM nodes directly — no root.update(), no reactive cascade.
    for (const gid of groupIds) {
      const st = starts[gid]
      const nx = st.x + snappedDx
      const ny = st.y + snappedDy
      livePositions[gid] = { ...livePositions[gid], x: nx, y: ny }
      const domNode = DRAG_DOM_NODES.get(gid)
      if (domNode) {
        domNode.style.left = nx + 'px'
        domNode.style.top = ny + 'px'
      }
    }

    let hoverGroup = null
    let hoverNode = null
    if (!isGroupDrag) {
      const t = detectDropTarget(id, livePositions[id], {
        ...root,
        positions: livePositions
      })
      if (t.groupId && t.groupId !== originalWs) hoverGroup = t.groupId
      if (t.nodeId && (!t.nodeGroupId || t.nodeGroupId !== originalWs)) {
        hoverNode = t.nodeId
      }
    }
    // Only push hoverGroup/hoverNode into root state — not positions.
    // Schedule SVG redraw at most once per animation frame.
    root.update({ hoverGroup, hoverNode })
    if (!DRAG_RAF) {
      DRAG_RAF = requestAnimationFrame(() => {
        DRAG_RAF = 0
        redrawConnections({ ...root, positions: livePositions })
      })
    }
  }
  const onUp = () => {
    doc.removeEventListener('mousemove', onMove)
    doc.removeEventListener('mouseup', onUp)
    if (DRAG_RAF) { cancelAnimationFrame(DRAG_RAF); DRAG_RAF = 0 }
    el.__didDrag = moved

    if (!isGroupDrag && moved) {
      // Use livePositions (the positions we tracked locally during drag)
      // rather than root.positions (which was not updated during drag).
      const anchorPos = livePositions[id]
      if (anchorPos) {
        const t = detectDropTarget(id, anchorPos, { ...root, positions: livePositions })
        const targetNodeWs = t.nodeGroupId
        const dropWs = targetNodeWs || t.groupId
        const newMap = { ...(root.groupMap || {}) }
        const groupsToRestack = new Set()
        let newWs = null

        if (dropWs && dropWs !== originalWs) {
          newMap[id] = dropWs
          groupsToRestack.add(dropWs)
        } else if (t.nodeId && !targetNodeWs) {
          newWs = nextGroupName(root)
          newMap[id] = newWs
          newMap[t.nodeId] = newWs
          groupsToRestack.add(newWs)
        } else if (!t.nodeId && !t.groupId && originalWs) {
          delete newMap[id]
        }

        if (originalWs && newMap[id] !== originalWs)
          groupsToRestack.add(originalWs)
        if (dropWs && dropWs === originalWs && t.nodeId)
          groupsToRestack.add(originalWs)

        let nextPositions = livePositions
        for (const ws of groupsToRestack) {
          const snapshot = {
            ...root,
            groupMap: newMap,
            positions: nextPositions
          }
          nextPositions = restackGroup(ws, snapshot).positions
        }
        // Single root.update() with final positions — one reactive cascade
        // instead of one per mousemove event.
        root.update({ positions: nextPositions, groupMap: newMap })
      } else if (moved) {
        // No drop-target logic needed but positions still changed — commit.
        root.update({ positions: livePositions })
      }
    } else if (moved) {
      // Group drag: commit live positions to root in one shot.
      root.update({ positions: livePositions })
    }

    if (moved) {
      const integrations = root.integrations || []
      const installables = root._installables || []
      const intIds = groupIds.filter((id) =>
        integrations.some((i) => i.id === id)
      )
      const mcpIds = groupIds.filter((id) =>
        installables.some((i) => i.id === id && i.domain === 'mcp')
      )
      const projectIds = groupIds.filter(
        (id) =>
          !integrations.some((i) => i.id === id) &&
          !installables.some((i) => i.id === id)
      )
      if (projectIds.length) persistPositions(root, projectIds)
      if (intIds.length) persistIntegrationPositions(root, intIds)
      if (mcpIds.length) persistInstallableMCPPosition(root, mcpIds)
    }

    root.update({ draggingId: null, hoverGroup: null, hoverNode: null })
    redrawConnections()

    if (!isGroupDrag) {
      for (const gid of groupIds) ACTIVE_DRAG_IDS.delete(gid)
    }
    setTimeout(() => {
      el.__didDrag = false
    }, 0)
  }
  doc.addEventListener('mousemove', onMove)
  doc.addEventListener('mouseup', onUp)
}

function startLink(e, el, root, fromId, opts = {}) {
  if (e.button !== 0) return
  e.stopPropagation()
  e.preventDefault()
  const doc = el.context.document
  const board = BOARD_EL
  if (!board) return

  const toBoardCoords = (ev) => {
    const r = board.getBoundingClientRect()
    const scale = currentScale()
    return {
      toX: (ev.clientX - r.left) / scale,
      toY: (ev.clientY - r.top) / scale
    }
  }
  root.update({ linking: { fromId, toId: null, ...toBoardCoords(e) } })
  redrawConnections()

  const onMove = (ev) => {
    const toId = nodeIdAtPoint(doc, ev.clientX, ev.clientY, fromId)
    root.update({ linking: { fromId, toId, ...toBoardCoords(ev) } })
    redrawConnections()
  }
  const onUp = (ev) => {
    doc.removeEventListener('mousemove', onMove)
    doc.removeEventListener('mouseup', onUp)
    const toId = nodeIdAtPoint(doc, ev.clientX, ev.clientY, fromId)
    if (toId) {
      const exists = (root.connections || []).some(
        (c) => c.from === fromId && c.to === toId
      )
      if (!exists) {
        const integrations = root.integrations || []
        const isIntegration =
          opts.kind === 'integration' ||
          integrations.some((i) => i.id === fromId || i.id === toId)
        const conn = {
          id: fromId + '→' + toId + ':' + Date.now(),
          from: fromId,
          to: toId
        }
        if (isIntegration) conn.kind = 'integration'
        root.update({ connections: [...(root.connections || []), conn] })
        if (isIntegration) {
          // Reassign the integration's scope: a drag from an integration
          // to a project card means "this integration is now scoped to
          // that project". Works in both directions.
          const intSide = integrations.find((i) => i.id === fromId)
            ? { intId: fromId, projectId: toId }
            : { intId: toId, projectId: fromId }
          persistIntegrationScope(
            root,
            intSide.intId,
            'project',
            intSide.projectId
          )
        } else {
          persistSharedLibraries(root, [toId])
        }
      }
    }
    root.update({ linking: null })
    redrawConnections()
  }
  doc.addEventListener('mousemove', onMove)
  doc.addEventListener('mouseup', onUp)
}

let LOAD_TOKEN = 0
const LOAD_IN_FLIGHT = new Map()
// Stale-load guard: a slow doLoadNodes finishing after the user has
// switched workspaces would clobber the new workspace's state with the
// old slug's nodes. Final commit compares `slug === LOAD_ACTIVE_SLUG`.
let LOAD_ACTIVE_SLUG = null

// Shared project-list cache — one `/projects?limit=1000` is heavy and
// filtering runs client-side per owner slug. Reuse across org switches
// and across dashboard ↔ workspace navigation; persist to sessionStorage
// so a reload still reuses the previous fetch (avoids HTTP 429).
const PROJECT_LIST_TTL_MS = 5 * 60_000
const PROJECT_LIST_KEY = '__canvas_projects_v1'
let PROJECT_LIST_PROMISE = null

function readCacheEntry() {
  try {
    const raw = sessionStorage.getItem(PROJECT_LIST_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry?.at || !Array.isArray(entry.data)) return null
    return entry
  } catch {
    return null
  }
}

function writeCache(list) {
  try {
    sessionStorage.setItem(
      PROJECT_LIST_KEY,
      JSON.stringify({ at: Date.now(), data: list })
    )
  } catch {}
}

// Patch a single project's fields inside the sessionStorage cache. Keeps
// the cache in sync with server-side mutations (canvasPosition,
// useLibraries, etc.) so a reload within the 5min TTL still reflects the
// latest state instead of reverting to the stale snapshot.
function patchCachedProject(projectDbId, patch) {
  const entry = readCacheEntry()
  if (!entry?.data) return
  let changed = false
  const next = entry.data.map((p) => {
    if (p?._id !== projectDbId && p?.id !== projectDbId) return p
    changed = true
    return { ...p, ...patch }
  })
  if (!changed) return
  try {
    sessionStorage.setItem(
      PROJECT_LIST_KEY,
      JSON.stringify({ at: entry.at, data: next })
    )
  } catch {}
}

// Optimistically insert a freshly-created project at the head of the
// __canvas_projects_v1 cache so the workspace board and dashboard sidebar
// reflect the new project immediately — without waiting for the 5-min
// TTL to expire or a manual session refresh.
//
// `rawProject` is the server response shape (as returned by
// `sdk.createOrgProject`) — the same raw objects stored by `writeCache`.
// We also reset PROJECT_LIST_PROMISE so the next background refetch
// doesn't skip the live fetch due to an in-flight promise.
//
// A `storage` event is fired so other open tabs (same origin) can pick
// up the change without a full reload.
export function prependProjectToCache(rawProject) {
  if (!rawProject) return
  try {
    const entry = readCacheEntry()
    const existing = entry?.data || []
    // De-dup: if a project with the same _id already landed (e.g. from a
    // concurrent background refetch) don't add a second copy.
    const id = rawProject._id || rawProject.id
    if (id && existing.some((p) => (p?._id || p?.id) === id)) return
    const next = [rawProject, ...existing]
    const serialised = JSON.stringify({ at: Date.now(), data: next })
    sessionStorage.setItem(PROJECT_LIST_KEY, serialised)
    // Reset in-flight promise so the next getAllProjectsCached() call
    // triggers a fresh network fetch instead of resolving a stale promise.
    PROJECT_LIST_PROMISE = null
    // Cross-tab broadcast — other tabs listening to the `storage` event
    // will re-read the cache and can update their own in-memory state.
    try {
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: PROJECT_LIST_KEY,
          newValue: serialised,
          storageArea: sessionStorage
        })
      )
    } catch {}
  } catch {}
}

// Optimistically insert a freshly-created organisation at the head of the
// __canvas_orgs_v1 cache so workspace-picker and sidebar org counts update
// without a session refresh (AC5 — new-org creation path).
export function appendOrgToCache(rawOrg) {
  if (!rawOrg) return
  try {
    const entry = readOrgCache()
    const existing = entry?.data || []
    const id = rawOrg._id || rawOrg.id
    if (id && existing.some((o) => (o?._id || o?.id) === id)) return
    const next = [rawOrg, ...existing]
    sessionStorage.setItem(
      ORG_CACHE_KEY,
      JSON.stringify({ at: Date.now(), data: next })
    )
    // Also warm the in-memory slug→id map so orgIdForSlug resolves
    // the new org immediately without a network round-trip.
    if (rawOrg.slug) ORG_ID_BY_SLUG.set(rawOrg.slug, id)
    try {
      const serialised = JSON.stringify({ at: Date.now(), data: next })
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: ORG_CACHE_KEY,
          newValue: serialised,
          storageArea: sessionStorage
        })
      )
    } catch {}
  } catch {}
}

export async function getAllProjectsCached() {
  const entry = readCacheEntry()
  const fresh = entry && Date.now() - entry.at < PROJECT_LIST_TTL_MS
  if (fresh) return entry.data
  if (PROJECT_LIST_PROMISE) return PROJECT_LIST_PROMISE
  const sdk = getSDK()
  if (!sdk.isReady?.()) await sdk.initialize({})
  PROJECT_LIST_PROMISE = sdk
    .getProjects({ limit: 1000 })
    .then((r) => {
      const list = r?.data || r || []
      writeCache(list)
      return list
    })
    .catch((err) => {
      // On 429 (or any error), keep whatever we have in sessionStorage —
      // stale data beats an empty board. Propagate the error only when
      // we have nothing cached at all.
      if (entry?.data) return entry.data
      throw err
    })
    .finally(() => {
      PROJECT_LIST_PROMISE = null
    })
  return PROJECT_LIST_PROMISE
}

async function doLoadNodes(root, slug) {
  const myToken = ++LOAD_TOKEN
  root.update({ loading: true, error: null })
  try {
    const list = await getAllProjectsCached()
    const localPositions = readAllLocalPositions()
    const ownProjects = list
      .filter((p) => ownerMatches(p, slug))
      .map((p) => normalizeProject(p, localPositions))
    // Pull in any project referenced as a library by nodes in this workspace
    // so incoming arrows from libraries outside the workspace (e.g. `brand`,
    // `shared`) render on load. Match by project `_id` (from useLibraries)
    // and falling back to bare key (legacy sharedLibraries).
    const referencedIds = new Set()
    const referencedKeys = new Set()
    ownProjects.forEach((n) => {
      ;(n.useLibraries || []).forEach((id) => referencedIds.add(id))
      ;(n.legacySharedLibs || []).forEach((k) => referencedKeys.add(k))
    })
    const ownIds = new Set(ownProjects.map((n) => n.id))
    // Narrow to referenced-library candidates BEFORE normalizing — the
    // full list (~150 projects) gets mapped otherwise, and most results
    // are thrown away by the filter downstream.
    const libraryNodes = list
      .filter((p) => (referencedIds.has(p?._id) || referencedKeys.has(projectKey(p))) &&
        !ownerMatches(p, slug))
      .map((p) => normalizeProject(p, localPositions))
      .filter((n) => !ownIds.has(n.id))
    const nodes = [...ownProjects, ...libraryNodes]
    if (myToken !== LOAD_TOKEN) return
    const orgId = await orgIdForSlug(slug)
    const units = await loadIntegrationUnitsSafe(orgId)
    if (myToken !== LOAD_TOKEN) return
    let integrations = flattenIntegrationUnits(units, nodes, orgId)
    // Dev-only: `?demo=project-int` synthesises a project-scope record for
    // the first own project so on-canvas rendering + linking can be
    // verified against real UI code before any real row is migrated from
    // org→project scope.
    if (window.location.search.includes('demo=project-int') && ownProjects[0]) {
      const host = ownProjects[0]
      integrations = [
        ...integrations,
        {
          id: 'int:project:custom_http:demo',
          kind: 'custom_http',
          category: 'custom',
          label: 'Webhook (demo)',
          kindLabel: 'Custom HTTP',
          scope: 'project',
          scopeId: host._id,
          projectId: host.id,
          capabilities: ['http.request'],
          enabled: true
        }
      ]
    }
    // Phase 5: load ALL workspace-scoped installables (integrations, MCP, …)
    // concurrently with the existing integration-unit load to avoid extra RTTs.
    const installablesPromise = loadWorkspaceInstallablesSafe(orgId)

    const positions = layoutPositions(nodes)
    // Canvas shows every configured integration so the full landscape at
    // /admin/integrations is visible. Saved `config.canvasPosition` wins;
    // otherwise:
    //   project → anchored below its owning project card
    //   workspace → auto-row just above the project grid
    //   org → auto-row above the workspace row (top-most infrastructure)
    let orgRowX = 24
    const orgRowY = -(INTEGRATION_NODE_H + 24) * 2 - 24
    let wsRowX = 24
    const wsRowY = -INTEGRATION_NODE_H - 48
    integrations.forEach((it) => {
      const saved = it.config?.canvasPosition
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
        positions[it.id] = {
          x: +saved.x,
          y: +saved.y,
          w: INTEGRATION_NODE_W,
          h: INTEGRATION_NODE_H
        }
        return
      }
      if (it.scope === 'project' && it.projectId) {
        const anchor = positions[it.projectId]
        if (!anchor) return
        positions[it.id] = {
          x: anchor.x,
          y: anchor.y + NODE_H + 24,
          w: INTEGRATION_NODE_W,
          h: INTEGRATION_NODE_H
        }
        return
      }
      if (it.scope === 'workspace') {
        positions[it.id] = {
          x: wsRowX,
          y: wsRowY,
          w: INTEGRATION_NODE_W,
          h: INTEGRATION_NODE_H
        }
        wsRowX += INTEGRATION_NODE_W + 24
        return
      }
      if (it.scope === 'org') {
        positions[it.id] = {
          x: orgRowX,
          y: orgRowY,
          w: INTEGRATION_NODE_W,
          h: INTEGRATION_NODE_H
        }
        orgRowX += INTEGRATION_NODE_W + 24
      }
    })

    // Await the Phase 5 installables load and seed canvas positions.
    // We use a row that sits between the project grid and the integration rows.
    const allInstallables = await installablesPromise
    if (myToken !== LOAD_TOKEN) return // stale guard — don't clobber a newer load
    let instTileX = 24
    const instTileY = -INSTALLABLE_H - 120 // above the integration rows
    let mcpTileX = 24
    const mcpTileY = instTileY - INSTALLABLE_H - 28
    allInstallables.forEach((it) => {
      if (positions[it.id]) return // already placed (integration positions seeded above)
      const savedPos =
        it.config?.canvasPosition ||
        it._raw?.metadata?.canvasPosition ||
        null
      if (savedPos && Number.isFinite(savedPos.x) && Number.isFinite(savedPos.y)) {
        positions[it.id] = { x: +savedPos.x, y: +savedPos.y, w: INSTALLABLE_W, h: INSTALLABLE_H }
        return
      }
      if (it.domain === 'mcp') {
        positions[it.id] = { x: mcpTileX, y: mcpTileY, w: INSTALLABLE_W, h: INSTALLABLE_H }
        mcpTileX += INSTALLABLE_W + 24
      } else {
        positions[it.id] = { x: instTileX, y: instTileY, w: INSTALLABLE_W, h: INSTALLABLE_H }
        instTileX += INSTALLABLE_W + 24
      }
    })

    if (slug !== LOAD_ACTIVE_SLUG) return // stale load — user switched
    root.update({
      slug,
      orgId: orgId || null,
      nodes,
      integrations,
      _installables: allInstallables,
      positions,
      connections: initialConnections(nodes, integrations),
      groupMap: {},
      groupNames: {},
      hoverGroup: null,
      hoverNode: null,
      draggingId: null,
      linking: null,
      loading: false,
      error: null,
      // Sentinel: loadNodes has completed at least once for this workspace.
      // LabEmptyTutorial reads this so it doesn't flash during the initial
      // mount race (root.loading flips false before nodes hydrate).
      _labLoadedAt: Date.now()
    })
    redrawConnections()
    if (PZ) {
      try {
        PZ.zoomAbs(0, 0, 0.9)
        PZ.moveTo(120, 120)
      } catch {}
    }

    // Background position sync. Reads authoritative canvasPositions from
    // the server for each own-workspace project via `getProjectData`. Any
    // that differ from the current render update `root.positions` and
    // the local snapshot. This is the multi-user "catch up" path — User
    // A moves a card in tab A, User B reloads tab B, background sync
    // pulls A's position into B. Own-only to cap fan-out.
    const ownDbIds = ownProjects.map((n) => n._id).filter(Boolean)
    if (ownDbIds.length) {
      fetchServerPositions(ownDbIds, (projectDbId, serverPos) => {
        if (slug !== LOAD_ACTIVE_SLUG) return
        const nodesNow = root.nodes || []
        const match = nodesNow.find((n) => n._id === projectDbId)
        if (!match) return
        const curPos = root.positions?.[match.id]
        if (curPos && curPos.x === serverPos.x && curPos.y === serverPos.y)
          return
        root.update({
          positions: {
            ...(root.positions || {}),
            [match.id]: { x: serverPos.x, y: serverPos.y, w: NODE_W, h: NODE_H }
          }
        })
        redrawConnections()
      }).catch(() => {})
    }
  } catch (err) {
    // Stale-load guard: if the user already switched away from this
    // slug, an error from the abandoned fetch must not clobber the
    // current workspace's nodes (that's what "items disappearing on
    // switch" bug reports trace back to).
    if (slug !== LOAD_ACTIVE_SLUG) return
    root.update({
      loading: false,
      error: err?.message || 'Failed to load workspace'
    })
  }
}

export async function loadNodes(root, slugOverride) {
  const slug = slugOverride || root?.activeWorkspace || ownerSlugFromUrl()
  if (!slug) {
    root.update({ loading: false, error: 'Missing owner slug', nodes: [] })
    return
  }
  LOAD_ACTIVE_SLUG = slug
  // De-dup concurrent calls for the same slug — dashboard onClick and the
  // embedded workspace's onUpdate both react to `activeWorkspace` changes.
  const existing = LOAD_IN_FLIGHT.get(slug)
  if (existing) return existing
  const p = doLoadNodes(root, slug).finally(() => {
    if (LOAD_IN_FLIGHT.get(slug) === p) LOAD_IN_FLIGHT.delete(slug)
  })
  LOAD_IN_FLIGHT.set(slug, p)
  return p
}

export const ProjectNode = {
  position: 'absolute',
  // Libraries use a stadium/pill silhouette to stand apart from app/mobile/tv
  // tiles which keep the standard rounded-rect look.
  round: (el, s) => (s.projectType === 'library' ? 'C' : 'Z2'),
  theme: 'canvas-card-glass',
  border: (el, s) => (
    s.projectType === 'library'
      ? '1px dashed rgba(180,140,255,0.45)'
      : '1px solid rgba(255,255,255,0.08)'
  ),
  flow: 'column',
  textDecoration: 'none',
  color: 'inherit',
  userSelect: 'none',
  cursor: 'grab',
  transition: 'B transform defaultBezier, B box-shadow defaultBezier',
  width: (el, s) => dimsFor(s.projectType).w + 'px',
  height: (el, s) => dimsFor(s.projectType).h + 'px',
  left: (el, s) => (s.root?.positions?.[s.id]?.x ?? 0) + 'px',
  top: (el, s) => (s.root?.positions?.[s.id]?.y ?? 0) + 'px',
  zIndex: (el, s) => (s.root?.draggingId === s.id ? 1000 : 1),
  boxShadow: (el, s) => {
    if (s.root?.hoverNode === s.id) {
      // iOS-style stacked-card preview: concentric ghost cards behind the
      // hovered target suggest a new workspace is about to form on drop.
      return [
        '0 0 0 10px rgba(20,22,27,0.92)',
        '0 0 0 11px rgba(255,255,255,0.16)',
        '0 0 0 20px rgba(20,22,27,0.82)',
        '0 0 0 21px rgba(255,255,255,0.1)',
        '0 26px 56px rgba(0,0,0,0.5)'
      ].join(', ')
    }
    return '0 1px 2px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.28)'
  },
  ':hover': { transform: 'translateY(-2px)' },
  ':hover > [data-link-handle]': { opacity: '1', transform: 'scale(1)' },
  attr: { 'data-node-id': (el, s) => s.id },

  onRender: (el, s) => {
    // onInit fires before el.node is created (v3.14 lifecycle order: Init →
    // cacheNode → Render). Use onRender so el.node is guaranteed to exist.
    if (s?.id && el.node) DRAG_DOM_NODES.set(s.id, el.node)
  },
  onRemove: (el, s) => {
    if (s?.id) DRAG_DOM_NODES.delete(s.id)
  },
  onMousedown: (e, el, s) => startDrag(e, el, s.root, s.id),
  onClick: (e, el, s) => {
    if (el.__didDrag) return
    if (!s.owner || !s.key) return
    const urls = buildProjectUrls(s.owner, s.key)
    if (urls) el.router(`/${urls.safeOwner}/${urls.safeKey}`, el.getRoot())
  },

  Thumbnail: {
    position: 'relative',
    width: '100%',
    // Thumbnail occupies the upper portion of the tile; body holds title/path.
    // Mobile (portrait) gives more vertical space to the screen frame; library
    // is a chip-style strip with a thinner thumb.
    height: (el, s) => {
      const t = s.projectType
      if (t === 'mobile') return '232px'
      if (t === 'library') return '96px'
      if (t === 'tv') return '156px'
      return '160px'
    },
    flexShrink: '0',
    overflow: 'hidden',
    borderTopLeftRadius: 'inherit',
    borderTopRightRadius: 'inherit',
    // Gradient always paints first. Explicit icon paints over it via
    // `background`; screenshot-service URL is rendered as a sibling
    // <img> so a 404 leaves the gradient + letter placeholder visible.
    background: (el, s) =>
      s.icon
        ? `center / cover no-repeat url("${s.icon}"), linear-gradient(135deg,#2a2f38 0%,#1a1d24 55%,#0f1116 100%)`
        : 'linear-gradient(135deg,#2a2f38 0%,#1a1d24 55%,#0f1116 100%)',
    pointerEvents: 'none',

    // Screenshot / thumbnail — renders below the frame chrome so the
    // chrome overlay (browser bar, bezel, etc.) paints on top of the
    // screenshot image. mountThumbnailCascade appends an <img> with
    // position:absolute;inset:0 so it fills this container.
    ScreenshotStack: {
      if: (el, s) => !s.icon && !!s.owner && !!s.key,
      tag: 'div',
      position: 'absolute',
      inset: '0',
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: '1',
      onRender: (el, s) => mountThumbnailCascade(el, s)
    },

    // Type-aware visual frame — renders on top of screenshot/gradient.
    // Each projectType gets a distinct chrome (AppFrame, DeckFrame, etc.).
    // The frame components handle their own empty-content state glyphs.
    TypeFrame: {
      extends: 'ProjectTileFrame',
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '2'
    }
  },

  Body: {
    padding: 'Y2 A Y2',
    flow: 'column',
    gap: 'V',
    flexGrow: '1',
    justify: 'center',
    pointerEvents: 'none',
    borderTop: '1px solid rgba(255,255,255,0.05)',

    Title: {
      tag: 'h3',
      text: (el, s) => s.name,
      fontSize: 'A',
      fontWeight: '600',
      color: 'white',
      lineHeight: '1.2',
      letterSpacing: '-0.01em'
    },
    Path: {
      tag: 'span',
      text: (el, s) => s.id,
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.42)',
      fontFamily: 'monospace',
      letterSpacing: '-0.02em'
    }
  },

  LinkHandle: {
    attr: { 'data-link-handle': '' },
    position: 'absolute',
    right: '-10px',
    top: '50%',
    marginTop: '-10px',
    width: '20px',
    height: '20px',
    round: '50%',
    background: 'rgba(255,255,255,0.85)',
    border: '2px solid #14161b',
    cursor: 'crosshair',
    pointerEvents: 'auto',
    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
    opacity: '0',
    transform: 'scale(0.6)',
    transition: 'A opacity defaultBezier, A transform defaultBezier',
    ':hover': { transform: 'scale(1.3)' },
    onMousedown: (e, el, s) => startLink(e, el, s.root, s.id)
  },

  MenuButton: {
    tag: 'button',
    type: 'button',
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '24px',
    height: '24px',
    round: '50%',
    padding: '0',
    border: '0',
    background: 'rgba(0,0,0,0.55)',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: '1',
    transition: 'A opacity defaultBezier',
    boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
    zIndex: '5',
    ':hover': { background: 'rgba(0,0,0,0.8)' },
    'aria-label': 'More options',
    text: '⋯',
    onPointerdown: (e) => e.stopPropagation(),
    onMousedown: (e) => e.stopPropagation(),
    onMouseup: (e) => e.stopPropagation(),
    onClick: (e, el, s) => {
      e.stopPropagation()
      e.preventDefault()
      const root = s.root
      const isOpen = root.openMenuId === s.id
      // No openMenuRect — Menu computes position LIVE from the tile's
      // board-coords + panzoom transform so it follows on pan/zoom.
      // _ctxMenuTick gives the menu a reactive signal to re-evaluate
      // its top/left when panzoom fires 'transform'.
      root.update({
        openMenuId: isOpen ? null : s.id,
        _ctxMenuTick: Date.now()
      })
    }
  },

  Menu: {
    display: (el, s) => (s.root?.openMenuId === s.id ? 'flex' : 'none'),
    // Portal: position relative to viewport, then re-parent the rendered DOM to
    // document.body on first render so panzoom's transform on `Board` does NOT
    // scale or translate this menu. Menu logic and reactive lookups continue
    // to work because DOMQL holds the element ref by JS, not by DOM ancestry.
    //
    // Position is computed LIVE from the tile's current screen position
    // (board coords transformed by panzoom), so the menu follows when the
    // user pans or zooms. The transform-tick subscription in the studios
    // page's onRender re-evaluates these factories on every pz transform.
    position: 'fixed',
    top: (el, s) => {
      void s.root?._ctxMenuTick // reactive subscribe
      const pos = computeMenuScreenPos(s.root, s.id)
      if (!pos) return '0px'
      const MENU_W = 232
      const MENU_MAX_H = Math.min(480, window.innerHeight - 24)
      const want = pos.tileBottom + 6
      const overflowY = want + MENU_MAX_H - window.innerHeight + 12
      return (overflowY > 0 ? Math.max(12, want - overflowY) : want) + 'px'
    },
    left: (el, s) => {
      void s.root?._ctxMenuTick // reactive subscribe
      const pos = computeMenuScreenPos(s.root, s.id)
      if (!pos) return '0px'
      const MENU_W = 232
      const want = pos.tileRight - 220
      return Math.max(12, Math.min(want, window.innerWidth - MENU_W)) + 'px'
    },
    minWidth: '220px',
    maxWidth: '232px',
    maxHeight: 'min(60vh, calc(100vh - 24px))',
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: 'V 0',
    round: '10px',
    background: 'rgba(24,27,33,0.98)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 10px 28px rgba(0,0,0,0.5)',
    zIndex: '10000',
    flow: 'column',
    pointerEvents: 'auto',
    attr: { 'data-project-menu': '' },
    onRender: (el) => {
      if (el.__portaled || !el.node) return
      el.__portaled = true
      try { document.body.appendChild(el.node) } catch (_) { /* noop */ }
    },
    onRemove: (el) => {
      if (el.node && el.node.parentNode === document.body) {
        try { document.body.removeChild(el.node) } catch (_) { /* noop */ }
      }
    },
    onPointerdown: (e) => e.stopPropagation(),
    onMousedown: (e) => e.stopPropagation(),
    onClick: (e) => e.stopPropagation(),
    onKeydown: (e) => { if (e.key === 'Escape') e.currentTarget.closest('[data-project-node]')?.querySelector('button')?.focus() },
    'aria-label': 'Project options',
    role: 'menu',

    // Child items default — buttons styled consistently.
    // Section headings and dividers override `tag` / role as needed.
    childExtends: 'Flex',
    childProps: {
      tag: 'button',
      type: 'button',
      role: 'menuitem',
      padding: 'Z X2',
      round: '0',
      background: 'transparent',
      border: '0',
      color: 'white',
      textAlign: 'left',
      fontSize: 'Z2',
      cursor: 'pointer',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'Y',
      ':hover': { background: 'rgba(255,255,255,0.06)' },
      ':focus-visible': {
        outline: '2px solid rgba(100,160,255,0.7)',
        outlineOffset: '-2px'
      }
    },

    // ─── Section: Open ───────────────────────────────────────────────────
    SectionLabelOpen: {
      tag: 'div',
      role: 'presentation',
      text: 'Open',
      padding: 'V X2 W',
      fontSize: 'Z1',
      color: 'rgba(255,255,255,0.3)',
      fontWeight: '600',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      cursor: 'default',
      background: 'transparent',
      border: '0',
      ':hover': { background: 'transparent' }
    },

    OpenCanvasItem: {
      'aria-label': 'Open in App — equivalent to: smbls open',
      attr: { title: 'Equivalent to: smbls open' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ openMenuId: null })
        const urls = buildProjectUrls(s.owner, s.key)
        if (urls) el.router(`/${urls.safeOwner}/${urls.safeKey}`, el.getRoot())
      },
      Label: { tag: 'span', text: 'Open in App' },
      Hint: { tag: 'span', text: '↵', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },
    OpenCloudCanvasItem: {
      'aria-label': 'Open Cloud Canvas',
      attr: { title: 'Opens the cloud editor for this project' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ openMenuId: null })
        const urls = buildProjectUrls(s.owner, s.key, s.defaultEnv || 'development')
        if (urls) window.open(urls.canvas, '_blank', 'noopener,noreferrer')
      },
      Label: { tag: 'span', text: 'Open Cloud Canvas' },
      Hint: { tag: 'span', text: '↗', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },
    OpenPreviewItem: {
      'aria-label': 'Open Preview',
      attr: { title: 'Opens the preview URL for this project' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ openMenuId: null })
        const urls = buildProjectUrls(s.owner, s.key, s.defaultEnv || 'development')
        if (urls) window.open(urls.preview, '_blank', 'noopener,noreferrer')
      },
      Label: { tag: 'span', text: 'Open Preview' },
      Hint: { tag: 'span', text: '↗', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },
    DirectDevItem: {
      'aria-label': 'Direct (dev)',
      attr: { title: 'Opens the dev subdomain URL' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ openMenuId: null })
        const urls = buildProjectUrls(s.owner, s.key)
        if (urls) window.open(urls.directDev, '_blank', 'noopener,noreferrer')
      },
      Label: { tag: 'span', text: 'Direct (dev)' },
      Hint: { tag: 'span', text: '↗', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },
    DirectProdItem: {
      'aria-label': 'Direct (prod)',
      attr: { title: 'Opens the production subdomain URL' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ openMenuId: null })
        const urls = buildProjectUrls(s.owner, s.key)
        if (urls) window.open(urls.directProd, '_blank', 'noopener,noreferrer')
      },
      Label: { tag: 'span', text: 'Direct (prod)' },
      Hint: { tag: 'span', text: '↗', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },
    CopyUrlItem: {
      'aria-label': 'Copy URL',
      attr: { title: 'Copy the canonical project URL to clipboard — equivalent to: smbls project link' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ openMenuId: null })
        const urls = buildProjectUrls(s.owner, s.key)
        if (urls && navigator.clipboard) {
          navigator.clipboard.writeText(
            `https://symbols.app/${urls.safeOwner}/${urls.safeKey}`
          ).catch(() => {})
        }
      },
      Label: { tag: 'span', text: 'Copy URL' },
      Hint: { tag: 'span', text: '⌘⇧C', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },

    // ─── Section: Manage ─────────────────────────────────────────────────
    DividerManage: {
      tag: 'div',
      role: 'separator',
      'aria-orientation': 'horizontal',
      height: '1px',
      background: 'rgba(255,255,255,0.07)',
      margin: 'V 0',
      cursor: 'default',
      border: '0',
      ':hover': { background: 'rgba(255,255,255,0.07)' }
    },
    SectionLabelManage: {
      tag: 'div',
      role: 'presentation',
      text: 'Manage',
      padding: 'V X2 W',
      fontSize: 'Z1',
      color: 'rgba(255,255,255,0.3)',
      fontWeight: '600',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      cursor: 'default',
      background: 'transparent',
      border: '0',
      ':hover': { background: 'transparent' }
    },

    SettingsItem: {
      'aria-label': 'Settings — equivalent to: smbls config',
      attr: { title: 'Equivalent to: smbls config' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          openMenuId: null,
          settingsOpen: true,
          settingsTab: s.root?.settingsTab || 'workspace'
        })
      },
      Label: { tag: 'span', text: 'Settings…' },
      Hint: { tag: 'span', text: '⌘,', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },
    EditMetadataItem: {
      'aria-label': 'Edit metadata — equivalent to: smbls project update',
      attr: { title: 'Equivalent to: smbls project update' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          openMenuId: null,
          projectEditMetadataModal: { id: s.id, name: s.name || '', key: s.key || '', owner: s.owner || '' }
        })
      },
      Label: { tag: 'span', text: 'Edit metadata…' }
    },
    PublishItem: {
      'aria-label': 'Publish — equivalent to: smbls push',
      attr: { title: 'Equivalent to: smbls push' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ openMenuId: null })
        const sdk = getSDK()
        if (typeof sdk?.push === 'function') {
          sdk.push(s._id || s.id).catch(() => {})
        }
      },
      Label: { tag: 'span', text: 'Publish' },
      Hint: { tag: 'span', text: '⌘P', fontSize: 'Z1', color: 'rgba(255,255,255,0.3)', flexShrink: '0' }
    },
    DuplicateItem: {
      'aria-label': 'Duplicate — equivalent to: smbls project duplicate',
      attr: { title: 'Equivalent to: smbls project duplicate' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          openMenuId: null,
          projectDuplicateModal: { sourceId: s._id || s.id, sourceName: s.name || s.key || '' }
        })
      },
      Label: { tag: 'span', text: 'Duplicate…' }
    },

    // ─── Section: Integrations ────────────────────────────────────────────
    DividerIntegrations: {
      tag: 'div',
      role: 'separator',
      'aria-orientation': 'horizontal',
      height: '1px',
      background: 'rgba(255,255,255,0.07)',
      margin: 'V 0',
      cursor: 'default',
      border: '0',
      ':hover': { background: 'rgba(255,255,255,0.07)' }
    },
    SectionLabelIntegrations: {
      tag: 'div',
      role: 'presentation',
      text: 'Integrations',
      padding: 'V X2 W',
      fontSize: 'Z1',
      color: 'rgba(255,255,255,0.3)',
      fontWeight: '600',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      cursor: 'default',
      background: 'transparent',
      border: '0',
      ':hover': { background: 'transparent' }
    },

    AddLibraryItem: {
      'aria-label': 'Import Library — link a shared component library',
      attr: { title: 'Link a shared library to this project' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          openMenuId: null,
          libraryPickerFor: s.id,
          libraryPickerQuery: ''
        })
      },
      Label: { tag: 'span', text: 'Import Library…' }
    },

    // ─── Section: Danger ─────────────────────────────────────────────────
    DividerDanger: {
      tag: 'div',
      role: 'separator',
      'aria-orientation': 'horizontal',
      height: '1px',
      background: 'rgba(255,255,255,0.07)',
      margin: 'V 0',
      cursor: 'default',
      border: '0',
      ':hover': { background: 'rgba(255,255,255,0.07)' }
    },
    SectionLabelDanger: {
      tag: 'div',
      role: 'presentation',
      text: 'Danger',
      padding: 'V X2 W',
      fontSize: 'Z1',
      color: 'rgba(255,255,255,0.3)',
      fontWeight: '600',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      cursor: 'default',
      background: 'transparent',
      border: '0',
      ':hover': { background: 'transparent' }
    },

    DeleteItem: {
      'aria-label': 'Delete project — equivalent to: smbls project delete',
      attr: { title: 'Equivalent to: smbls project delete' },
      color: 'rgb(255,90,90)',
      ':hover': { background: 'rgba(255,60,60,0.12)' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          openMenuId: null,
          projectDeleteConfirm: { id: s._id || s.id, key: s.key || '', name: s.name || s.key || '' }
        })
      },
      Label: { tag: 'span', text: 'Delete project' }
    }
  }
}

export const IntegrationNode = {
  position: 'absolute',
  round: '10px',
  flow: 'row',
  align: 'center start',
  gap: 'Z',
  padding: 'Z',
  userSelect: 'none',
  cursor: 'grab',
  width: INTEGRATION_NODE_W + 'px',
  height: INTEGRATION_NODE_H + 'px',
  background: 'rgba(20,22,27,0.85)',
  border: (el, s) => `1px solid ${integrationColor(s.category)}55`,
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  color: 'white',
  transition: 'B transform defaultBezier, B box-shadow defaultBezier',
  left: (el, s) => (s.root?.positions?.[s.id]?.x ?? 0) + 'px',
  top: (el, s) => (s.root?.positions?.[s.id]?.y ?? 0) + 'px',
  zIndex: (el, s) => (s.root?.draggingId === s.id ? 1000 : 2),
  ':hover': { transform: 'translateY(-1px)' },
  ':hover > [data-link-handle]': { opacity: '1', transform: 'scale(1)' },
  attr: { 'data-node-id': (el, s) => s.id },
  onRender: (el, s) => {
    // onInit fires before el.node is created (v3.14 lifecycle order: Init →
    // cacheNode → Render). Use onRender so el.node is guaranteed to exist.
    if (s?.id && el.node) DRAG_DOM_NODES.set(s.id, el.node)
  },
  onRemove: (el, s) => {
    if (s?.id) DRAG_DOM_NODES.delete(s.id)
  },
  onMousedown: (e, el, s) => startDrag(e, el, s.root, s.id),
  onClick: (e, el, s) => {
    if (el.__didDrag) return
    window.open(integrationAdminUrl(s.kind), '_blank', 'noopener,noreferrer')
  },

  Glyph: {
    width: '34px',
    height: '34px',
    round: '8px',
    flexShrink: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: (el, s) => (s.icon ? 'white' : integrationColor(s.category)),
    pointerEvents: 'none',
    overflow: 'hidden',
    Img: {
      hide: (el, s) => !s.icon || el.parent._imgFailed,
      tag: 'img',
      src: (el, s) => s.icon ? `https://cdn.simpleicons.org/${s.icon}` : '',
      alt: (el, s) => s.kindLabel || s.kind,
      width: '60%',
      height: '60%',
      objectFit: 'contain',
      onRender: (el) => {
        if (!el.node) return
        el.node.onerror = () => {
          if (!el.parent) return
          el.parent._imgFailed = true
          el.node.style.display = 'none'
          const letter = el.parent.Letter
          if (letter?.node) letter.node.style.display = ''
        }
      }
    },
    Letter: {
      hide: (el, s) => !!s.icon && !el.parent._imgFailed,
      tag: 'span',
      fontWeight: '700',
      fontSize: 'A',
      color: '#14161b',
      text: (el, s) => integrationGlyph(s.kind)
    }
  },

  Info: {
    flow: 'column',
    gap: '2px',
    flexGrow: '1',
    overflow: 'hidden',
    pointerEvents: 'none',
    Label: {
      tag: 'div',
      text: (el, s) => s.label || s.kindLabel || s.kind,
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'white',
      style: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    },
    Kind: {
      tag: 'div',
      text: (el, s) => s.kindLabel || s.kind,
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.55)',
      textTransform: 'uppercase',
      letterSpacing: '0.06em'
    }
  },

  LinkHandle: {
    attr: { 'data-link-handle': '' },
    position: 'absolute',
    right: '-10px',
    top: '50%',
    marginTop: '-10px',
    width: '20px',
    height: '20px',
    round: '50%',
    background: (el, s) => integrationColor(s.category),
    border: '2px solid #14161b',
    cursor: 'crosshair',
    pointerEvents: 'auto',
    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
    opacity: '0',
    transform: 'scale(0.6)',
    transition: 'A opacity defaultBezier, A transform defaultBezier',
    ':hover': { transform: 'scale(1.3)' },
    onMousedown: (e, el, s) =>
      startLink(e, el, s.root, s.id, { kind: 'integration' })
  }
}

// ─── Phase 5 canvas tile variants ────────────────────────────────────────────
// Each domain renders a compact (INSTALLABLE_W × INSTALLABLE_H) positioned
// tile on the board. All share the same drag + position mechanics via the
// common DRAG_DOM_NODES map + startDrag. Click → openInstallableModal (Phase 4
// shared function registered in workspace/packages/shared/functions/installable.js).

// Shared base style for all installable tiles. Domain variants extend this by
// key name or override specific visual properties.
const _installableNodeBase = {
  position: 'absolute',
  round: '10px',
  flow: 'column',
  gap: 'V',
  padding: 'Z',
  userSelect: 'none',
  cursor: 'grab',
  width: INSTALLABLE_W + 'px',
  height: INSTALLABLE_H + 'px',
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  color: 'white',
  transition: 'B transform defaultBezier, B box-shadow defaultBezier',
  left: (el, s) => (s.root?.positions?.[s.id]?.x ?? 0) + 'px',
  top: (el, s) => (s.root?.positions?.[s.id]?.y ?? 0) + 'px',
  zIndex: (el, s) => (s.root?.draggingId === s.id ? 1000 : 2),
  ':hover': { transform: 'translateY(-1px)' },
  attr: { 'data-node-id': (el, s) => s.id },
  onRender: (el, s) => {
    if (s?.id && el.node) DRAG_DOM_NODES.set(s.id, el.node)
  },
  onRemove: (el, s) => {
    if (s?.id) DRAG_DOM_NODES.delete(s.id)
  },
  onMousedown: (e, el, s) => startDrag(e, el, s.root, s.id),
  onClick: (e, el, s) => {
    if (el.__didDrag) return
    el.call('openInstallableModal', {
      domain: s.domain,
      kind: s.kind,
      installed: true,
      defaultScope: s.scopeType ? { type: s.scopeType, id: s.scopeId } : null
    })
  }
}

// Library tile — stack-of-cards visual using box-shadow layers.
export const LibraryNode = {
  ..._installableNodeBase,
  background: 'rgba(20,22,27,0.92)',
  border: '1px solid rgba(90,200,130,0.3)',
  // Ghost "stacked cards" behind to signal library/collection type.
  style: {
    boxShadow: [
      '0 0 0 4px rgba(20,22,27,0.72)',
      '0 0 0 5px rgba(90,200,130,0.18)',
      '0 0 0 9px rgba(20,22,27,0.52)',
      '0 0 0 10px rgba(90,200,130,0.1)',
      '0 6px 18px rgba(0,0,0,0.4)'
    ].join(', ')
  },

  Header: {
    flow: 'x',
    gap: 'V',
    align: 'center',
    pointerEvents: 'none',

    Glyph: {
      width: '28px',
      height: '28px',
      round: '6px',
      flexShrink: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(90,200,130,0.18)',
      fontSize: 'Z2',
      text: '◻'
    },

    Name: {
      tag: 'div',
      text: (el, s) => s.name || 'Library',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'white',
      style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
    }
  },

  DomainBadge: {
    tag: 'span',
    text: 'Library',
    fontSize: 'Y1',
    fontWeight: '700',
    color: 'rgba(90,200,130,0.9)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    pointerEvents: 'none'
  }
}

// MCP tile — plug icon + URL chip + status dot.
export const MCPNode = {
  ..._installableNodeBase,
  background: 'rgba(20,22,27,0.92)',
  border: '1px solid rgba(84,174,255,0.3)',

  Header: {
    flow: 'x',
    gap: 'V',
    align: 'center',
    pointerEvents: 'none',

    Glyph: {
      width: '28px',
      height: '28px',
      round: '6px',
      flexShrink: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(84,174,255,0.15)',
      fontSize: 'Z2',
      text: '⬡'
    },

    Name: {
      tag: 'div',
      text: (el, s) => s.name || 'MCP',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'white',
      style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
    }
  },

  StatusRow: {
    flow: 'x',
    gap: 'V',
    align: 'center',
    pointerEvents: 'none',

    Dot: {
      width: '8px',
      height: '8px',
      round: '50%',
      flexShrink: '0',
      background: (el, s) => s.status === 'enabled' ? 'rgba(84,174,255,0.9)' : 'rgba(255,255,255,0.25)'
    },
    StatusLabel: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: (el, s) => s.status === 'enabled' ? 'rgba(84,174,255,0.9)' : 'rgba(255,255,255,0.3)',
      text: (el, s) => s.status === 'enabled' ? 'Live' : 'Disabled'
    }
  }
}

// Plugin tile — puzzle-piece visual.
export const PluginNode = {
  ..._installableNodeBase,
  background: 'rgba(20,22,27,0.92)',
  border: '1px solid rgba(184,140,255,0.3)',

  Header: {
    flow: 'x',
    gap: 'V',
    align: 'center',
    pointerEvents: 'none',

    Glyph: {
      width: '28px',
      height: '28px',
      round: '6px',
      flexShrink: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(184,140,255,0.15)',
      fontSize: 'Z2',
      text: '⬦'
    },

    Name: {
      tag: 'div',
      text: (el, s) => s.name || 'Plugin',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'white',
      style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
    }
  },

  DomainBadge: {
    tag: 'span',
    text: 'Plugin',
    fontSize: 'Y1',
    fontWeight: '700',
    color: 'rgba(184,140,255,0.9)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    pointerEvents: 'none'
  }
}

// Generic installable canvas tile — branches on `domain` to pick the correct
// domain-specific frame. Registered as a named export so `childExtends` can
// reference it by key from InstallableNodes. Children map domain → component.
export const InstallableNode = {
  ..._installableNodeBase,
  // Delegate all rendering to the domain-specific sub-element. The containing
  // element is just the drag target; it carries no content itself.
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: '0',

  // Domain-specific frame rendered inside — auto-extends by matching key.
  // DOMQL v3.14 resolves 'LibraryNode', 'MCPNode', 'PluginNode', 'IntegrationNode'
  // as registered exports. We render only one at a time via `if`.
  LibraryFrame: {
    extends: 'LibraryNode',
    if: (el, s) => s.domain === 'library',
    // Inherit all state from parent.
    position: 'relative',
    left: '0',
    top: '0',
    zIndex: 'auto',
    width: '100%',
    height: '100%',
    // Disable nested drag — parent handles it.
    onMousedown: null,
    onClick: null
  },

  MCPFrame: {
    extends: 'MCPNode',
    if: (el, s) => s.domain === 'mcp',
    position: 'relative',
    left: '0',
    top: '0',
    zIndex: 'auto',
    width: '100%',
    height: '100%',
    onMousedown: null,
    onClick: null
  },

  PluginFrame: {
    extends: 'PluginNode',
    if: (el, s) => s.domain === 'plugin',
    position: 'relative',
    left: '0',
    top: '0',
    zIndex: 'auto',
    width: '100%',
    height: '100%',
    onMousedown: null,
    onClick: null
  },

  IntegrationFrame: {
    extends: 'IntegrationNode',
    if: (el, s) => s.domain === 'integration',
    position: 'relative',
    left: '0',
    top: '0',
    zIndex: 'auto',
    width: '100%',
    height: '100%',
    onMousedown: null,
    onClick: null
  }
}

// ─── Empty-canvas welcome overlay ────────────────────────────────────────────
// Shown when the lab board has no positioned tiles yet (no projects, no
// installables). Guides the user toward creating their first app — the
// primary CTA — with installing an integration as a secondary action.
//
// Visibility guards (all required):
//   - loadNodes has resolved (root._labLoadedAt > 0) — prevents flashing
//     during initial mount before the first fetch completes
//   - not currently loading
//   - no error
//   - nodes array empty
//   - installables array empty
export const LabEmptyTutorial = {
  if: (el, s) => {
    const root = s.root || s
    if (root.loading || root.error) return false
    if (!root._labLoadedAt) return false
    const hasNodes = (root.nodes || []).length > 0
    const hasInstallables = (root._installables || []).length > 0
    return !hasNodes && !hasInstallables
  },
  position: 'absolute',
  inset: '0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 4,

  TutCard: {
    pointerEvents: 'auto',
    flow: 'column',
    align: 'center center',
    gap: 'A',
    padding: 'B C',
    round: 'A',
    background: 'rgba(20,22,27,0.72)',
    border: '1px solid rgba(255,255,255,0.08)',
    style: { backdropFilter: 'blur(12px)', maxWidth: '380px', textAlign: 'center' },

    TutIcon: {
      tag: 'div',
      text: '◈',
      fontSize: 'D',
      color: 'rgba(255,255,255,0.18)',
      lineHeight: '1'
    },

    TutTitle: {
      tag: 'h3',
      text: 'Welcome to your lab',
      fontSize: 'A',
      fontWeight: '700',
      color: 'white',
      margin: '0'
    },

    TutBody: {
      tag: 'p',
      text: 'Start by creating a new app — or pin an existing project from your dashboard. You can also install integrations, MCP connectors, libraries, and plugins as draggable tiles.',
      fontSize: 'Z2',
      color: 'rgba(255,255,255,0.5)',
      margin: '0',
      lineHeight: '1.55'
    },

    // Primary CTA: create new app — the most common first action.
    TutCTA: {
      tag: 'button',
      type: 'button',
      text: '+ Create your new app',
      padding: 'Y B',
      round: '999px',
      border: '0',
      background: 'rgba(80,120,255,0.85)',
      color: 'white',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '600',
      ':hover': { background: 'rgba(80,120,255,1)' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          newProjectPicker: {
            open: true,
            tab: 'featured',
            templateId: 'blank',
            templateProject: null,
            projectType: 'app',
            name: '',
            creating: false,
            error: null,
            _libList: null,
            _libQuery: ''
          }
        })
      }
    },

    // Secondary action — small link below the primary CTA. Same picker
    // the rail toggle opens, but reachable from the welcome card too.
    TutSecondary: {
      tag: 'button',
      type: 'button',
      text: 'or install an integration',
      padding: 'Z 0',
      border: '0',
      background: 'transparent',
      color: 'rgba(255,255,255,0.55)',
      cursor: 'pointer',
      fontSize: 'Z',
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      ':hover': { color: 'rgba(255,255,255,0.85)' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          integrationPicker: {
            open: true,
            step: 'list',
            form: { __scope: 'workspace', __scopeId: null, __slug: 'default' }
          }
        })
      }
    }
  }
}

export const IntegrationChip = {
  flow: 'row',
  align: 'center start',
  gap: 'V',
  padding: 'V X',
  round: '999px',
  background: 'rgba(255,255,255,0.04)',
  border: (el, s) => `1px solid ${integrationColor(s.category)}55`,
  color: 'white',
  fontSize: 'Z',
  cursor: 'default',
  pointerEvents: 'auto',

  Dot: {
    width: '10px',
    height: '10px',
    round: '50%',
    flexShrink: '0',
    background: (el, s) => integrationColor(s.category)
  },
  Label: {
    tag: 'span',
    text: (el, s) => s.label || s.kindLabel || s.kind,
    fontSize: 'Z',
    color: 'rgba(255,255,255,0.9)'
  },
  Kind: {
    tag: 'span',
    text: (el, s) => s.kindLabel || s.kind,
    fontSize: 'Z',
    color: 'rgba(255,255,255,0.45)'
  }
}

// Single chip inside a scope rail. Extends IntegrationChip by key name but
// adds: icon glyph, scope pill, "Configure" link, "Re-scope to project" on
// right-click, and click → integration admin page.
export const ScopeRailChip = {
  flow: 'row',
  align: 'center',
  gap: 'V',
  padding: 'V X',
  round: '999px',
  background: 'rgba(255,255,255,0.05)',
  border: (el, s) => `1px solid ${integrationColor(s.category)}44`,
  color: 'white',
  fontSize: 'Z',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'A background defaultBezier',
  ':hover': { background: 'rgba(255,255,255,0.09)' },
  onClick: (e, el, s) => {
    window.open(integrationAdminUrl(s.kind), '_blank', 'noopener,noreferrer')
  },
  onContextmenu: (e, el, s) => {
    // Right-click "Re-scope to project" — opens the integration picker pre-
    // loaded with this integration's kind so the user can assign it to a
    // specific project. Only relevant for workspace-scoped rows; org-scoped
    // re-scoping would require org admin rights handled in governance.
    if (s.scopeType !== 'workspace') return
    e.preventDefault()
    const p = s.root?.integrationPicker || {}
    s.root?.update({
      integrationPicker: {
        ...p,
        open: true,
        step: 'form',
        selectedKind: {
          kind: s.kind,
          label: s.label || s.kindLabel || s.kind,
          category: s.category,
          default_scope: 'project'
        },
        form: { __scope: 'project', __scopeId: null, __slug: s.slug || 'default' }
      }
    })
  },

  Glyph: {
    width: '22px',
    height: '22px',
    round: '6px',
    flexShrink: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: (el, s) => (s.icon ? 'white' : integrationColor(s.category)),
    overflow: 'hidden',
    Img: {
      hide: (el, s) => !s.icon || el.parent._imgFailed,
      tag: 'img',
      src: (el, s) => s.icon ? `https://cdn.simpleicons.org/${s.icon}` : '',
      alt: (el, s) => s.kindLabel || s.kind,
      width: '65%',
      height: '65%',
      objectFit: 'contain',
      onRender: (el) => {
        if (!el.node) return
        el.node.onerror = () => {
          if (!el.parent) return
          el.parent._imgFailed = true
          el.node.style.display = 'none'
          const letter = el.parent.Letter
          if (letter?.node) letter.node.style.display = ''
        }
      }
    },
    Letter: {
      hide: (el, s) => !!s.icon && !el.parent._imgFailed,
      tag: 'span',
      fontWeight: '700',
      fontSize: 'Z1',
      color: '#14161b',
      text: (el, s) => integrationGlyph(s.kind)
    }
  },

  Name: {
    tag: 'span',
    text: (el, s) => s.label || s.kindLabel || s.kind,
    fontSize: 'Z',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.9)',
    style: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '120px'
    }
  },

  ScopePill: {
    tag: 'span',
    text: (el, s) => s.scopeType === 'org' ? 'ORG' : 'WORKSPACE',
    fontSize: 'Z1',
    fontWeight: '700',
    letterSpacing: '0.06em',
    color: (el, s) => s.scopeType === 'org' ? '#b88cff' : '#f0b85a',
    background: (el, s) =>
      s.scopeType === 'org' ? 'rgba(184,140,255,0.12)' : 'rgba(240,184,90,0.12)',
    padding: '1px 5px',
    round: '4px'
  }
}

// Horizontal rail that renders all workspace-scoped integrations in a sticky
// strip above the canvas grid. Collapses (height: 0 + overflow hidden) when
// there are no workspace integrations; shows an "Add workspace integration"
// button in empty state. Non-draggable — workspace integrations are already
// workspace-wide and cannot be dropped onto a specific project.
export const WorkspaceIntegrationRail = {
  position: 'absolute',
  top: '0',
  left: '0',
  right: '0',
  zIndex: 20,
  flow: 'column',
  pointerEvents: 'none',

  // Reserve vertical space so board content never jumps when rail appears.
  // A single-line rail is ~44px; empty state is 0 (collapsed).
  Bar: {
    pointerEvents: 'auto',
    flow: 'row',
    align: 'center',
    gap: 'V',
    padding: '0 X',
    // Reserve height regardless of content — prevents board layout shift when
    // the first workspace integration is installed.
    minHeight: '44px',
    background: 'rgba(20,22,27,0.82)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    style: { backdropFilter: 'blur(8px)' },
    // Rail is visible only when there are workspace-scoped integrations OR
    // we always show a slim affordance.
    display: (el, s) => {
      const items = (s?.root?.integrations || []).filter((i) => i.scope === 'workspace')
      return items.length ? 'flex' : 'flex'
    },

    HeaderLabel: {
      tag: 'span',
      text: 'Workspace',
      fontSize: 'Z1',
      fontWeight: '700',
      letterSpacing: '0.08em',
      color: '#f0b85a',
      textTransform: 'uppercase',
      flexShrink: '0',
      marginRight: 'V'
    },

    // Chips for each workspace-scoped integration
    Chips: {
      flow: 'row',
      gap: 'V',
      flexGrow: '1',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      childrenAs: 'state',
      children: (el, s) =>
        (s?.root?.integrations || [])
          .filter((i) => i.scope === 'workspace')
          .map((i) => ({ ...i, key: i.id })),
      childExtends: 'ScopeRailChip'
    },

    // Empty state — shown when there are no workspace integrations yet.
    Empty: {
      hide: (el, s) =>
        (s?.root?.integrations || []).some((i) => i.scope === 'workspace'),
      tag: 'button',
      type: 'button',
      text: '+ Add workspace integration',
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.35)',
      background: 'transparent',
      border: '1px dashed rgba(255,255,255,0.15)',
      round: '999px',
      padding: '3px 10px',
      cursor: 'pointer',
      ':hover': { color: 'rgba(255,255,255,0.65)', borderColor: 'rgba(255,255,255,0.3)' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          integrationPicker: {
            open: true,
            step: 'list',
            form: { __scope: 'workspace', __scopeId: null, __slug: 'default' }
          }
        })
      }
    }
  }
}

// Horizontal rail that renders all org-scoped integrations. Collapsed by
// default (root.orgRailOpen = false). A toggle button lets the user expand it.
// When expanded it shows above the WorkspaceIntegrationRail.
export const OrgIntegrationRail = {
  position: 'absolute',
  // Sits above the workspace rail. Workspace rail is 44px; org rail slides in
  // above it. Uses CSS transform to animate open/close.
  top: '0',
  left: '0',
  right: '0',
  zIndex: 21,
  flow: 'column',
  pointerEvents: 'none',

  // Toggle strip — always visible; click expands/collapses the chip row.
  Toggle: {
    pointerEvents: 'auto',
    flow: 'row',
    align: 'center',
    gap: 'V',
    padding: '0 X',
    height: '28px',
    background: 'rgba(14,16,20,0.9)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    style: { backdropFilter: 'blur(6px)' },
    // Only render the toggle when there are org integrations (or when the user
    // has previously opened the rail).
    display: (el, s) => {
      const hasOrg = (s?.root?.integrations || []).some((i) => i.scope === 'org')
      return (hasOrg || s?.root?.orgRailOpen) ? 'flex' : 'none'
    },
    cursor: 'pointer',
    ':hover': { background: 'rgba(20,22,27,0.96)' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.root?.update({ orgRailOpen: !s.root?.orgRailOpen })
    },

    Caret: {
      tag: 'span',
      text: (el, s) => s.root?.orgRailOpen ? '▾' : '▸',
      color: '#b88cff',
      fontSize: 'Z1',
      flexShrink: '0'
    },
    Label: {
      tag: 'span',
      text: 'Org integrations',
      fontSize: 'Z1',
      fontWeight: '700',
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: '#b88cff',
      flexGrow: '1'
    },
    Count: {
      tag: 'span',
      text: (el, s) => {
        const n = (s?.root?.integrations || []).filter((i) => i.scope === 'org').length
        return n ? `${n}` : ''
      },
      fontSize: 'Z1',
      color: 'rgba(184,140,255,0.65)',
      background: 'rgba(184,140,255,0.12)',
      padding: '1px 5px',
      round: '4px'
    }
  },

  // Chip row — visible only when orgRailOpen.
  Bar: {
    pointerEvents: 'auto',
    flow: 'row',
    align: 'center',
    gap: 'V',
    padding: '0 X',
    minHeight: '40px',
    background: 'rgba(16,18,23,0.88)',
    borderBottom: '1px solid rgba(184,140,255,0.12)',
    style: { backdropFilter: 'blur(8px)' },
    display: (el, s) => (s?.root?.orgRailOpen ? 'flex' : 'none'),

    Chips: {
      flow: 'row',
      gap: 'V',
      flexGrow: '1',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      childrenAs: 'state',
      children: (el, s) =>
        (s?.root?.integrations || [])
          .filter((i) => i.scope === 'org')
          .map((i) => ({ ...i, key: i.id })),
      childExtends: 'ScopeRailChip'
    },

    Empty: {
      hide: (el, s) =>
        (s?.root?.integrations || []).some((i) => i.scope === 'org'),
      tag: 'span',
      text: 'No org-wide integrations installed.',
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.3)',
      fontStyle: 'italic'
    }
  }
}

// Compact MCP connector chip — inline chip showing connector name + status pill.
// title and aria-label support screen-reader discovery.
export const MCPConnectorChip = {
  flow: 'x',
  gap: 'Z',
  align: 'center',
  padding: 'V Z1',
  round: 'Y',
  theme: 'canvas-card-glass',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'cerulean',
  cursor: 'pointer',
  pointerEvents: 'auto',
  overflow: 'hidden',
  title: (el, s) => `MCP connector: ${s.name || s.url || ''}`,
  'aria-label': (el, s) => `MCP connector: ${s.name || s.url || ''}`,
  ':hover': { background: 'cerulean.1' },

  // Accent dot (cerulean for enabled, muted for disabled).
  Dot: {
    width: 'Y1',
    height: 'Y1',
    round: '50%',
    flexShrink: '0',
    background: (el, s) => s.status === 'enabled' ? 'cerulean' : 'overlayStrong'
  },

  Name: {
    tag: 'span',
    text: (el, s) => s.name || s.url || 'MCP',
    fontSize: 'Z',
    fontWeight: '500',
    color: 'white.9',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'E'
  },

  IntegrationScopeBadge: {
    scope_type: (el, s) => s.scope_type,
    scope_id: (el, s) => s.scope_id
  },

  // Status pill — text label so meaning is not conveyed by color alone.
  StatusPill: {
    tag: 'span',
    padding: 'V Z',
    round: 'Y',
    fontSize: 'Y1',
    fontWeight: '700',
    background: (el, s) => s.status === 'enabled' ? 'cerulean.1' : 'overlayStrong',
    color: (el, s) => s.status === 'enabled' ? 'cerulean' : 'caption',
    text: (el, s) => s.status === 'enabled' ? 'Live' : 'Disabled'
  }
}

// Horizontal rail that renders all MCP connectors scoped to the workspace.
// Sits below the WorkspaceIntegrationRail (zIndex 19). Collapsed when no
// connectors are installed.
export const MCPConnectorRail = {
  position: 'absolute',
  style: { top: 'calc(var(--spacing-B) + var(--spacing-C) + var(--spacing-A))' }, // below org rail (≈B) + workspace rail (≈C) + header gap (≈A)
  left: '0',
  right: '0',
  zIndex: 19,
  flow: 'column',
  pointerEvents: 'none',

  Bar: {
    pointerEvents: 'auto',
    flow: 'x',
    align: 'center',
    gap: 'V',
    padding: '0 X',
    minHeight: 'B1',
    background: 'gray2.8',
    borderBottom: '1px solid cerulean.08',
    style: { backdropFilter: 'blur(8px)' },
    hide: (el, s) => !(s?.root?._mcpConnectors || []).length,

    HeaderLabel: {
      tag: 'span',
      text: 'MCP',
      fontSize: 'Z1',
      fontWeight: '700',
      letterSpacing: '0.08em',
      color: 'cerulean',
      textTransform: 'uppercase',
      flexShrink: '0',
      marginRight: 'V'
    },

    Chips: {
      flow: 'x',
      gap: 'V',
      flexGrow: '1',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      childrenAs: 'state',
      children: (el, s) =>
        (s?.root?._mcpConnectors || []).map((c) => ({ ...c, key: c.id })),
      childExtends: 'MCPConnectorChip'
    },

    // Empty state — always hidden because Bar is hidden when empty.
    // Kept for future use (e.g. "Add MCP connector" affordance in empty rail).
    AddBtn: {
      hide: (el, s) => (s?.root?._mcpConnectors || []).length > 0,
      tag: 'button',
      type: 'button',
      text: '+ Add MCP connector',
      fontSize: 'Z',
      color: 'cerulean.6',
      background: 'transparent',
      border: '1px dashed cerulean.3',
      round: 'D',
      padding: 'V2 Z',
      cursor: 'pointer',
      ':hover': { color: 'cerulean', borderColor: 'cerulean' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({
          _mcpConnectorPickerOpen: true,
          _mcpConnectorDraft: {
            transport: 'sse',
            authKind: 'none',
            scopeType: 'workspace',
            scopeId: null
          }
        })
      }
    }
  }
}

export const IntegrationKindRow = {
  flow: 'row',
  align: 'center start',
  gap: 'V',
  padding: 'V X',
  round: '8px',
  cursor: 'pointer',
  color: 'white',
  border: '1px solid transparent',
  ':hover': {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)'
  },
  Glyph: {
    width: '30px',
    height: '30px',
    round: '6px',
    flexShrink: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: (el, s) => (s.icon ? 'white' : integrationColor(s.category)),
    overflow: 'hidden',
    Img: {
      hide: (el, s) => !s.icon || el.parent._imgFailed,
      tag: 'img',
      src: (el, s) => s.icon ? `https://cdn.simpleicons.org/${s.icon}` : '',
      alt: (el, s) => s.label || s.kind,
      width: '60%',
      height: '60%',
      objectFit: 'contain',
      onRender: (el) => {
        if (!el.node) return
        el.node.onerror = () => {
          if (!el.parent) return
          el.parent._imgFailed = true
          el.node.style.display = 'none'
          const letter = el.parent.Letter
          if (letter?.node) letter.node.style.display = ''
        }
      }
    },
    Letter: {
      hide: (el, s) => !!s.icon && !el.parent._imgFailed,
      tag: 'span',
      fontWeight: '700',
      fontSize: 'Z2',
      color: '#14161b',
      text: (el, s) => integrationGlyph(s.kind)
    }
  },
  Info: {
    flow: 'column',
    flexGrow: '1',
    gap: '2px',
    Name: {
      tag: 'div',
      text: (el, s) => s.label || s.kind,
      fontSize: 'Z2',
      fontWeight: '500'
    },
    Sub: {
      tag: 'div',
      text: (el, s) => `${s.category} · ${s.auth_model || 'no auth'}`,
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.5)',
      fontFamily: 'monospace'
    }
  },
  Chevron: {
    tag: 'span',
    text: '→',
    color: 'rgba(255,255,255,0.35)'
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    const p = s.root?.integrationPicker || {}
    s.root?.update({
      integrationPicker: {
        ...p,
        step: 'form',
        selectedKind: {
          kind: s.kind,
          label: s.label,
          category: s.category,
          auth_model: s.auth_model,
          default_scope: s.default_scope,
          config_schema: s.config_schema,
          secret_label: s.secret_label,
          secret_hint: s.secret_hint
        },
        form: {
          __scope: s.default_scope || 'org',
          __scopeId: null,
          __slug: 'default'
        }
      }
    })
  }
}

export const IntegrationFormField = {
  flow: 'column',
  gap: 'W',
  Label: {
    tag: 'label',
    text: (el, s) => {
      const f =
        typeof s.field === 'function' ? s.field(null, s) : s.field || el.field
      return (f?.label || f?.key || '') + (f?.required ? ' *' : '')
    },
    fontSize: 'Z',
    color: 'rgba(255,255,255,0.7)',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  Input: {
    tag: 'input',
    type: (el, s) => {
      const f =
        typeof s.field === 'function' ? s.field(null, s) : s.field || el.field
      return f?.type || 'text'
    },
    placeholder: (el, s) => {
      const f =
        typeof s.field === 'function' ? s.field(null, s) : s.field || el.field
      return f?.placeholder || ''
    },
    value: (el, s) => {
      const f =
        typeof s.field === 'function' ? s.field(null, s) : s.field || el.field
      return s.root?.integrationPicker?.form?.[f?.key] || ''
    },
    padding: 'V X',
    round: '6px',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(0,0,0,0.35)',
    color: 'white',
    fontSize: 'Z2',
    onInput: (e, el, s) => {
      const f =
        typeof s.field === 'function' ? s.field(null, s) : s.field || el.field
      if (!f?.key) return
      const p = s.root?.integrationPicker || {}
      s.root?.update({
        integrationPicker: {
          ...p,
          form: { ...(p.form || {}), [f.key]: e.target.value }
        }
      })
    }
  },
  Hint: {
    tag: 'div',
    if: (el, s) => {
      const f =
        typeof s.field === 'function' ? s.field(null, s) : s.field || el.field
      return !!f?.hint
    },
    text: (el, s) => {
      const f =
        typeof s.field === 'function' ? s.field(null, s) : s.field || el.field
      return f?.hint
    },
    fontSize: 'Z',
    color: 'rgba(255,255,255,0.4)'
  }
}

// Unified settings modal for both workspace and project (canvas) pages.
// `root.settingsOpen` controls visibility, `root.settingsTab` picks the
// active section. Workspace tab is always available; Project tab shows
// canvas-level controls when `getCanvasV2()` returns a canvas state, and
// a helpful "open a project" placeholder otherwise. The Zoombar's
// Settings button on both pages triggers the same state, so a single
// modal definition covers both surfaces.
export const SettingsModal = {
  display: (el, s) => (s.root?.settingsOpen ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  zIndex: 200,
  background: 'rgba(0,0,0,0.5)',
  align: 'center center',
  pointerEvents: 'auto',
  style: { backdropFilter: 'blur(4px)' },
  onPointerDown: (ev, el, s) => {
    if (ev.target !== el.node) return
    s.root?.update({ settingsOpen: false })
  },

  // Keys prefixed with `Settings*` so DOMQL's key-based auto-extend
  // doesn't accidentally merge us with global `Card`, `Header`, `Body`,
  // `Content`, `ProjectSection`, etc. (ProjectSection in particular pulls
  // in ItemPreview whose href factory `.split()`s getAppKey() and crashes
  // on any route without an app key).
  SettingsCard: {
    position: 'relative',
    width: '640px',
    maxWidth: '92%',
    maxHeight: '80vh',
    background: '#1c1f24',
    color: 'white',
    round: 'A',
    flow: 'y',
    overflow: 'hidden',
    style: { boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
    onPointerDown: (ev) => ev.stopPropagation(),

    SettingsHeader: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A B',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      SettingsTitle: {
        tag: 'h3',
        text: 'Settings',
        fontSize: 'A',
        margin: '0'
      },
      SettingsClose: {
        tag: 'button',
        type: 'button',
        text: '×',
        'aria-label': 'Close settings',
        cursor: 'pointer',
        background: 'transparent',
        border: '0',
        color: 'white',
        fontSize: 'B',
        padding: '0 W',
        onClick: (ev, el, s) => {
          ev.stopPropagation()
          s.root?.update({ settingsOpen: false })
        }
      }
    },

    SettingsBody: {
      flow: 'x',
      flexGrow: '1',
      overflow: 'hidden',

      WsSettingsNav: {
        flow: 'y',
        width: '180px',
        padding: 'A V',
        gap: 'V',
        borderRight: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(0,0,0,0.25)',

        NavItemWorkspace: settingsTab(
          'workspace',
          'Workspace',
          'Workspace-wide drag, layout and picker settings.'
        ),
        NavItemProject: settingsTab(
          'project',
          'Project',
          'Canvas viewport, snap, grid and selection tools.'
        )
      },

      SettingsContent: {
        flexGrow: '1',
        flow: 'y',
        padding: 'A B',
        overflow: 'auto',
        gap: 'A',

        WorkspacePane: {
          display: (el, s) =>
            (s.root?.settingsTab || 'workspace') === 'workspace'
              ? 'flex'
              : 'none',
          flow: 'y',
          gap: 'A',

          DragModeRow: settingsBlockRow(
            'Drag mode',
            'Free lets you drop cards anywhere; Grid snaps to 24px increments.',
            {
              flow: 'x',
              gap: '0',
              round: 'Z',
              padding: 'W',
              background: 'rgba(255,255,255,0.06)',
              childExtends: 'Button',
              childProps: {
                padding: 'W Y',
                fontSize: 'Z',
                round: 'Z',
                cursor: 'pointer',
                border: '0'
              },
              FreeformBtn: {
                text: 'Free',
                background: (el, s) =>
                  getDragMode(s.root) === 'freeform'
                    ? 'rgba(255,255,255,0.16)'
                    : 'transparent',
                color: (el, s) =>
                  getDragMode(s.root) === 'freeform'
                    ? 'white'
                    : 'rgba(255,255,255,0.55)',
                onClick: (e, el, s) => s.root.update({ dragMode: 'freeform' })
              },
              GridBtn: {
                text: 'Grid',
                background: (el, s) =>
                  getDragMode(s.root) === 'grid'
                    ? 'rgba(255,255,255,0.16)'
                    : 'transparent',
                color: (el, s) =>
                  getDragMode(s.root) === 'grid'
                    ? 'white'
                    : 'rgba(255,255,255,0.55)',
                onClick: (e, el, s) => s.root.update({ dragMode: 'grid' })
              }
            }
          ),

          AutoLayoutRow: settingsBlockRow(
            'Auto layout',
            'Rearranges all cards into a tidy grid and persists positions.',
            {
              tag: 'button',
              type: 'button',
              padding: 'W A',
              round: 'Z',
              border: '0',
              background: 'rgba(255,255,255,0.12)',
              color: 'white',
              fontSize: 'Z',
              cursor: 'pointer',
              text: 'Run now',
              ':hover': { background: 'rgba(255,255,255,0.2)' },
              onClick: (e, el, s) => {
                e.stopPropagation()
                const root = s.root
                if (!Array.isArray(root?.nodes)) return
                const newPositions = layoutPositions(root.nodes)
                root.update({ positions: newPositions })
                persistPositions(
                  { nodes: root.nodes, positions: newPositions },
                  root.nodes.map((n) => n.id)
                )
              }
            }
          )
        },

        ProjectPane: {
          display: (el, s) =>
            (s.root?.settingsTab || 'workspace') === 'project'
              ? 'flex'
              : 'none',
          flow: 'y',
          gap: 'A',

          SettingsPlaceholder: {
            if: (el) => !el.call?.('getCanvasV2'),
            padding: 'B',
            round: 'Z',
            border: '1px dashed rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.02)',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.55)',
            flow: 'y',
            gap: 'V',
            PlaceholderTitle: {
              tag: 'span',
              text: 'No project open',
              fontWeight: '600',
              color: 'white'
            },
            PlaceholderHint: {
              tag: 'span',
              text: 'Project-level canvas settings (snap, rulers, frame mode) appear here when you open a project.',
              fontSize: 'Z'
            }
          },

          // Canvas v2 settings — mirror the old CvCanvasSettingsModal body.
          // Rendered inside an `if` so they only build when a canvas
          // context exists (workspace page returns null from getCanvasV2
          // and lands on the Placeholder above).
          FrameModeRow: settingsCanvasRow(
            'Frame mode',
            'Live iframe previews for each card.',
            {
              checked: 'frameMode',
              patch: (v) => ({ frameMode: v })
            }
          ),
          ShowRulersRow: settingsCanvasRow(
            'Show rulers',
            'Screen-space rulers along the top and left edges.',
            {
              checked: 'showRulers',
              patch: (v) => ({ showRulers: v })
            }
          ),
          ShowMinimapRow: settingsCanvasRow(
            'Show minimap',
            'Miniature overview at the bottom-right of the canvas.',
            {
              checked: 'showMinimap',
              patch: (v) => ({ showMinimap: v })
            }
          ),
          SnapRow: settingsCanvasRow(
            'Snap',
            'Master toggle for all snapping behaviours.',
            {
              checked: (v2) => !!v2?.snap?.enabled,
              patchSnap: (v) => ({ enabled: v })
            }
          ),
          SnapGridRow: settingsCanvasRow(
            'Snap to grid',
            'Pins drags to the canvas grid step.',
            {
              checked: (v2) => !!v2?.snap?.toGrid,
              patchSnap: (v) => ({ toGrid: v })
            }
          ),
          SnapItemsRow: settingsCanvasRow(
            'Snap to items',
            'Pulls drags to other cards as they align.',
            {
              checked: (v2) => !!v2?.snap?.toItems,
              patchSnap: (v) => ({ toItems: v })
            }
          ),
          SnapGuidesRow: settingsCanvasRow(
            'Snap to guides',
            'Snaps drags onto user-placed rulers and guides.',
            {
              checked: (v2) => !!v2?.snap?.toGuides,
              patchSnap: (v) => ({ toGuides: v })
            }
          )
        }
      }
    }
  }
}

// ─── ProjectDeleteConfirmModal ────────────────────────────────────────────────
// 2-step confirm for destructive project deletion. Triggered from ProjectNode
// Menu's Delete item by setting `root.projectDeleteConfirm = { id, key, name }`.
// Step 1: prompt with project name + type-to-confirm field.
// Step 2: actually calls sdk.deleteProject then removes from canvas.
//
// Keys prefixed "Del" to avoid DOMQL auto-extend collisions with shared exports.
export const ProjectDeleteConfirmModal = {
  display: (el, s) => (s.root?.projectDeleteConfirm ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  zIndex: 300,
  background: 'rgba(0,0,0,0.65)',
  align: 'center center',
  pointerEvents: 'auto',
  style: { backdropFilter: 'blur(4px)', outline: 'none' },
  tabindex: '-1',
  onRender: (el, s) => {
    if (s.root?.projectDeleteConfirm) el.node?.focus?.()
  },
  onPointerDown: (ev, el, s) => {
    if (ev.target !== el.node) return
    s.root?.update({ projectDeleteConfirm: null })
  },
  onKeydown: (ev, el, s) => {
    if (ev.key === 'Escape') s.root?.update({ projectDeleteConfirm: null })
  },

  DelCard: {
    position: 'relative',
    width: '420px',
    maxWidth: '92%',
    background: '#1c1f24',
    color: 'white',
    round: 'A',
    flow: 'y',
    overflow: 'hidden',
    style: { boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
    onPointerDown: (ev) => ev.stopPropagation(),

    DelHeader: {
      padding: 'A B',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      flow: 'x',
      align: 'center space-between',
      DelTitle: {
        tag: 'h3',
        text: (el, s) => `Delete "${s.root?.projectDeleteConfirm?.name}"?`,
        fontSize: 'A',
        margin: '0',
        color: 'rgb(255,90,90)'
      },
      DelClose: {
        tag: 'button',
        type: 'button',
        text: '×',
        'aria-label': 'Cancel delete',
        cursor: 'pointer',
        background: 'transparent',
        border: '0',
        color: 'white',
        fontSize: 'B',
        padding: '0 W',
        onClick: (ev, el, s) => {
          ev.stopPropagation()
          s.root?.update({ projectDeleteConfirm: null })
        }
      }
    },

    DelBody: {
      padding: 'A B',
      flow: 'y',
      gap: 'A',

      DelWarning: {
        tag: 'p',
        text: 'This action cannot be undone. Type the project key below to confirm deletion.',
        fontSize: 'Z2',
        color: 'rgba(255,255,255,0.7)',
        margin: '0',
        lineHeight: '1.5'
      },

      DelConfirmLabel: {
        tag: 'label',
        text: (el, s) => `Type "${s.root?.projectDeleteConfirm?.key}" to confirm:`,
        fontSize: 'Z2',
        color: 'rgba(255,255,255,0.55)',
        display: 'block'
      },

      DelInput: {
        tag: 'input',
        type: 'text',
        placeholder: (el, s) => s.root?.projectDeleteConfirm?.key || '',
        'aria-label': 'Confirm project key',
        autocomplete: 'off',
        padding: 'Y X',
        round: 'Z',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: 'white',
        fontSize: 'Z2',
        width: '100%',
        style: { boxSizing: 'border-box' },
        onInput: (ev, el, s) => {
          const val = ev.target?.value || ''
          s.root?.update({ _deleteConfirmTyped: val })
        }
      },

      DelActions: {
        flow: 'x',
        gap: 'Y',
        justifyContent: 'flex-end',

        DelCancelBtn: {
          tag: 'button',
          type: 'button',
          text: 'Cancel',
          padding: 'Y A',
          round: 'Z',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          color: 'white',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'rgba(255,255,255,0.12)' },
          onClick: (ev, el, s) => {
            ev.stopPropagation()
            s.root?.update({ projectDeleteConfirm: null, _deleteConfirmTyped: '' })
          }
        },

        DelConfirmBtn: {
          tag: 'button',
          type: 'button',
          text: 'Delete project',
          padding: 'Y A',
          round: 'Z',
          border: '0',
          cursor: 'pointer',
          fontSize: 'Z2',
          background: (el, s) => {
            const cfg = s.root?.projectDeleteConfirm
            const typed = s.root?._deleteConfirmTyped || ''
            return cfg?.key && typed === cfg.key ? 'rgb(200,40,40)' : 'rgba(200,40,40,0.3)'
          },
          color: 'white',
          ':hover': {},
          onClick: async (ev, el, s) => {
            ev.stopPropagation()
            const cfg = s.root?.projectDeleteConfirm
            const typed = s.root?._deleteConfirmTyped || ''
            if (!cfg?.key || typed !== cfg.key) return
            const sdk = getSDK()
            if (typeof sdk?.deleteProject === 'function') {
              try {
                await sdk.deleteProject(cfg.id)
              } catch (_) {}
            }
            // Remove from local canvas state regardless of server response
            const nodes = (s.root?.nodes || []).filter((n) => n._id !== cfg.id && n.id !== cfg.id)
            s.root?.update({
              nodes,
              projectDeleteConfirm: null,
              _deleteConfirmTyped: ''
            })
          }
        }
      }
    }
  }
}

// ─── ProjectDuplicateModal ────────────────────────────────────────────────────
// Name + key prompt for duplicating a project. Triggered by setting
// `root.projectDuplicateModal = { sourceId, sourceName }`.
// Calls sdk.duplicateProject after validation.
// Keys prefixed "Dup" to avoid DOMQL auto-extend collisions.
export const ProjectDuplicateModal = {
  display: (el, s) => (s.root?.projectDuplicateModal ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  zIndex: 300,
  background: 'rgba(0,0,0,0.65)',
  align: 'center center',
  pointerEvents: 'auto',
  style: { backdropFilter: 'blur(4px)', outline: 'none' },
  tabindex: '-1',
  onRender: (el, s) => {
    if (s.root?.projectDuplicateModal) el.node?.focus?.()
  },
  onPointerDown: (ev, el, s) => {
    if (ev.target !== el.node) return
    s.root?.update({ projectDuplicateModal: null })
  },
  onKeydown: (ev, el, s) => {
    if (ev.key === 'Escape') s.root?.update({ projectDuplicateModal: null })
  },

  DupCard: {
    position: 'relative',
    width: '420px',
    maxWidth: '92%',
    background: '#1c1f24',
    color: 'white',
    round: 'A',
    flow: 'y',
    overflow: 'hidden',
    style: { boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
    onPointerDown: (ev) => ev.stopPropagation(),

    DupHeader: {
      padding: 'A B',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      flow: 'x',
      align: 'center space-between',
      DupTitle: {
        tag: 'h3',
        text: (el, s) => `Duplicate "${s.root?.projectDuplicateModal?.sourceName}"`,
        fontSize: 'A',
        margin: '0'
      },
      DupClose: {
        tag: 'button',
        type: 'button',
        text: '×',
        'aria-label': 'Cancel duplicate',
        cursor: 'pointer',
        background: 'transparent',
        border: '0',
        color: 'white',
        fontSize: 'B',
        padding: '0 W',
        onClick: (ev, el, s) => {
          ev.stopPropagation()
          s.root?.update({ projectDuplicateModal: null })
        }
      }
    },

    DupBody: {
      padding: 'A B',
      flow: 'y',
      gap: 'A',

      DupNameLabel: {
        tag: 'label',
        text: 'New project name',
        fontSize: 'Z2',
        color: 'rgba(255,255,255,0.55)'
      },
      DupNameInput: {
        tag: 'input',
        type: 'text',
        'aria-label': 'New project name',
        placeholder: (el, s) => `Copy of ${s.root?.projectDuplicateModal?.sourceName || 'project'}`,
        autocomplete: 'off',
        padding: 'Y X',
        round: 'Z',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: 'white',
        fontSize: 'Z2',
        width: '100%',
        style: { boxSizing: 'border-box' },
        onInput: (ev, el, s) => {
          s.root?.update({ _dupName: ev.target?.value || '' })
        }
      },

      DupKeyLabel: {
        tag: 'label',
        text: 'New project key (slug)',
        fontSize: 'Z2',
        color: 'rgba(255,255,255,0.55)'
      },
      DupKeyInput: {
        tag: 'input',
        type: 'text',
        'aria-label': 'New project key',
        placeholder: 'my-project-copy',
        autocomplete: 'off',
        padding: 'Y X',
        round: 'Z',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: 'white',
        fontSize: 'Z2',
        fontFamily: 'monospace',
        width: '100%',
        style: { boxSizing: 'border-box' },
        onInput: (ev, el, s) => {
          const raw = (ev.target?.value || '').toLowerCase().replace(/[^a-z0-9-]/g, '-')
          s.root?.update({ _dupKey: raw })
        }
      },

      DupError: {
        if: (el, s) => !!s.root?._dupError,
        tag: 'p',
        text: (el, s) => s.root?._dupError || '',
        fontSize: 'Z1',
        color: 'rgb(255,100,100)',
        margin: '0'
      },

      DupActions: {
        flow: 'x',
        gap: 'Y',
        justifyContent: 'flex-end',

        DupCancelBtn: {
          tag: 'button',
          type: 'button',
          text: 'Cancel',
          padding: 'Y A',
          round: 'Z',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          color: 'white',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'rgba(255,255,255,0.12)' },
          onClick: (ev, el, s) => {
            ev.stopPropagation()
            s.root?.update({ projectDuplicateModal: null, _dupName: '', _dupKey: '', _dupError: '' })
          }
        },

        DupSubmitBtn: {
          tag: 'button',
          type: 'button',
          text: (el, s) => s.root?._dupCreating ? 'Duplicating…' : 'Duplicate',
          padding: 'Y A',
          round: 'Z',
          border: '0',
          background: 'rgba(80,120,255,0.85)',
          color: 'white',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'rgba(80,120,255,1)' },
          onClick: async (ev, el, s) => {
            ev.stopPropagation()
            const cfg = s.root?.projectDuplicateModal
            if (!cfg?.sourceId) return
            const name = (s.root?._dupName || '').trim() ||
              `Copy of ${cfg.sourceName || 'project'}`
            const newKey = (s.root?._dupKey || '').trim()
            if (!newKey) {
              s.root?.update({ _dupError: 'Key is required.' })
              return
            }
            const sdk = getSDK()
            if (typeof sdk?.duplicateProject !== 'function') {
              s.root?.update({ _dupError: 'Duplicate not supported by this SDK version.' })
              return
            }
            s.root?.update({ _dupCreating: true, _dupError: '' })
            try {
              const project = await sdk.duplicateProject({
                sourceProjectId: cfg.sourceId,
                name,
                newKey
              })
              if (project) prependProjectToCache(project)
              s.root?.update({
                projectDuplicateModal: null,
                _dupName: '',
                _dupKey: '',
                _dupError: '',
                _dupCreating: false
              })
            } catch (err) {
              s.root?.update({
                _dupError: err?.message || 'Duplicate failed.',
                _dupCreating: false
              })
            }
          }
        }
      }
    }
  }
}

// ─── ProjectEditMetadataModal ─────────────────────────────────────────────────
// Edit name + key for an existing project. Triggered by setting
// `root.projectEditMetadataModal = { id, name, key, owner }`.
// Calls sdk.updateProject then refreshes the local node.
// Keys prefixed "Meta" to avoid DOMQL auto-extend collisions.
export const ProjectEditMetadataModal = {
  display: (el, s) => (s.root?.projectEditMetadataModal ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  zIndex: 300,
  background: 'rgba(0,0,0,0.65)',
  align: 'center center',
  pointerEvents: 'auto',
  style: { backdropFilter: 'blur(4px)', outline: 'none' },
  tabindex: '-1',
  onRender: (el, s) => {
    if (s.root?.projectEditMetadataModal) el.node?.focus?.()
  },
  onPointerDown: (ev, el, s) => {
    if (ev.target !== el.node) return
    s.root?.update({ projectEditMetadataModal: null })
  },
  onKeydown: (ev, el, s) => {
    if (ev.key === 'Escape') s.root?.update({ projectEditMetadataModal: null })
  },

  MetaCard: {
    position: 'relative',
    width: '420px',
    maxWidth: '92%',
    background: '#1c1f24',
    color: 'white',
    round: 'A',
    flow: 'y',
    overflow: 'hidden',
    style: { boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
    onPointerDown: (ev) => ev.stopPropagation(),

    MetaHeader: {
      padding: 'A B',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      flow: 'x',
      align: 'center space-between',
      MetaTitle: {
        tag: 'h3',
        text: 'Edit project metadata',
        fontSize: 'A',
        margin: '0'
      },
      MetaClose: {
        tag: 'button',
        type: 'button',
        text: '×',
        'aria-label': 'Close',
        cursor: 'pointer',
        background: 'transparent',
        border: '0',
        color: 'white',
        fontSize: 'B',
        padding: '0 W',
        onClick: (ev, el, s) => {
          ev.stopPropagation()
          s.root?.update({ projectEditMetadataModal: null })
        }
      }
    },

    MetaBody: {
      padding: 'A B',
      flow: 'y',
      gap: 'A',

      MetaNameLabel: {
        tag: 'label',
        text: 'Project name',
        fontSize: 'Z2',
        color: 'rgba(255,255,255,0.55)'
      },
      MetaNameInput: {
        tag: 'input',
        type: 'text',
        'aria-label': 'Project name',
        placeholder: 'My project',
        autocomplete: 'off',
        padding: 'Y X',
        round: 'Z',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: 'white',
        fontSize: 'Z2',
        width: '100%',
        style: { boxSizing: 'border-box' },
        value: (el, s) => s.root?.projectEditMetadataModal?.name || '',
        onInput: (ev, el, s) => {
          const cfg = s.root?.projectEditMetadataModal
          if (cfg) s.root?.update({ projectEditMetadataModal: { ...cfg, _editName: ev.target?.value || '' } })
        }
      },

      MetaKeyLabel: {
        tag: 'label',
        text: 'Project key (slug)',
        fontSize: 'Z2',
        color: 'rgba(255,255,255,0.55)'
      },
      MetaKeyInput: {
        tag: 'input',
        type: 'text',
        'aria-label': 'Project key',
        placeholder: 'my-project',
        autocomplete: 'off',
        padding: 'Y X',
        round: 'Z',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: 'white',
        fontSize: 'Z2',
        fontFamily: 'monospace',
        width: '100%',
        style: { boxSizing: 'border-box' },
        value: (el, s) => s.root?.projectEditMetadataModal?.key || '',
        onInput: (ev, el, s) => {
          const raw = (ev.target?.value || '').toLowerCase().replace(/[^a-z0-9-]/g, '-')
          const cfg = s.root?.projectEditMetadataModal
          if (cfg) s.root?.update({ projectEditMetadataModal: { ...cfg, _editKey: raw } })
        }
      },

      MetaHint: {
        tag: 'p',
        text: 'Equivalent to: smbls project update',
        fontSize: 'Z1',
        color: 'rgba(255,255,255,0.3)',
        margin: '0',
        fontFamily: 'monospace'
      },

      MetaError: {
        if: (el, s) => !!s.root?.projectEditMetadataModal?._error,
        tag: 'p',
        text: (el, s) => s.root?.projectEditMetadataModal?._error || '',
        fontSize: 'Z1',
        color: 'rgb(255,100,100)',
        margin: '0'
      },

      MetaActions: {
        flow: 'x',
        gap: 'Y',
        justifyContent: 'flex-end',

        MetaCancelBtn: {
          tag: 'button',
          type: 'button',
          text: 'Cancel',
          padding: 'Y A',
          round: 'Z',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          color: 'white',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'rgba(255,255,255,0.12)' },
          onClick: (ev, el, s) => {
            ev.stopPropagation()
            s.root?.update({ projectEditMetadataModal: null })
          }
        },

        MetaSaveBtn: {
          tag: 'button',
          type: 'button',
          text: (el, s) => s.root?.projectEditMetadataModal?._saving ? 'Saving…' : 'Save',
          padding: 'Y A',
          round: 'Z',
          border: '0',
          background: 'rgba(80,120,255,0.85)',
          color: 'white',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'rgba(80,120,255,1)' },
          onClick: async (ev, el, s) => {
            ev.stopPropagation()
            const cfg = s.root?.projectEditMetadataModal
            if (!cfg?.id) return
            const name = (cfg._editName !== undefined ? cfg._editName : cfg.name || '').trim()
            const key = (cfg._editKey !== undefined ? cfg._editKey : cfg.key || '').trim()
            if (!name || !key) {
              s.root?.update({ projectEditMetadataModal: { ...cfg, _error: 'Name and key are required.' } })
              return
            }
            const sdk = getSDK()
            if (typeof sdk?.updateProject !== 'function') {
              s.root?.update({ projectEditMetadataModal: { ...cfg, _error: 'Update not supported by this SDK version.' } })
              return
            }
            s.root?.update({ projectEditMetadataModal: { ...cfg, _saving: true, _error: '' } })
            try {
              await sdk.updateProject(cfg.id, { name, slug: key })
              // Refresh the local node in canvas state
              const nodes = (s.root?.nodes || []).map((n) =>
                (n._id === cfg.id || n.id === cfg.id) ? { ...n, name, key } : n
              )
              s.root?.update({ nodes, projectEditMetadataModal: null })
            } catch (err) {
              const cur = s.root?.projectEditMetadataModal
              s.root?.update({ projectEditMetadataModal: { ...cur, _error: err?.message || 'Save failed.', _saving: false } })
            }
          }
        }
      }
    }
  }
}

export const LibraryPickerItem = {
  flow: 'row',
  align: 'center start',
  gap: 'V',
  padding: 'V X',
  round: '8px',
  cursor: 'pointer',
  color: 'white',
  border: '1px solid transparent',
  ':hover': {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)'
  },

  Info: {
    flow: 'column',
    flexGrow: '1',
    gap: '2px',
    Name: {
      tag: 'div',
      text: (el, s) => s.name,
      fontSize: 'Z2',
      fontWeight: '500'
    },
    Path: {
      tag: 'div',
      text: (el, s) => `${s.ownerSlug}/${s.key}`,
      fontSize: 'Z',
      color: 'rgba(255,255,255,0.5)',
      fontFamily: 'monospace'
    }
  },

  Visibility: {
    tag: 'span',
    text: (el, s) => (s.visibility === 'public' ? 'public' : 'org'),
    fontSize: 'Z',
    padding: '2px 8px',
    round: '10px',
    background: (el, s) =>
      s.visibility === 'public'
        ? 'rgba(80,200,120,0.18)'
        : 'rgba(255,255,255,0.08)',
    color: (el, s) =>
      s.visibility === 'public' ? 'rgb(120,220,160)' : 'rgba(255,255,255,0.7)'
  },

  onClick: (e, el, s) => {
    e.stopPropagation()
    const root = s.root
    const targetNodeId = s.targetNodeId
    const isStandalone = targetNodeId === '__standalone__'
    const targetNode = isStandalone
      ? null
      : (root?.nodes || []).find((n) => n.id === targetNodeId)
    if (!s._id || (!isStandalone && !targetNode?._id)) return

    const patch = { libraryPickerFor: null, libraryPickerQuery: '' }
    let nodes = root.nodes || []
    let libNode = nodes.find((n) => n._id === s._id)

    if (!libNode) {
      const raw = (root.libraryPickerList || []).find((p) => p._id === s._id)
      if (!raw) return
      libNode = normalizeProject(raw)
      nodes = [...nodes, libNode]
      const anchor = isStandalone
        ? { x: 120, y: 120 }
        : root.positions?.[targetNodeId] || { x: 120, y: 120 }
      patch.nodes = nodes
      patch.positions = {
        ...(root.positions || {}),
        [libNode.id]: {
          x: anchor.x - (isStandalone ? 0 : COL_STRIDE),
          y: anchor.y + (isStandalone ? ROW_STRIDE : 0),
          w: NODE_W,
          h: NODE_H
        }
      }
    }

    if (isStandalone) {
      root.update(patch)
      return
    }

    const exists = (root.connections || []).some(
      (c) => c.from === libNode.id && c.to === targetNodeId
    )
    if (!exists) {
      patch.connections = [
        ...(root.connections || []),
        {
          id: libNode.id + '→' + targetNodeId + ':' + Date.now(),
          from: libNode.id,
          to: targetNodeId
        }
      ]
    }

    root.update(patch)
    if (!exists) {
      persistSharedLibraries(root, [targetNodeId])
      redrawConnections()
    }
  }
}

const groupFrameChildren = GROUP_SLOTS.reduce((acc, ws) => {
  acc['GroupFrame_' + ws] = {
    attr: { 'data-group-id': ws },
    position: 'absolute',
    left: (el, s) => {
      const bb = groupBbox(s.root, ws)
      return bb ? bb.x - GROUP_PAD + 'px' : '-9999px'
    },
    top: (el, s) => {
      const bb = groupBbox(s.root, ws)
      return bb ? bb.y - GROUP_PAD + 'px' : '-9999px'
    },
    width: (el, s) => {
      const bb = groupBbox(s.root, ws)
      return bb ? bb.w + GROUP_PAD * 2 + 'px' : '0px'
    },
    height: (el, s) => {
      const bb = groupBbox(s.root, ws)
      return bb ? bb.h + GROUP_PAD * 2 + 'px' : '0px'
    },
    display: (el, s) => {
      const bb = groupBbox(s.root, ws)
      return bb && nodesOfGroup(ws, s.root).length >= 2 ? 'block' : 'none'
    },
    round: '16px',
    cursor: 'grab',
    border: (el, s) =>
      s.root?.hoverGroup === ws
        ? '2px solid rgba(255,255,255,0.8)'
        : '1px solid rgba(255,255,255,0.15)',
    background: (el, s) =>
      s.root?.hoverGroup === ws
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(255,255,255,0.03)',
    transition: 'A background defaultBezier, A border defaultBezier',

    onMousedown: (e, el, s) => {
      const root = s.root
      const members = nodesOfGroup(ws, root)
      if (!members.length) return
      startDrag(e, el, root, members[0].id, { group: true, groupWs: ws })
    },

    Label: {
      tag: 'span',
      position: 'absolute',
      top: '-28px',
      left: '4px',
      fontSize: 'Z',
      fontWeight: '600',
      letterSpacing: '0.08em',
      color: 'rgba(255,255,255,0.6)',
      textTransform: 'uppercase',
      pointerEvents: 'auto',
      cursor: 'text',
      padding: '2px 6px',
      round: '4px',
      style: { outline: 'none' },
      ':hover': { background: 'rgba(255,255,255,0.08)' },
      text: (el, s) => s.root?.groupNames?.[ws] || ws,
      onMousedown: (e) => e.stopPropagation(),
      onDblclick: (e, el) => {
        e.stopPropagation()
        el.node.contentEditable = 'true'
        el.node.focus()
        const doc = el.context.document
        const win = el.context.window
        const range = doc.createRange()
        range.selectNodeContents(el.node)
        const sel = win.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
      },
      onBlur: (e, el, s) => {
        el.node.contentEditable = 'false'
        const name = el.node.textContent.trim()
        s.root.update({
          groupNames: { ...(s.root.groupNames || {}), [ws]: name || ws }
        })
      },
      onKeydown: (e, el) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault()
          el.node.blur()
        }
      }
    }
  }
  return acc
}, {})

// Standalone picker overlays — exported so they can be mounted on ANY
// canvas surface (project page `/<owner>/<key>` or workspace `/workspaces/<slug>`).
// State lives on s.root so all handlers work identically regardless of surface.
// `workspace` below auto-extends both via key-name matching.
export const IntegrationPickerOverlay = {
  display: (el, s) => (s.root?.integrationPicker?.open ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  background: 'rgba(0,0,0,0.55)',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'B',
  zIndex: 210,
  style: { backdropFilter: 'blur(4px)' },
  onMousedown: (e, el, s) => {
    if (e.target === el.node)
      s.root?.update({ integrationPicker: { open: false } })
  },
  onRender: (el, s) => {
    if (el.__kindsFetched) return
    el.__kindsFetched = true
    ;(async () => {
      const bridge = await sdkBridge()
      if (!bridge?.listIntegrationKinds) return
      try {
        const kinds = await bridge.listIntegrationKinds()
        const prev = s.root?.integrationPicker || {}
        s.root?.update({
          integrationPicker: {
            ...prev,
            kinds: Array.isArray(kinds) ? kinds : []
          }
        })
      } catch (err) {
        const prev = s.root?.integrationPicker || {}
        s.root?.update({
          integrationPicker: {
            ...prev,
            error: err?.message || 'failed to load kinds'
          }
        })
      }
    })()
  },

  // Renamed off-default keys (Panel/Header/Hint/Form) where they
  // collided with globally registered components via key-based
  // auto-extend. `IntegrationPanel`, `PickerHeader`, etc. are unique
  // so the auto-extend doesn't merge in unrelated subtrees.
  IntegrationPanel: {
    width: '560px',
    maxWidth: '100%',
    maxHeight: 'min(80vh, 700px)',
    background: 'rgba(24,27,33,0.98)',
    border: '1px solid rgba(255,255,255,0.08)',
    round: '12px',
    boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
    flow: 'column',
    overflow: 'hidden',

    PickerHeader: {
      padding: 'A B',
      flow: 'column',
      gap: 'V',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      PickerRow: {
        flow: 'row',
        align: 'center space-between',
        gap: 'V',
        PickerTitle: {
          tag: 'div',
          text: (el, s) =>
            s.root?.integrationPicker?.step === 'form'
              ? 'Configure ' +
                (s.root.integrationPicker.selectedKind?.label ||
                  'integration')
              : 'Add integration',
          color: 'white',
          fontSize: 'A',
          fontWeight: '600'
        },
        PickerClose: {
          tag: 'button',
          type: 'button',
          text: '×',
          'aria-label': 'Close integration picker',
          padding: '0 W',
          fontSize: 'B',
          background: 'transparent',
          border: '0',
          color: 'rgba(255,255,255,0.7)',
          cursor: 'pointer',
          ':hover': { color: 'white' },
          onClick: (e, el, s) => {
            e.stopPropagation()
            s.root?.update({ integrationPicker: { open: false } })
          }
        }
      },
      PickerHint: {
        tag: 'div',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 'Z',
        text: (el, s) =>
          s.root?.integrationPicker?.step === 'form'
            ? 'Fill in connection details. Secrets stay server-side.'
            : 'Choose a service to connect.'
      },
      SearchInput: {
        tag: 'input',
        type: 'text',
        placeholder: 'Search integrations…',
        if: (el, s) => s.root?.integrationPicker?.step !== 'form',
        padding: 'V X',
        round: '8px',
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(0,0,0,0.35)',
        color: 'white',
        fontSize: 'Z2',
        onInput: (e, el, s) => {
          const prev = s.root?.integrationPicker || {}
          s.root?.update({
            integrationPicker: { ...prev, query: e.target.value }
          })
        }
      }
    },

    PickerKindList: {
      flow: 'column',
      gap: 'V',
      padding: 'V A',
      overflow: 'auto',
      flexGrow: '1',
      if: (el, s) => s.root?.integrationPicker?.step !== 'form',
      children: (el, s) => {
        const p = s.root?.integrationPicker
        if (!p) return []
        const q = (p.query || '').toLowerCase().trim()
        return (p.kinds || [])
          .filter(
            (k) =>
              !q ||
              `${k.kind} ${k.label} ${k.category}`.toLowerCase().includes(q)
          )
          .map((k) => ({ ...k, _pickerKey: k.kind }))
      },
      childrenAs: 'state',
      childExtends: 'IntegrationKindRow'
    },

    PickerLoading: {
      if: (el, s) => {
        const p = s.root?.integrationPicker
        return p && p.step !== 'form' && p.kinds == null
      },
      padding: 'A',
      text: 'Loading integrations…',
      color: 'rgba(255,255,255,0.55)',
      fontSize: 'Z2'
    },

    PickerForm: {
      flow: 'column',
      gap: 'A',
      padding: 'A B',
      overflow: 'auto',
      flexGrow: '1',
      if: (el, s) => s.root?.integrationPicker?.step === 'form',

      ScopeBlock: {
        flow: 'column',
        gap: 'W',
        ScopeLabel: {
          tag: 'div',
          text: 'Scope',
          fontSize: 'Z',
          color: 'rgba(255,255,255,0.7)',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.06em'
        },
        ScopeButtons: {
          flow: 'row',
          gap: 'W',
          childExtends: 'Button',
          childProps: {
            padding: 'W Y',
            fontSize: 'Z2',
            round: '6px',
            border: '1px solid rgba(255,255,255,0.12)',
            cursor: 'pointer',
            background: 'transparent',
            color: 'white'
          },
          OrgBtn: {
            text: 'Org',
            background: (el, s) =>
              (s.root?.integrationPicker?.form?.__scope || 'org') === 'org'
                ? 'rgba(255,255,255,0.16)'
                : 'transparent',
            onClick: (e, el, s) => {
              const p = s.root?.integrationPicker || {}
              s.root?.update({
                integrationPicker: {
                  ...p,
                  form: { ...(p.form || {}), __scope: 'org', __scopeId: null }
                }
              })
            }
          },
          WorkspaceBtn: {
            text: 'Workspace',
            background: (el, s) =>
              s.root?.integrationPicker?.form?.__scope === 'workspace'
                ? 'rgba(255,255,255,0.16)'
                : 'transparent',
            onClick: (e, el, s) => {
              const p = s.root?.integrationPicker || {}
              s.root?.update({
                integrationPicker: {
                  ...p,
                  form: {
                    ...(p.form || {}),
                    __scope: 'workspace',
                    __scopeId: null
                  }
                }
              })
            }
          },
          ProjectBtn: {
            text: 'Project',
            background: (el, s) =>
              s.root?.integrationPicker?.form?.__scope === 'project'
                ? 'rgba(255,255,255,0.16)'
                : 'transparent',
            onClick: (e, el, s) => {
              const p = s.root?.integrationPicker || {}
              s.root?.update({
                integrationPicker: {
                  ...p,
                  form: { ...(p.form || {}), __scope: 'project' }
                }
              })
            }
          }
        },
        ProjectSelect: {
          if: (el, s) =>
            s.root?.integrationPicker?.form?.__scope === 'project',
          tag: 'select',
          padding: 'V X',
          round: '6px',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(0,0,0,0.35)',
          color: 'white',
          fontSize: 'Z2',
          children: (el, s) => {
            const placeholder = [{ _id: '', name: '— pick project —' }]
            const own = (s.root?.nodes || []).filter(
              (n) => n._id && n.owner === s.root?.slug
            )
            return placeholder.concat(own)
          },
          childrenAs: 'state',
          childProps: (el, s) => ({
            tag: 'option',
            attr: { value: s._id || '' },
            text: s.name || s.key || '—'
          }),
          onChange: (e, el, s) => {
            const p = s.root?.integrationPicker || {}
            s.root?.update({
              integrationPicker: {
                ...p,
                form: { ...(p.form || {}), __scopeId: e.target.value || null }
              }
            })
          }
        }
      },

      FormFields: {
        flow: 'column',
        gap: 'V',
        children: (el, s) => {
          const p = s.root?.integrationPicker
          const kind = p?.selectedKind
          const configFields = Array.isArray(kind?.config_schema?.fields)
            ? kind.config_schema.fields
            : []
          const hasDisplayName = configFields.some(
            (f) => f.key === 'display_name' || f.key === '__displayName'
          )
          const base = [
            {
              field: {
                key: '__slug',
                label: 'Slug',
                placeholder: 'default',
                required: true,
                hint: 'Unique within scope.'
              }
            },
            ...(hasDisplayName
              ? []
              : [
                  {
                    field: {
                      key: '__displayName',
                      label: 'Display name',
                      placeholder: 'Human-readable label'
                    }
                  }
                ]),
            ...configFields.map((f) => ({ field: f }))
          ]
          const auth = kind?.auth_model
          if (auth && auth !== 'none') {
            base.push({
              field: {
                key: '__secret',
                label: kind?.secret_label || 'Secret',
                placeholder: kind?.secret_hint || '',
                type: 'password',
                required: true
              }
            })
          }
          return base
        },
        childrenAs: 'state',
        childExtends: 'IntegrationFormField'
      },

      ErrorLine: {
        tag: 'div',
        if: (el, s) => !!s.root?.integrationPicker?.error,
        text: (el, s) => s.root.integrationPicker.error,
        color: '#ff8a8a',
        fontSize: 'Z'
      },

      Buttons: {
        flow: 'row',
        gap: 'V',
        justify: 'flex-end',
        paddingTop: 'V',
        CancelBtn: {
          tag: 'button',
          type: 'button',
          text: 'Cancel',
          padding: 'V X',
          fontSize: 'Z2',
          round: '6px',
          cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'transparent',
          color: 'rgba(255,255,255,0.8)',
          onClick: (e, el, s) =>
            s.root?.update({ integrationPicker: { open: false } })
        },
        SaveBtn: {
          tag: 'button',
          type: 'button',
          text: (el, s) =>
            s.root?.integrationPicker?.saving ? 'Saving…' : 'Create',
          padding: 'V X',
          fontSize: 'Z2',
          round: '6px',
          cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.16)',
          color: 'white',
          onClick: (e, el, s) => {
            e.stopPropagation()
            saveIntegrationFromPicker(s.root)
          }
        }
      }
    }
  }
}

export const LibraryPickerOverlay = {
  display: (el, s) => (s.root?.libraryPickerFor ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  background: 'rgba(0,0,0,0.55)',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: '80px',
  zIndex: 200,
  onMousedown: (e, el, s) => {
    if (e.target === el.node)
      s.root?.update({ libraryPickerFor: null, libraryPickerQuery: '' })
  },

  Panel: {
    width: '480px',
    maxHeight: '60vh',
    background: 'rgba(24,27,33,0.98)',
    border: '1px solid rgba(255,255,255,0.08)',
    round: '12px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
    flow: 'column',
    overflow: 'hidden',

    Header: {
      padding: 'X A',
      flow: 'column',
      gap: 'V',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      Title: {
        tag: 'div',
        text: 'Import library',
        color: 'white',
        fontSize: 'A',
        fontWeight: '600'
      },
      Hint: {
        tag: 'div',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 'Z',
        text: 'Search public libraries or projects in your orgs.'
      },
      SearchInput: {
        tag: 'input',
        type: 'text',
        placeholder: 'Search libraries…',
        padding: 'V X',
        round: '8px',
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(0,0,0,0.35)',
        color: 'white',
        fontSize: 'Z2',
        onRender: (el, s) => {
          if (el.__autofocused) return
          el.__autofocused = true
          setTimeout(() => el.node?.focus?.(), 50)
        },
        onInput: (e, el, s) => {
          s.root?.update({ libraryPickerQuery: e.target.value })
        }
      }
    },

    Results: {
      flow: 'column',
      overflow: 'auto',
      flexGrow: '1',
      padding: 'V',
      onRender: (el, s) => {
        if (el.__loadingInit) return
        el.__loadingInit = true
        ;(async () => {
          try {
            const list = await getAllProjectsCached()
            s.root?.update({ libraryPickerList: list || [] })
          } catch {}
        })()
      },

      List: {
        flow: 'column',
        gap: 'V',
        childrenAs: 'state',
        children: (el, s) => {
          const root = s.root
          if (!root?.libraryPickerFor) return []
          const isStandalone = root.libraryPickerFor === '__standalone__'
          const q = (root.libraryPickerQuery || '').toLowerCase().trim()
          const nodes = root.nodes || []
          const targetNode = isStandalone
            ? null
            : nodes.find((n) => n.id === root.libraryPickerFor)
          const meOwner = targetNode?.owner || root.slug
          const alreadyLibs = isStandalone
            ? new Set(nodes.map((n) => n._id).filter(Boolean))
            : new Set(
                (root.connections || [])
                  .filter((c) => c.to === root.libraryPickerFor)
                  .map((c) => nodes.find((n) => n.id === c.from)?._id)
                  .filter(Boolean)
              )
          const all = root.libraryPickerList || []
          const filtered = all
            .filter((p) => {
              if (!p?.key) return false
              if (targetNode && p._id === targetNode._id) return false
              if (alreadyLibs.has(p._id)) return false
              const isPublic = p.visibility === 'public'
              const sameOwner = (p.owner?.slug || '') === meOwner
              if (!isPublic && !sameOwner) return false
              if (!q) return true
              const hay =
                `${p.key} ${p.name || ''} ${p.owner?.slug || ''}`.toLowerCase()
              return hay.includes(q)
            })
            .slice(0, 40)
            .map((p) => ({
              _id: p._id,
              key: p.key,
              name: p.name || p.key,
              ownerSlug: p.owner?.slug || '',
              visibility: p.visibility,
              targetNodeId: root.libraryPickerFor
            }))
          return filtered
        },
        childExtends: 'LibraryPickerItem'
      },

      Empty: {
        display: (el, s) => {
          const root = s.root
          if (!root?.libraryPickerFor) return 'none'
          const list = root.libraryPickerList || []
          return list.length ? 'none' : 'block'
        },
        padding: 'A',
        text: 'Loading…',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 'Z2'
      }
    }
  }
}

// ─── Featured template definitions ──────────────────────────────────────────
// Each entry defines page stubs passed as `pages` content when creating a
// project from a template. "blank" produces an empty project (no pages passed).
// The deck/landing stubs mirror the page-key structure of commercials/deck and
// commercials/landing so the newly created project is immediately navigable.
//
// `templateProjectKey` — when set, the server deep-clones pages/components/
// designSystem from that project's ProjectData instead of using the inline
// `pages` stub. Format: "owner/key" — e.g. "commercials/deck".
const FEATURED_TEMPLATES = [
  {
    id: 'blank',
    label: 'Blank',
    hint: 'Empty project — start from scratch.',
    emoji: '◻',
    pages: null,
    projectType: 'project'
  },
  {
    id: 'deck',
    label: 'Deck',
    hint: '13-slide pitch deck sourced from commercials/deck.',
    emoji: '📑',
    projectType: 'project',
    // templateProjectKey triggers server-side deep-clone from the live
    // commercials/deck project (pages, components, designSystem included).
    templateProjectKey: 'commercials/deck',
    // Fallback page stubs used when the server clone fails or the source
    // project is unavailable — keeps the create flow from silently breaking.
    pages: {
      index: { extends: 'slide0' },
      slide0: { tag: 'div', text: 'Slide 1' },
      slide1: { tag: 'div', text: 'Slide 2' },
      slide2: { tag: 'div', text: 'Slide 3' },
      slide3: { tag: 'div', text: 'Slide 4' },
      slide4: { tag: 'div', text: 'Slide 5' },
      slide5: { tag: 'div', text: 'Slide 6' },
      slide6: { tag: 'div', text: 'Slide 7' },
      slide7: { tag: 'div', text: 'Slide 8' },
      slide8: { tag: 'div', text: 'Slide 9' },
      slide9: { tag: 'div', text: 'Slide 10' },
      slide10: { tag: 'div', text: 'Slide 11' },
      slide11: { tag: 'div', text: 'Slide 12' },
      slide12: { tag: 'div', text: 'Slide 13' }
    }
  },
  {
    id: 'landing',
    label: 'Landing',
    hint: 'Marketing landing page sourced from commercials/landing.',
    emoji: '🏠',
    projectType: 'project',
    // templateProjectKey triggers server-side deep-clone from the live
    // commercials/landing project (all pages and design system included).
    templateProjectKey: 'commercials/landing',
    // Fallback page stubs used when the server clone fails or the source
    // project is unavailable.
    pages: {
      index: { extends: 'main' },
      main: { tag: 'div', text: 'Main' },
      pricing: { tag: 'div', text: 'Pricing' },
      signup: { tag: 'div', text: 'Sign up' },
      terms: { tag: 'div', text: 'Terms' },
      privacy: { tag: 'div', text: 'Privacy' }
    }
  },
  {
    id: 'app',
    label: 'App',
    hint: 'Web app scaffold — dashboard, settings, auth.',
    emoji: '📱',
    projectType: 'project',
    pages: {
      index: { extends: 'dashboard' },
      dashboard: { tag: 'div', text: 'Dashboard' },
      settings: { tag: 'div', text: 'Settings' },
      login: { tag: 'div', text: 'Login' }
    }
  },
  {
    id: 'library',
    label: 'Library',
    hint: 'Shared component library for your workspace.',
    emoji: '📚',
    projectType: 'library',
    pages: null
  }
]

// ─── newProjectPicker state helpers ──────────────────────────────────────────
function closeNewProjectPicker (root) {
  root?.update({ newProjectPicker: { open: false } })
}

async function submitNewProjectPicker (root, rootEl) {
  const p = root?.newProjectPicker
  if (!p || p.creating) return
  const name = (p.name || '').trim()
  if (!name) {
    root.update({ newProjectPicker: { ...p, error: 'Please enter a project name.' } })
    return
  }

  // Determine template source:
  //  - tab === 'library' → user picked a specific project key to scaffold from
  //  - tab === 'featured' → use FEATURED_TEMPLATES entry (may have templateProjectKey)
  const tab = p.tab || 'featured'
  const tpl = FEATURED_TEMPLATES.find((t) => t.id === p.templateId) || FEATURED_TEMPLATES[0]

  // Library tab requires a project selection before submitting.
  if (tab === 'library' && !p.templateProject) {
    root.update({ newProjectPicker: { ...p, error: 'Select a project to scaffold from.' } })
    return
  }

  const slugRaw = String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '')
    .slice(0, 40) || `project-${Math.random().toString(36).slice(2, 6)}`

  root.update({ newProjectPicker: { ...p, creating: true, error: null } })

  const sdk = rootEl?.context?.methods?.sdk
  if (!sdk?.createOrgProject) {
    root.update({ newProjectPicker: { ...p, creating: false, error: 'SDK not available.' } })
    return
  }

  // Resolve org id from slug caches (same logic as WorkspaceNavbar.orgIdFromCaches)
  const slug = root.slug || root.activeWorkspace || ''
  let orgId = null
  try {
    const rawProjects = sessionStorage.getItem('__canvas_projects_v1')
    if (rawProjects) {
      const { data } = JSON.parse(rawProjects)
      for (const proj of data || []) {
        const o = proj?.owner
        if (o?.slug === slug && o?.id && o?.type === 'organization') { orgId = o.id; break }
      }
    }
  } catch {}
  if (!orgId) {
    try {
      const rawOrgs = sessionStorage.getItem('__canvas_orgs_v1')
      if (rawOrgs) {
        const { data } = JSON.parse(rawOrgs)
        const match = (data || []).find((o) => o?.slug === slug)
        if (match) orgId = match._id || match.id || null
      }
    } catch {}
  }
  if (!orgId) {
    root.update({
      newProjectPicker: { ...p, creating: false, error: 'Could not resolve org for this workspace.' }
    })
    return
  }

  try {
    const payload = {
      name,
      // Library tab always creates a project-type project regardless of which
      // featured template was last highlighted.
      projectType: tab === 'library' ? 'project' : (tpl.projectType || 'project'),
      slug: slugRaw
    }

    // Determine the templateProject key to send for server-side deep-clone:
    //  1. Library tab: user explicitly picked a project → p.templateProject (owner/key)
    //  2. Featured template with templateProjectKey → tpl.templateProjectKey
    //  3. Featured template without templateProjectKey → inline pages stub
    if (tab === 'library' && p.templateProject) {
      // Server clones pages/components/designSystem from this project.
      payload.templateProject = p.templateProject
    } else if (tpl.templateProjectKey) {
      // Server clones from the canonical commercials template project.
      payload.templateProject = tpl.templateProjectKey
      // Also include the page stubs as fallback — server ignores them when
      // templateProject clone succeeds.
      if (tpl.pages) payload.pages = tpl.pages
    } else if (tpl.pages) {
      // No server-side clone; use the inline page stubs as-is.
      payload.pages = tpl.pages
    }

    const project = await sdk.createOrgProject(orgId, payload)
    // Optimistically insert into the shared projects cache so the workspace
    // board and dashboard sidebar reflect the new project without a reload.
    prependProjectToCache(project)
    closeNewProjectPicker(root)
    const owner = project?.owner?.slug || slug
    const key = project?.key || slugRaw
    const urls = buildProjectUrls(owner, key)
    if (urls) rootEl.router(`/${urls.safeOwner}/${urls.safeKey}`, rootEl.getRoot())
  } catch (err) {
    root.update({
      newProjectPicker: { ...p, creating: false, error: err?.message || 'Create failed.' }
    })
  }
}

// ─── NpTemplateCard — child card for the featured-templates grid ──────────────
// Referenced by childExtends: 'NpTemplateCard' in NewProjectPickerOverlay.
// Prefixed "Np" to avoid key-based auto-extend collisions with shared components.
export const NpTemplateCard = {
  tag: 'button',
  type: 'button',
  flow: 'column',
  gap: 'W',
  padding: 'X V',
  round: '10px',
  cursor: 'pointer',
  border: (el, s) =>
    (s.root?.newProjectPicker?.templateId || 'blank') === s._tplId
      ? '2px solid rgba(255,255,255,0.55)'
      : '2px solid rgba(255,255,255,0.07)',
  background: (el, s) =>
    (s.root?.newProjectPicker?.templateId || 'blank') === s._tplId
      ? 'rgba(255,255,255,0.08)'
      : 'rgba(255,255,255,0.02)',
  ':hover': {
    background: 'rgba(255,255,255,0.07)',
    border: '2px solid rgba(255,255,255,0.2)'
  },
  alignItems: 'center',
  textAlign: 'center',

  NpCardEmoji: {
    tag: 'span',
    text: (el, s) => s._tplEmoji || '◻',
    fontSize: 'B2',
    lineHeight: '1'
  },
  NpCardLabel: {
    tag: 'span',
    text: (el, s) => s._tplLabel || '',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'white'
  },
  NpCardHint: {
    tag: 'span',
    text: (el, s) => s._tplHint || '',
    fontSize: 'Z',
    color: 'rgba(255,255,255,0.45)',
    lineHeight: '1.4'
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    const p = s.root?.newProjectPicker || {}
    s.root?.update({ newProjectPicker: { ...p, templateId: s._tplId } })
  }
}

// ─── NpLibItem — one row in the library-tab project list ────────────────────
// Prefixed "NpLib" to avoid auto-extend collisions with shared components.
// Clicking a row sets newProjectPicker.templateProject = "owner/key".
export const NpLibItem = {
  tag: 'button',
  type: 'button',
  flow: 'row',
  gap: 'V',
  padding: 'V X',
  round: '6px',
  border: (el, s) => {
    const sel = s.root?.newProjectPicker?.templateProject
    const ownerKey = s._libOwner ? `${s._libOwner}/${s._libKey}` : s._libKey
    return sel === ownerKey
      ? '1px solid rgba(255,255,255,0.4)'
      : '1px solid transparent'
  },
  background: (el, s) => {
    const sel = s.root?.newProjectPicker?.templateProject
    const ownerKey = s._libOwner ? `${s._libOwner}/${s._libKey}` : s._libKey
    return sel === ownerKey
      ? 'rgba(255,255,255,0.1)'
      : 'transparent'
  },
  cursor: 'pointer',
  textAlign: 'left',
  ':hover': { background: 'rgba(255,255,255,0.06)' },
  alignItems: 'center',

  NpLibItemOwner: {
    tag: 'span',
    text: (el, s) => s._libOwner ? `${s._libOwner}/` : '',
    fontSize: 'Z',
    color: 'rgba(255,255,255,0.45)',
    flexShrink: '0'
  },
  NpLibItemName: {
    tag: 'span',
    text: (el, s) => s._libName || s._libKey || '',
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'white',
    flexGrow: '1'
  },

  onClick: (e, el, s) => {
    e.stopPropagation()
    const p = s.root?.newProjectPicker || {}
    const ownerKey = s._libOwner ? `${s._libOwner}/${s._libKey}` : s._libKey
    s.root?.update({ newProjectPicker: { ...p, templateProject: ownerKey, error: null } })
  }
}

// ─── NewProjectPickerOverlay ─────────────────────────────────────────────────
// Full-screen overlay for "New project" creation.
// Triggered by WorkspaceNavbar "New project" / "New library" items.
// State key: root.newProjectPicker = { open, tab, templateId, templateProject, name, creating, error, _libList, _libQuery }
export const NewProjectPickerOverlay = {
  display: (el, s) => (s.root?.newProjectPicker?.open ? 'flex' : 'none'),
  position: 'fixed',
  inset: '0',
  background: 'rgba(0,0,0,0.55)',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'B',
  zIndex: 220,
  style: { backdropFilter: 'blur(4px)' },
  onMousedown: (e, el, s) => {
    if (e.target === el.node) closeNewProjectPicker(s.root)
  },

  NpPanel: {
    width: '560px',
    maxWidth: '100%',
    maxHeight: 'min(90vh, 700px)',
    background: 'rgba(18,20,26,0.99)',
    border: '1px solid rgba(255,255,255,0.08)',
    round: '14px',
    boxShadow: '0 30px 80px rgba(0,0,0,0.65)',
    flow: 'column',
    overflow: 'hidden',

    NpHeader: {
      padding: 'A B',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      flow: 'row',
      align: 'center space-between',

      NpTitle: {
        tag: 'div',
        text: 'New project',
        color: 'white',
        fontSize: 'A',
        fontWeight: '600'
      },
      NpClose: {
        tag: 'button',
        type: 'button',
        text: '×',
        'aria-label': 'Close new-project picker',
        padding: '0 W',
        fontSize: 'B',
        background: 'transparent',
        border: '0',
        color: 'rgba(255,255,255,0.7)',
        cursor: 'pointer',
        ':hover': { color: 'white' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          closeNewProjectPicker(s.root)
        }
      }
    },

    NpNameRow: {
      padding: 'V B',
      paddingTop: 'X',
      flow: 'column',
      gap: 'W',

      NpNameLabel: {
        tag: 'label',
        text: 'Project name',
        fontSize: 'Z',
        fontWeight: '600',
        color: 'rgba(255,255,255,0.7)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em'
      },
      NpNameInput: {
        tag: 'input',
        type: 'text',
        placeholder: 'My project…',
        padding: 'V X',
        round: '8px',
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(0,0,0,0.4)',
        color: 'white',
        fontSize: 'Z2',
        onRender: (el, s) => {
          if (el.__npFocused) return
          el.__npFocused = true
          setTimeout(() => el.node?.focus?.(), 60)
        },
        onInput: (e, el, s) => {
          const p = s.root?.newProjectPicker || {}
          s.root?.update({ newProjectPicker: { ...p, name: e.target.value, error: null } })
        },
        onKeydown: (e, el, s) => {
          if (e.key === 'Enter') submitNewProjectPicker(s.root, el.getRoot())
          if (e.key === 'Escape') closeNewProjectPicker(s.root)
        }
      }
    },

    NpTabs: {
      flow: 'row',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      padding: '0 B',
      gap: '0',

      NpTabFeatured: {
        tag: 'button',
        type: 'button',
        text: 'Featured templates',
        padding: 'V W',
        fontSize: 'Z2',
        fontWeight: '500',
        background: 'transparent',
        border: '0',
        borderBottom: (el, s) =>
          (s.root?.newProjectPicker?.tab || 'featured') === 'featured'
            ? '2px solid white'
            : '2px solid transparent',
        color: (el, s) =>
          (s.root?.newProjectPicker?.tab || 'featured') === 'featured'
            ? 'white'
            : 'rgba(255,255,255,0.45)',
        cursor: 'pointer',
        marginBottom: '-1px',
        onClick: (e, el, s) => {
          const p = s.root?.newProjectPicker || {}
          // Clear templateProject when switching away from library tab.
          s.root?.update({ newProjectPicker: { ...p, tab: 'featured', templateProject: null, error: null } })
        }
      },

      NpTabLibrary: {
        tag: 'button',
        type: 'button',
        text: 'From library',
        padding: 'V W',
        fontSize: 'Z2',
        fontWeight: '500',
        background: 'transparent',
        border: '0',
        borderBottom: (el, s) =>
          s.root?.newProjectPicker?.tab === 'library'
            ? '2px solid white'
            : '2px solid transparent',
        color: (el, s) =>
          s.root?.newProjectPicker?.tab === 'library'
            ? 'white'
            : 'rgba(255,255,255,0.45)',
        cursor: 'pointer',
        marginBottom: '-1px',
        onClick: (e, el, s) => {
          const p = s.root?.newProjectPicker || {}
          s.root?.update({ newProjectPicker: { ...p, tab: 'library' } })
          // Trigger library list load on tab activation. onRender on NpLibSearch
          // fires only once at mount and may not fire if the element was mounted
          // while the modal was closed or the tab was inactive. Loading here
          // guarantees the list is fetched whenever the user switches to this tab.
          if (!p._libList || !p._libList.length) {
            ;(async () => {
              try {
                const list = await getAllProjectsCached()
                const fresh = s.root?.newProjectPicker || {}
                s.root?.update({ newProjectPicker: { ...fresh, _libList: list || [] } })
              } catch {}
            })()
          }
        }
      }
    },

    // Featured templates grid — 3 columns, spans the middle body
    NpFeaturedGrid: {
      display: (el, s) =>
        (s.root?.newProjectPicker?.tab || 'featured') === 'featured' ? 'grid' : 'none',
      style: { gridTemplateColumns: 'repeat(3, 1fr)' },
      gap: 'V',
      padding: 'X B',
      overflow: 'auto',
      children: () => FEATURED_TEMPLATES.map((tpl) => ({
        _tplId: tpl.id,
        _tplLabel: tpl.label,
        _tplHint: tpl.hint,
        _tplEmoji: tpl.emoji
      })),
      childrenAs: 'state',
      childExtends: 'NpTemplateCard'
    },

    // Library tab — inline project search + selection to scaffold from.
    // Selecting a project sets newProjectPicker.templateProject = "owner/key".
    // Submit passes templateProject to the server for deep-clone.
    NpLibraryTab: {
      display: (el, s) =>
        s.root?.newProjectPicker?.tab === 'library' ? 'flex' : 'none',
      flow: 'column',
      gap: 'V',
      padding: 'X B',
      flexGrow: '1',
      minHeight: '0',

      NpLibHint: {
        tag: 'div',
        text: 'Choose any project to scaffold from — its pages, components, and design system will be copied into the new project.',
        fontSize: 'Z2',
        color: 'rgba(255,255,255,0.55)',
        lineHeight: '1.5'
      },

      // Selected project badge — shows current selection with a clear button.
      NpLibSelected: {
        display: (el, s) => s.root?.newProjectPicker?.templateProject ? 'flex' : 'none',
        flow: 'row',
        gap: 'V',
        align: 'center',
        padding: 'V X',
        round: '8px',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.2)',

        NpLibSelectedLabel: {
          tag: 'span',
          text: (el, s) => {
            const key = s.root?.newProjectPicker?.templateProject || ''
            return `Template: ${key}`
          },
          fontSize: 'Z2',
          color: 'white',
          flexGrow: '1'
        },
        NpLibSelectedClear: {
          tag: 'button',
          type: 'button',
          text: '×',
          padding: '0 W',
          fontSize: 'A',
          background: 'transparent',
          border: '0',
          color: 'rgba(255,255,255,0.55)',
          cursor: 'pointer',
          ':hover': { color: 'white' },
          onClick: (e, el, s) => {
            e.stopPropagation()
            const p = s.root?.newProjectPicker || {}
            s.root?.update({ newProjectPicker: { ...p, templateProject: null, error: null } })
          }
        }
      },

      // Search input — filters the project list.
      NpLibSearch: {
        tag: 'input',
        type: 'search',
        placeholder: 'Search projects…',
        padding: 'V X',
        round: '8px',
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(0,0,0,0.4)',
        color: 'white',
        fontSize: 'Z2',
        onRender: (el, s) => {
          // Guard: skip if already loaded a non-empty list.
          // Allow a retry if __npLibLoaded was set but the list came back empty
          // (e.g. the fetch resolved before the SDK was ready).
          const p = s.root?.newProjectPicker || {}
          if (el.__npLibLoaded && p._libList && p._libList.length) return
          el.__npLibLoaded = true
          // Pre-load the full project list when the library tab renders.
          ;(async () => {
            try {
              const list = await getAllProjectsCached()
              const fresh = s.root?.newProjectPicker || {}
              s.root?.update({ newProjectPicker: { ...fresh, _libList: list || [] } })
            } catch {}
          })()
        },
        onInput: (e, el, s) => {
          const p = s.root?.newProjectPicker || {}
          s.root?.update({ newProjectPicker: { ...p, _libQuery: e.target.value, error: null } })
        }
      },

      // Scrollable results list.
      NpLibResults: {
        flow: 'column',
        gap: '2px',
        overflow: 'auto',
        flexGrow: '1',
        minHeight: '120px',
        maxHeight: '260px',

        NpLibLoading: {
          display: (el, s) => {
            const p = s.root?.newProjectPicker || {}
            return (!p._libList || !p._libList.length) ? 'block' : 'none'
          },
          padding: 'X',
          text: 'Loading…',
          color: 'rgba(255,255,255,0.4)',
          fontSize: 'Z2'
        },

        NpLibList: {
          flow: 'column',
          gap: '2px',
          childrenAs: 'state',
          children: (el, s) => {
            const p = s.root?.newProjectPicker || {}
            const q = ((p._libQuery || '')).toLowerCase().trim()
            const all = p._libList || []
            return all
              .filter((proj) => {
                if (!proj?.key) return false
                if (!q) return true
                const hay = `${proj.key} ${proj.name || ''} ${proj.owner?.slug || ''}`.toLowerCase()
                return hay.includes(q)
              })
              .slice(0, 50)
              .map((proj) => ({
                _libId: proj._id,
                _libKey: proj.key,
                _libName: proj.name || proj.key,
                _libOwner: proj.owner?.slug || ''
              }))
          },
          childExtends: 'NpLibItem'
        }
      }
    },

    NpFooter: {
      padding: 'V B',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      flow: 'row',
      align: 'center space-between',
      gap: 'V',

      NpError: {
        tag: 'div',
        text: (el, s) => s.root?.newProjectPicker?.error || '',
        fontSize: 'Z',
        color: '#ff7a7a',
        flexGrow: '1'
      },
      NpActions: {
        flow: 'row',
        gap: 'V',
        NpCancel: {
          tag: 'button',
          type: 'button',
          text: 'Cancel',
          padding: 'V X',
          fontSize: 'Z2',
          round: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'transparent',
          color: 'rgba(255,255,255,0.8)',
          ':hover': { background: 'rgba(255,255,255,0.06)' },
          onClick: (e, el, s) => {
            e.stopPropagation()
            closeNewProjectPicker(s.root)
          }
        },
        NpCreate: {
          tag: 'button',
          type: 'button',
          text: (el, s) => {
            const p = s.root?.newProjectPicker || {}
            if (p.creating) return 'Creating…'
            if (p.tab === 'library' && !p.templateProject) return 'Pick a template above'
            return 'Create project'
          },
          disabled: (el, s) => {
            const p = s.root?.newProjectPicker || {}
            if (p.creating) return true
            // Library tab requires a project selection before enabling create.
            if (p.tab === 'library' && !p.templateProject) return true
            return false
          },
          padding: 'V X',
          fontSize: 'Z2',
          round: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.16)',
          color: 'white',
          ':hover': { background: 'rgba(255,255,255,0.22)' },
          onClick: (e, el, s) => {
            e.stopPropagation()
            submitNewProjectPicker(s.root, el.getRoot())
          }
        }
      }
    }
  }
}

export const studios = {
  tag: 'main',
  id: 'main-content',
  // Default is fixed-to-viewport for the standalone `/workspaces/<slug>`
  // route. Dashboard's `WorkspaceEmbed` slot overrides `position: relative`
  // so the embedded workspace is constrained to the AppMain content area.
  metadata: (el, s) => {
    const root = s.root || s
    const slug = root.slug || root.activeWorkspace || ''
    const org = (root.orgs || []).find((o) => o.slug === slug)
    const name = org?.name || slug || 'Workspace'
    return { title: name + ' · Workspaces / Symbols.app' }
  },
  position: 'fixed',
  inset: '0',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  background: '#14161b',
  color: 'white',
  fontFamily: 'system-ui',

  onRender: (el, s) => {
    if (el.__wsLoaded) return
    el.__wsLoaded = true

    const root = s.root || s
    // On re-entry (e.g. navigating /dashboard → /workspaces → /dashboard),
    // the element is a fresh instance but root already holds valid data from
    // the previous visit. Only seed keys that are truly absent — avoid a
    // blanket root.update() that resets every reactive consumer (nodes,
    // integrations, positions) and triggers a full-subtree re-mount.
    // Transient UI state (hover, linking, drag) is always cleared regardless.
    const patch = {}
    if (!root.slug) patch.slug = ''
    if (!root.nodes) patch.nodes = []
    if (!root.integrations) patch.integrations = []
    if (!root._installables) patch._installables = []
    if (!root.positions) patch.positions = {}
    if (!root.connections) patch.connections = []
    if (!root.groupMap) patch.groupMap = {}
    if (!root.groupNames) patch.groupNames = {}
    if (!root.dragMode) patch.dragMode = 'grid'
    // Org rail is collapsed by default on first load. The user can toggle it;
    // the value persists in root state for the session.
    if (root.orgRailOpen === undefined) patch.orgRailOpen = false
    // Always reset transient UI state so stale drag/hover/link overlays
    // don't persist across route transitions.
    patch.hoverGroup = null
    patch.hoverNode = null
    patch.linking = null
    patch.draggingId = null
    // Only set loading=true when we have no nodes yet — avoids tearing down
    // the already-rendered board on re-entry while the fresh fetch runs.
    if (!root.nodes || root.nodes.length === 0) patch.loading = true
    patch.error = null
    if (Object.keys(patch).length) root.update(patch)
    WORKSPACE_ROOT = root
    // URL wins over stale `activeWorkspace` state when the user
    // navigated directly to /workspaces/{slug} (e.g. from history back,
    // URL bar, deep-link). `ownerSlugFromUrl` only returns a slug when
    // the path is actually /workspaces/{x}, so falling through to
    // activeWorkspace is still correct when embedded.
    el._loadedSlug = ownerSlugFromUrl() || root.activeWorkspace || ''
    if (el._loadedSlug) loadNodes(root, el._loadedSlug)

    // Close any open project tile menu when clicking outside it.
    // The Menu component stops propagation on its own mousedown so this
    // only fires for clicks that land outside an open menu.
    const closeMenuOnOutsideClick = (ev) => {
      const r = WORKSPACE_ROOT
      if (!r?.openMenuId) return
      // The Menu element is portaled into document.body so it lives outside
      // the [data-node-id] tile. Allow clicks landing inside a menu/dialog
      // through to their own handlers (item buttons stop propagation and
      // close the menu themselves). data-project-menu is the project
      // context menu's marker; role=menu/dialog covers other surfaces.
      if (ev.target?.closest?.('[data-project-menu], [role="menu"], [role="dialog"]')) return
      // If the click is inside a [data-node-id] element that matches the
      // currently open menu, let the node's own handlers manage state.
      const nodeEl = ev.target?.closest?.('[data-node-id]')
      if (nodeEl && nodeEl.dataset.nodeId === r.openMenuId) return
      r.update({ openMenuId: null, _ctxMenuTick: Date.now() })
    }
    if (!window.__wsMenuCloseHooked) {
      window.__wsMenuCloseHooked = true
      document.addEventListener('pointerdown', closeMenuOnOutsideClick, { capture: true })
    }

    // Defer panzoom init until the Board + Dots nodes have mounted. On
    // first onRender of a page with reactive children, they might not
    // exist yet (race between page mount and Nodes/Connections mount).
    const initPanzoom = () => {
      const scene = el.Board?.node
      if (!scene) {
        requestAnimationFrame(initPanzoom)
        return
      }
      // Guard against panzoom double-attach if onRender fires multiple
      // times for the same scene (smbls re-renders on reactive state
      // changes); a second panzoom() on the same element would leak event
      // listeners and fight the first instance for the transform.
      if (scene.__panzoomAttached) return
      scene.__panzoomAttached = true
      const BASE = 24
      const dots = el.Dots?.node
      const updateDots = () => {
        if (!dots) return
        const { x, y, scale } = pz.getTransform()
        const size = scale * BASE
        dots.style.backgroundSize = size + 'px ' + size + 'px'
        dots.style.backgroundPosition = (x % size) + 'px ' + (y % size) + 'px'
      }

      const pz = panzoom(scene, {
        minZoom: 0.2,
        maxZoom: 2,
        zoomSpeed: 0.8,
        zoomDoubleClickSpeed: 1,
        beforeMouseDown: (e) =>
          !!e.target.closest?.(
            '[data-node-id],[data-link-handle],[data-group-id]'
          ),
        beforeWheel: (e) => {
          const isPinch = e.metaKey || e.ctrlKey
          if (isPinch) return false
          e.preventDefault()
          const { x, y } = pz.getTransform()
          pz.moveTo(x - e.deltaX, y - e.deltaY)
          return true
        }
      })
      PZ = pz
      BOARD_EL = scene
      CONNECTIONS_NODE = el.Board?.Connections?.node || null

      // Shared ZoomInNavbar reads `globalScope.panzoomInstance` +
      // `globalScope.positions` for its zoom buttons; without this
      // mirror, the Zoombar sits inert on the workspace page.
      const gs = el.context?.globalScope || (window.__canvasGlobalScope = window.__canvasGlobalScope || {})
      window.__canvasGlobalScope = gs
      gs.panzoomInstance = pz
      gs.positions = gs.positions || { x: 0, y: 0 }
      const syncGsPos = () => {
        const t = pz.getTransform()
        gs.positions.x = t.x
        gs.positions.y = t.y
        writeZoomLabel(pz)
      }
      syncGsPos()
      pz.on('transform', syncGsPos)
      // rAF is paused in background tabs but setInterval still fires —
      // guarantees the label catches up when the tab returns to fg.
      if (!window.__wsZoomInterval) {
        window.__wsZoomInterval = setInterval(() => {
          const p = window.__canvasGlobalScope?.panzoomInstance
          if (p) writeZoomLabel(p)
        }, 250)
      }

      if (CONNECTIONS_NODE) {
        CONNECTIONS_NODE.addEventListener('click', (ev) => {
          const path = ev.target.closest?.('[data-connection-id]')
          if (!path) return
          const cid = path.dataset.connectionId
          const current = WORKSPACE_ROOT?.connections || []
          const removed = current.find((c) => c.id === cid)
          WORKSPACE_ROOT?.update({
            connections: current.filter((c) => c.id !== cid)
          })
          redrawConnections()
          if (removed && WORKSPACE_ROOT) {
            if (removed.kind === 'integration') {
              const integrations = WORKSPACE_ROOT.integrations || []
              const intSide = integrations.find((i) => i.id === removed.from)
                ? removed.from
                : integrations.find((i) => i.id === removed.to)
                  ? removed.to
                  : null
              if (intSide)
                persistIntegrationScope(WORKSPACE_ROOT, intSide, 'org', null)
            } else {
              persistSharedLibraries(WORKSPACE_ROOT, [removed.to])
            }
          }
        })
      }
      pz.on('transform', updateDots)
      // Re-render the open project context menu on every transform tick
      // so it follows its anchor tile during pan/zoom. Bumping a tick
      // invalidates the menu's reactive top/left factories without
      // touching any other root state. Only writes when a menu is open
      // — pure pan/zoom with no menu open is free.
      pz.on('transform', () => {
        const r = WORKSPACE_ROOT
        if (r?.openMenuId) r.update({ _ctxMenuTick: Date.now() })
      })
      // Seed both panzoom's internal state AND the Board's DOMQL style so
      // the first paint is already at scale 0.9 / offset 120,120 — the
      // library's rAF loop otherwise defers the DOM transform write
      // until the user interacts, leaving the board at identity.
      pz.zoomAbs(0, 0, 0.9)
      pz.moveTo(120, 120)
      // DOMQL v3.14's CSS_PROPS registry doesn't include `transform` /
      // `transformOrigin`, so seed them via `style:` to reach the DOM.
      el.Board?.update({
        style: {
          transformOrigin: '0 0',
          transform: 'matrix(0.9, 0, 0, 0.9, 120, 120)'
        }
      })
      updateDots()
    }
    initPanzoom()
  },

  onRemove: () => {
    WORKSPACE_ROOT = null
    DRAG_DOM_NODES.clear()
    if (DRAG_RAF) { cancelAnimationFrame(DRAG_RAF); DRAG_RAF = 0 }
    if (window.__wsZoomInterval) {
      clearInterval(window.__wsZoomInterval)
      window.__wsZoomInterval = null
    }
  },

  onUpdate: (el, s) => {
    const root = s.root || s
    const slug = ownerSlugFromUrl() || root.activeWorkspace
    if (slug && slug !== el._loadedSlug) {
      el._loadedSlug = slug
      loadNodes(root, slug)
    }
  },

  Dots: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    style: {
      backgroundImage:
        'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }
  },

  // Slim status pill at bottom-left. Canvas actions (auto layout, drag
  // mode) moved into WorkspaceSettingsModal; creation actions moved into
  // WorkspaceNavbar's + menu.
  WorkspaceViews: {
    position: 'absolute',
    bottom: 'X2',
    left: 'X2',
    zIndex: 10,
    padding: 'Y A',
    round: 'C1',
    theme: 'canvas-card-glass',
    flow: 'x',
    gap: 'V',
    align: 'center',
    pointerEvents: 'auto',

    Title: {
      tag: 'span',
      text: (el, s) => s.root?.slug || 'Workspace',
      fontSize: 'A',
      fontWeight: '600'
    },
    Subtitle: {
      tag: 'span',
      text: (el, s) => {
        const root = s.root
        if (root?.loading) return 'loading…'
        if (root?.error) return 'error: ' + root.error
        const n = root?.nodes?.length || 0
        const intCount = (root?.integrations || []).length
        const intStr = intCount ? ` · ${intCount} integration${intCount === 1 ? '' : 's'}` : ''
        return `· ${n} project${n === 1 ? '' : 's'}${intStr}`
      },
      fontSize: 'Z2',
      color: 'rgba(255,255,255,0.6)'
    }
  },

  // Floating create-menu navbar. Shared component — see
  // components/LabsNavbar.js. Auto-extended by key name.
  LabsNavbar: {},

  // OrgIntegrationRail intentionally NOT mounted on the studios canvas — it
  // would overlay the panzoom board and duplicate functionality that the
  // on-canvas InstallableNodes already provide (org-scoped integrations
  // can be dragged onto the board as positioned tiles). Component still
  // exported above for use elsewhere (settings, admin pages, etc).
  // Removed from studios canvas per Nika 2026-04-26.
  OrgIntegrationRail: null,

  // MCP connector rail — compact chip strip below the integration rail
  // (zIndex 19). Surfaces all MCP connectors scoped to this workspace.
  // Auto-extended by key name from MCPConnectorRail below.
  MCPConnectorRail: {},

  SettingsModal: {},

  // Project lifecycle modals — triggered from ProjectNode Menu items.
  // Auto-extended by key name → picks up the exported modal components above.
  ProjectDeleteConfirmModal: {},
  ProjectDuplicateModal: {},
  ProjectEditMetadataModal: {},

  CommandBar: null,

  Zoombar: {
    fontSize: 'Z1',
    position: 'absolute',
    bottom: 'X2',
    right: 'X2',
    top: 'auto',
    width: 'auto',
    pointerEvents: 'auto',
    zIndex: 10,
    NavbarButtonSet: {
      // Override the default /settings modal handoff — we open our own
      // unified SettingsModal instead.
      Settings: {
        onClick: (ev, el, s) => {
          ev.stopPropagation()
          s.root?.update({
            settingsOpen: true,
            settingsTab: s.root?.settingsTab || 'workspace'
          })
        }
      }
    }
  },

  CanvasPromptTextarea: {
    PromptButtons: { InspectBtn: null }
  },

  // Integrations now render on the canvas as draggable nodes, so the
  // side shelf is retired. Keep the key with `null` so the parent
  // component tree explicitly opts out of the legacy shelf slot.
  IntegrationShelf: null,

  ImportLibraryButton: null,

  // Auto-extends IntegrationPickerOverlay (exported above). The picker
  // component is now surface-agnostic — mounted here AND in CanvasEditor.
  IntegrationPicker: { extends: 'IntegrationPickerOverlay' },

  // Auto-extends LibraryPickerOverlay (exported above). Surface-agnostic
  // picker — mounted here AND in CanvasEditor.
  LibraryPicker: { extends: 'LibraryPickerOverlay' },

  // Auto-extends NewProjectPickerOverlay (exported above). Template-based
  // project creation modal opened from WorkspaceNavbar "New project" item.
  NewProjectPicker: { extends: 'NewProjectPickerOverlay' },

  Board: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: BOARD_W + 'px',
    height: BOARD_H + 'px',

    Connections: {
      tag: 'div',
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      zIndex: 5,
      html: (el, s) => buildConnectionsSVG(s.root)
    },

    ...groupFrameChildren,

    Nodes: {
      // Each child gets an explicit `key: n.id` so the v3.14 reconciler
      // uses stable project-key identity rather than positional index.
      // This means a workspace switch correctly removes orphan nodes
      // instead of reusing positional slots. `pos` is NOT inlined here
      // (the old approach that caused re-render storms): ProjectNode reads
      // position directly from `s.root.positions[s.id]` using v3.14 reactive
      // state. The children factory only re-runs when `nodes` changes
      // (workspace switch / load), not on every drag position update.
      // Guard `s` itself — v3.14 fires the effect once with undefined state
      // before the signal hydrates, which would throw on `s.root`.
      children: (el, s) => {
        return (s?.root?.nodes || []).map((n) => ({
          ...n,
          key: n.id
        }))
      },
      childrenAs: 'state',
      childExtends: 'ProjectNode'
    },

    IntegrationNodes: {
      // Project-scoped integrations rendered as canvas tiles (anchored below
      // their owning project card). Workspace/org integrations surface in the
      // overlay rails (WorkspaceIntegrationRail / OrgIntegrationRail) above.
      children: (el, s) => {
        const positions = s?.root?.positions || {}
        return (s?.root?.integrations || [])
          .filter((i) => i.scope === 'project' && positions[i.id])
          .map((i) => ({ ...i, key: i.id }))
      },
      childrenAs: 'state',
      childExtends: 'IntegrationNode'
    },

    // Phase 5 — ALL workspace-scoped installables as positioned canvas tiles.
    // Covers every domain: integrations (non-project-scoped), MCP connectors,
    // libraries, plugins. Click → openInstallableModal (Phase 4).
    // Tiles use the generic InstallableNode which branches on `domain`.
    InstallableNodes: {
      children: (el, s) => {
        const positions = s?.root?.positions || {}
        // De-dupe: project-scope integrations are already rendered by
        // IntegrationNodes above using the dedicated IntegrationNode chrome.
        // Without this exclusion every project-scope integration renders
        // twice (once 315×65 IntegrationNode, once 180×72 InstallableNode)
        // stacked at the same position. Exclude them here.
        const renderedIntegrationIds = new Set(
          (s?.root?.integrations || [])
            .filter((i) => i.scope === 'project' && positions[i.id])
            .map((i) => i.id)
        )
        return (s?.root?._installables || [])
          .filter((i) => positions[i.id] && !renderedIntegrationIds.has(i.id))
          .map((i) => ({ ...i, key: i.id }))
      },
      childrenAs: 'state',
      childExtends: 'InstallableNode'
    },

    // Empty-canvas tutorial — visible when no nodes and no installables.
    LabEmptyTutorial: {}
  }
}
